import { useState, useRef, useCallback, useEffect, useLayoutEffect, useMemo, Fragment } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import { getToken, v, api } from "../utils/helpers";
import { wortDiff, geaenderteAbschnitte, diffUmfang, stellenFuerRevision, loeseStellen, schlankeRevisionen, repariereMarkdownSyntax } from "../utils/dokuDiff";
import { renderIflowSvgString } from "../utils/bpmnShared";
import { markdownToDocx } from "../utils/docxExport";
import { sauberesHtml } from "../utils/sanitizeHtml";

// Die Kennung geht an den Server und steckt im Dateinamen des Exports - sie bleibt woertlich.
// Beschriftung und Beschreibung stehen im Katalog unter stil.<id>.
const DOC_STYLES = [{ id: "technical" }, { id: "executive" }, { id: "onboarding" }];

const CONN_COLORS = ["#6889ab", "#6e9a80", "#b0797a", "#b89a5a", "#8e81b0", "#5e9a9e", "#a67790", "#5e9e84"];

// Ersatzliste, solange der Server seine eigene Agentenliste noch nicht geschickt hat. id bleibt
// die Phasennummer (activePhase vergleicht darauf), key zeigt auf den Katalog agent.<key>.
const AGENTS = [
  { id: 1, key: "analyse" },
  { id: 2, key: "viz" },
  { id: 3, key: "redaktion" },
  { id: 4, key: "validierung" },
];

// EU-KI-Verordnung (AI Act) Transparenzhinweis - DETERMINISTISCH ergaenzt (NICHT vom LLM
// generiert), steht fest und kursiv am Anfang des KI-erzeugten Inhalts und in allen Exporten.
// Bewusst NICHT Teil von docContent, damit ihn weder das Modell noch das Verschieben antasten.
// Der Wortlaut steht im Katalog unter dokument.kiHinweis und folgt der Sprache der Oberflaeche.

// Verschiebe-Absicht bei der Inline-Bearbeitung (markierte Passage an eine andere Position).
// Loest den sektionsuebergreifenden mode:"move" aus statt der section-lokalen Umschreibung -
// nur so kann eine Passage z.B. "ganz an den Anfang" wandern (ueber Section-Grenzen hinweg).
// Verschiebe-Absicht bewusst ENG: entweder ein eindeutiges Verschiebe-Verb (immer Move) ODER eine
// eindeutige Zielposition ("ganz an den Anfang"/"ans Ende") - Letzteres nur, wenn KEINE Hinzufuege-
// Absicht vorliegt (sonst wuerde "fuege oben X ein" faelschlich verschoben). Mehrdeutige Woerter
// (umstell->"Umstellung", "vor Abschnitt", generisches "nach oben") raus, um False Positives zu
// vermeiden, die eine schnelle Umschreibung in den riskanten Ganz-Doku-Move-Pfad umleiten.
const DOKU_MOVE_VERB_RE = /verschieb|versetz|umsortier|\bmove\b|hochziehen|runterziehen|weiter (nach )?(oben|unten|vorne|hinten)/i;
const DOKU_MOVE_DEST_RE = /ganz (an den anfang|ans ende|nach (ganz )?(oben|unten|vorne|hinten))|an den anfang|ans ende|an die spitze|nach ganz (oben|unten)/i;
const DOKU_ADD_RE = /\b(einfüg|einzufüg|ergänz|hinzufüg|anhäng|erweiter)|füge\b.{0,40}\b(ein|hinzu)|neue[rn]?\s+(abschnitt|absatz|zeile|überschrift|hinweis|trennlinie|spalte|zusammenfassung)/i;

// Regenerierungs-Wunsch im "Ergaenzen"-Feld: volle Neu-Generierung statt nur einen Abschnitt
// anzuhaengen. Bewusst tolerant (Verb VOR oder NACH "neu", plus Synonyme), damit natuerliche
// Formulierungen nicht mehr durchrutschen und still ignoriert werden.

// ===== Redesign 07/2026: CI/CD-Setup-Designsprache (Mock browser-verifiziert Light+Dark) =====
// Volle Seitenbreite, cs-Stepper, Szenario-Karten fuer Quelle/Stil, Agenten-Pipeline-Panel,
// Agenten-Progress-Karten (ehrliche %: Analyse real, Rest indeterminate), Selbst-schreiben-Modus.
const DK_CSS = `
/* Elevation-Grammatik (ux-architect + ui-ux-pro-max 22.07.2026): ZWEI Ebenen.
   Sektion = Arbeitsflaeche (shadow-sm, subtle Border). Interaktives/Lebendiges = erhabene Karte
   auf --dk-raise (Light: Weiss + Schatten traegt; Dark: bg-3 HELLER als Sektion = tonale
   Elevation, sonst versinken Karten als "Loecher"). Informatives = Hairline-Trenner, KEINE Box. */
.dk-root{ color:var(--iio-text-1); --dk-raise:var(--iio-bg-2); --dk-raise-border:var(--iio-border); }
[data-theme="dark"] .dk-root{ --dk-raise:var(--iio-bg-3); --dk-raise-border:var(--iio-border-subtle); }
.dk-root .crumb{ font-size:11px; letter-spacing:.04em; color:var(--iio-text-3); margin-bottom:7px; }
.dk-root .crumb b{ color:var(--iio-signal); font-weight:700; }
.dk-root h1.dk-h1{ margin:0; font-size:27px; font-weight:700; letter-spacing:-.02em; }
.dk-root .dk-sub{ margin-top:6px; font-size:13.5px; color:var(--iio-text-2); max-width:760px; line-height:1.5; }

.dk-rail{ display:flex; align-items:flex-start; margin:24px 0 20px; overflow-x:auto; padding-bottom:4px; }
.dk-rnode{ display:flex; flex-direction:column; align-items:center; gap:8px; flex:0 0 auto; min-width:140px; background:none; border:none; padding:0; cursor:pointer; font-family:inherit; }
.dk-rdot{ width:34px; height:34px; border-radius:50%; border:2px solid var(--iio-border); background:var(--iio-bg-1); color:var(--iio-text-3); display:flex; align-items:center; justify-content:center; font-size:13px; font-weight:700; transition:border-color .3s,color .3s,background .3s; }
.dk-rnode.act .dk-rdot{ border-color:var(--iio-signal); color:var(--iio-signal); background:color-mix(in srgb,var(--iio-signal) 10%,var(--iio-bg-1)); }
.dk-rnode.done .dk-rdot{ border-color:var(--iio-success); color:var(--iio-success); }
.dk-rnode.done .dk-rdot svg{ width:15px; height:15px; stroke:currentColor; fill:none; stroke-width:2.6; stroke-linecap:round; stroke-linejoin:round; }
.dk-rnode .lbl{ font-size:11px; font-weight:600; color:var(--iio-text-3); text-align:center; line-height:1.3; max-width:130px; }
.dk-rnode.act .lbl{ color:var(--iio-text-1); } .dk-rnode.done .lbl{ color:var(--iio-text-2); }
.dk-rnode.future{ opacity:.55; cursor:default; }
.dk-rseg{ flex:1 1 auto; height:2px; background:var(--iio-border-subtle); margin-top:17px; min-width:16px; border-radius:1px; position:relative; overflow:hidden; }
.dk-rseg i{ position:absolute; inset:0; background:var(--iio-success); transform:scaleX(0); transform-origin:left; transition:transform .4s cubic-bezier(.22,.61,.36,1); }
.dk-rseg.fill i{ transform:scaleX(1); }

.dk-grid{ display:flex; flex-direction:column; gap:16px; }
.dk-sect{ background:var(--iio-bg-2); border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-xl); padding:20px 24px; box-shadow:var(--iio-shadow-sm); opacity:0; transform:translateY(14px); animation:dkRise .5s cubic-bezier(.22,.61,.36,1) forwards; }
.dk-sect:nth-of-type(1){animation-delay:.04s} .dk-sect:nth-of-type(2){animation-delay:.11s} .dk-sect:nth-of-type(3){animation-delay:.18s} .dk-sect:nth-of-type(4){animation-delay:.25s}
@keyframes dkRise{ to{ opacity:1; transform:none; } }
.dk-sh{ display:flex; align-items:center; gap:11px; margin-bottom:16px; }
/* Flex-Kinder duerfen nicht unter ihre min-content-Breite gedrueckt werden - ohne min-width:0
   verbreitert ein langer Befundtext den Kopf und damit die Seite. */
.dk-sh > *{ min-width:0; }
.dk-sh .no{ width:25px; height:25px; border-radius:50%; border:1.5px solid var(--iio-brand-fg); color:var(--iio-brand-fg); display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; flex-shrink:0; }
.dk-sh .no.ok{ border-color:var(--iio-success); color:var(--iio-success); }
.dk-sh h2{ margin:0; font-size:15.5px; font-weight:700; }
.dk-sh .shsub{ font-size:11.5px; color:var(--iio-text-3); margin-top:1px; }
.dk-sh .right{ margin-left:auto; font-size:11.5px; color:var(--iio-text-3); display:flex; align-items:center; gap:8px; }

.dk-btn{ height:38px; padding:0 18px; border:1px solid transparent; border-radius:var(--iio-r-md); font-size:13px; font-weight:700; cursor:pointer; display:inline-flex; align-items:center; justify-content:center; gap:8px; white-space:nowrap; transition:all var(--iio-tx-fast, 120ms ease); font-family:inherit; }
.dk-btn.prim{ background:var(--iio-signal); color:#fff; } .dk-btn.prim:hover:not(:disabled){ background:var(--iio-signal-2); }
.dk-btn.dark{ background:var(--iio-brand-ink); color:#fff; } .dk-btn.dark:hover:not(:disabled){ opacity:.9; }
[data-theme="dark"] .dk-root .dk-btn.dark{ background:var(--iio-info); color:#06131a; }
.dk-btn.ghost{ background:transparent; color:var(--iio-text-2); border-color:var(--iio-border); }
.dk-btn.ghost:hover:not(:disabled){ background:var(--iio-bg-3); color:var(--iio-text-1); }
.dk-btn.sm{ height:30px; padding:0 12px; font-size:12px; }
.dk-btn:disabled{ opacity:.5; cursor:not-allowed; }
.dk-btn svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.dk-btn.sm svg{ width:13px; height:13px; }
.dk-btn:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }

.dk-scn-row{ display:grid; grid-template-columns:1fr 1fr; gap:16px; }
.dk-scn{ text-align:left; display:flex; flex-direction:column; gap:9px; padding:18px; background:var(--dk-raise); border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-lg); box-shadow:var(--iio-shadow-sm); cursor:pointer; font-family:inherit; transition:border-color var(--iio-tx-fast,120ms ease), box-shadow .25s ease, transform .25s cubic-bezier(.22,.61,.36,1); }
.dk-scn:hover:not(:disabled){ transform:translateY(-2px); border-color:var(--iio-text-4); box-shadow:var(--iio-shadow-md); }
.dk-scn:disabled{ opacity:.55; cursor:not-allowed; }
.dk-scn.is-active{ border-color:var(--iio-signal); box-shadow:var(--iio-shadow-sm), inset 0 0 0 1px var(--iio-signal); }
.dk-scn .ic{ width:34px; height:34px; border-radius:var(--iio-r-md); border:1px solid var(--iio-border); display:flex; align-items:center; justify-content:center; color:var(--iio-text-2); }
.dk-scn.is-active .ic{ color:var(--iio-signal); border-color:var(--iio-signal); }
.dk-scn .ic svg{ width:16px; height:16px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.dk-scn .t{ font-size:14.5px; font-weight:700; color:var(--iio-text-1); }
.dk-scn .d{ font-size:12px; color:var(--iio-text-3); line-height:1.55; }

/* Buendiger Abschluss: beide Spalten strecken; letzte Elemente (Stats bzw. Panel-Fussnote)
   pinnen per margin-top:auto an die gemeinsame Unterkante. */
.dk-s1{ display:grid; grid-template-columns:minmax(0,1fr) 300px; gap:22px; align-items:stretch; }
.dk-s1 > div:first-child{ display:flex; flex-direction:column; }
@media(max-width:1080px){ .dk-s1{ grid-template-columns:1fr; } }
.dk-agents{ border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-lg); background:var(--dk-raise); box-shadow:var(--iio-shadow-sm); padding:16px 18px; display:flex; flex-direction:column; }
.dk-agents .ah{ display:flex; align-items:center; gap:9px; margin-bottom:14px; }
.dk-agents .ah .mark{ width:26px; height:26px; border-radius:var(--iio-r-sm); background:var(--iio-brand-ink); color:#F5F2EC; display:flex; align-items:center; justify-content:center; font-size:11px; font-weight:800; }
[data-theme="dark"] .dk-root .dk-agents .ah .mark{ background:var(--iio-info); color:#06131a; }
.dk-agents .ah b{ font-size:12.5px; font-weight:700; }
.dk-agents .ah span{ font-size:10.5px; color:var(--iio-text-3); display:block; }
.dk-agrow{ display:grid; grid-template-columns:36px minmax(0,1fr); column-gap:12px; align-items:center; }
.dk-agdot{ width:36px; height:36px; border-radius:50%; border:1.5px solid var(--iio-border); background:var(--iio-bg-2); color:var(--iio-text-2); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.dk-agdot svg{ width:17px; height:17px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.dk-agrow .tx b{ font-size:12.5px; font-weight:700; display:block; }
.dk-agrow .tx span{ font-size:11px; color:var(--iio-text-3); }
.dk-aglink{ display:grid; grid-template-columns:36px 1fr; column-gap:12px; }
.dk-aglink i{ width:2px; height:16px; background:var(--iio-border); justify-self:center; border-radius:1px; }
.dk-agents .foot{ margin-top:auto; padding-top:12px; border-top:1px dashed var(--iio-border-subtle); font-size:11px; color:var(--iio-text-3); line-height:1.5; }
.dk-agents .dk-agrow:last-of-type{ margin-bottom:14px; }
.dk-agents .foot b{ color:var(--iio-text-2); }

/* Statistik: KEINE Box mehr - Hairline-Streifen als Sektions-Fuss (Trenner statt Kasten) */
.dk-stats{ display:grid; grid-template-columns:repeat(3,1fr); border-top:1px solid var(--iio-border-subtle); background:transparent; margin-top:auto; }
.dk-stat{ display:flex; align-items:center; gap:13px; padding:14px 20px 2px; }
.dk-stat+.dk-stat{ border-left:1px solid var(--iio-border-subtle); }
.dk-stat .n{ font-size:22px; font-weight:700; color:var(--iio-brand-fg); line-height:1; }
.dk-stat .l b{ font-size:12.5px; font-weight:700; display:block; }
.dk-stat .l span{ font-size:11px; color:var(--iio-text-3); }

.dk-stylecards{ display:grid; grid-template-columns:repeat(3,1fr); gap:12px; }
.dk-stylecards .dk-scn{ padding:14px; }
.dk-stylecards .t{ font-size:13px; }
.dk-cfg{ display:grid; grid-template-columns:minmax(0,1.35fr) minmax(0,1fr); gap:22px; align-items:start; }
@media(max-width:1080px){ .dk-cfg{ grid-template-columns:1fr; } }
.dk-actionrow{ display:flex; align-items:center; justify-content:space-between; gap:16px; margin-top:18px; padding-top:16px; border-top:1px solid var(--iio-border-subtle); }
.dk-actionrow .info{ font-size:12px; color:var(--iio-text-3); line-height:1.5; }
.dk-actionrow .info b{ color:var(--iio-text-2); }

.dk-genhead{ display:flex; align-items:center; gap:12px; margin-bottom:16px; flex-wrap:wrap; }
.dk-genhead .bar2{ width:2px; height:15px; background:var(--iio-signal); animation:dkPulse 1.6s ease-in-out infinite; }
@keyframes dkPulse{ 0%,100%{ transform:scaleY(1); opacity:1; } 50%{ transform:scaleY(.55); opacity:.5; } }
.dk-genhead b{ font-size:14.5px; }
.dk-genhead span{ font-size:12px; color:var(--iio-text-3); }
.dk-agcards{ display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:14px; }
@media(max-width:1180px){ .dk-agcards{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
.dk-agc{ border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-lg); background:var(--dk-raise); box-shadow:var(--iio-shadow-sm); padding:14px 16px; opacity:0; transform:translateY(10px); animation:dkRise .45s cubic-bezier(.22,.61,.36,1) forwards; }
.dk-agc.run{ border-color:color-mix(in srgb, var(--iio-info) 45%, var(--dk-raise-border)); }
.dk-agc.done{ border-color:color-mix(in srgb, var(--iio-success) 35%, var(--dk-raise-border)); }
.dk-agc:nth-child(1){animation-delay:.05s} .dk-agc:nth-child(2){animation-delay:.11s} .dk-agc:nth-child(3){animation-delay:.17s} .dk-agc:nth-child(4){animation-delay:.23s}
.dk-agc .head{ display:flex; align-items:center; gap:10px; margin-bottom:10px; }
.dk-agc .head .dk-agdot{ width:32px; height:32px; }
.dk-agc.run .dk-agdot{ border-color:var(--iio-info); color:var(--iio-info); }
.dk-agc.done .dk-agdot{ border-color:var(--iio-success); color:var(--iio-success); }
.dk-agc .head b{ font-size:13px; font-weight:700; display:block; }
.dk-agc .head span{ font-size:10.5px; color:var(--iio-text-3); }
.dk-agc .st{ display:inline-flex; align-items:center; gap:7px; font-size:11px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; margin-bottom:9px; }
.dk-agc .st::before{ content:''; width:2px; height:11px; background:currentColor; }
/* Motion-Budget: EINE Bewegungsquelle je Karte - der Indeterminate-Balken traegt die Bewegung,
   der Status-Bar bleibt statisch (UX-Befund: Doppel-Puls wirkt unruhig). */
.dk-agc .st.ok{ color:var(--iio-success); } .dk-agc .st.run{ color:var(--iio-info); } .dk-agc .st.wait{ color:var(--iio-text-4); }
.dk-pbar{ height:5px; border-radius:var(--iio-r-xs); background:var(--iio-bg-3); overflow:hidden; position:relative; }
.dk-pbar i{ position:absolute; inset:0; border-radius:var(--iio-r-xs); transform-origin:left; transform:scaleX(0); transition:transform .5s cubic-bezier(.22,.61,.36,1); }
.dk-pbar i.ok{ background:var(--iio-success); } .dk-pbar i.run{ background:var(--iio-info); }
.dk-pbar.ind i{ background:var(--iio-info); width:34%; transform:none; animation:dkInd 1.4s ease-in-out infinite; }
@keyframes dkInd{ 0%{ left:-36%; } 100%{ left:102%; } }
.dk-pmeta{ display:flex; justify-content:space-between; gap:8px; font-size:11px; color:var(--iio-text-3); margin-top:6px; }
.dk-pmeta b{ color:var(--iio-text-2); font-weight:600; }
/* Fusszeile: Hairline-Streifen statt Box (Trenner-Prinzip) */
.dk-genfoot{ display:flex; align-items:center; gap:18px; margin:16px -24px 0; padding:12px 24px 0; border-top:1px solid var(--iio-border-subtle); background:transparent; font-size:12px; color:var(--iio-text-3); flex-wrap:wrap; }
.dk-genfoot b{ color:var(--iio-text-1); font-weight:600; }
.dk-genfoot .sep{ width:1px; height:14px; background:var(--iio-border); }

/* Dokument = die EINE grosse erhabene Papierflaeche; Toolbar tonal abgesetzt */
.dk-doc{ border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-lg); background:var(--iio-bg-2); box-shadow:var(--iio-shadow-sm); overflow:hidden; }
.dk-doc-tb{ display:flex; align-items:center; justify-content:space-between; gap:12px; padding:9px 16px; background:var(--iio-bg-1); border-bottom:1px solid var(--iio-border-subtle); flex-wrap:wrap; }
.dk-doc-tb .l{ display:flex; align-items:center; gap:10px; flex-wrap:wrap; }
.dk-doc-tb .l b{ font-size:13px; }
.dk-edittag{ display:inline-flex; align-items:center; gap:6px; height:20px; padding:0 8px; border:1px solid var(--iio-success); color:var(--iio-success); border-radius:var(--iio-r-xs); font-size:10.5px; font-weight:600; }
.dk-edittag.on{ border-color:var(--iio-info); color:var(--iio-info); }
.dk-edittag svg{ width:11px; height:11px; stroke:currentColor; fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; }
.dk-doc-tb .r{ display:flex; gap:6px; flex-wrap:wrap; }
.dk-append{ display:flex; gap:10px; margin-top:14px; padding:12px 14px; background:var(--iio-bg-2); border:1px solid var(--iio-border); border-radius:var(--iio-r-lg); align-items:center; }
.dk-append input{ flex:1; height:38px; padding:0 12px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); color:var(--iio-text-1); font:inherit; font-size:13px; outline:none; transition:border-color var(--iio-tx-fast,120ms ease); }
.dk-append input:focus{ border-color:var(--iio-brand-fg); }
.dk-hintln{ margin-top:10px; font-size:11.5px; color:var(--iio-text-3); display:flex; align-items:center; gap:8px; }
.dk-hintln svg{ width:14px; height:14px; stroke:var(--iio-text-3); fill:none; stroke-width:1.9; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; }

/* --- Verfeinerungs-Board (Sektion 3 rechts, ux-architect Kreativ-Konzept 23.07.2026) ---
   EIN mutiges Element: die typografische Verfeinerungs-Skala (2px-Persimmon-Oberkante wandert
   mit den Beigaben). Darunter zwei offene, erhabene Karten (CI-Slots + Multi-Dropzone). */
/* EIN zusammenhaengendes, abgerundetes Panel (wie das KI-Team-Panel), statt einzeln
   "eingefuegt" wirkender Kanten: Meter = Kopfzone, Gruppen durch Hairlines getrennt. */
.dk-enrich{ display:flex; flex-direction:column; gap:14px; background:var(--dk-raise); border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-lg); box-shadow:var(--iio-shadow-sm); padding:16px 18px 18px; }
.dk-enrich-meter{ padding:0 0 14px; border-bottom:1px solid var(--iio-border-subtle); }
.dk-enrich-eyebrow{ display:block; font-size:10px; font-weight:700; letter-spacing:.14em; text-transform:uppercase; color:var(--iio-text-4); margin-bottom:10px; }
.dk-enrich-scale{ list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(3,1fr); gap:6px; font-size:11.5px; color:var(--iio-text-3); }
.dk-enrich-scale li{ padding:8px 0 0; text-align:center; border-top:2px solid var(--iio-border-subtle); transition:color .2s ease, border-color .32s cubic-bezier(.22,.61,.36,1); }
.dk-enrich-scale li.on{ color:var(--iio-text-1); border-top-color:var(--iio-signal); }
.dk-enrich-hint{ margin:10px 0 0; font-size:11.5px; line-height:1.5; color:var(--iio-text-3); }
.dk-enrich-card{ background:transparent; border:none; box-shadow:none; border-radius:0; padding:0; display:flex; flex-direction:column; gap:10px; }
.dk-enrich-card + .dk-enrich-card{ border-top:1px solid var(--iio-border-subtle); padding-top:14px; }
.dk-enrich-cardhead{ display:flex; align-items:baseline; justify-content:space-between; gap:12px; }
.dk-enrich-cardhead h3{ margin:0; font-size:13.5px; font-weight:700; color:var(--iio-text-1); }
.dk-optional{ font-size:10px; font-weight:500; color:var(--iio-text-4); letter-spacing:.06em; }
.dk-enrich-lede{ margin:0; font-size:11.5px; line-height:1.5; color:var(--iio-text-3); }
.dk-enrich-slots{ display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:9px; }
.dk-enrich-slot{ position:relative; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; padding:12px 8px; min-height:92px; background:color-mix(in srgb, var(--iio-bg-0) 32%, var(--dk-raise)); border:1.5px dashed var(--iio-border); border-radius:var(--iio-r-md); cursor:pointer; text-align:center; transition:border-color .14s ease, background .14s ease; }
.dk-enrich-slot:hover{ border-color:var(--iio-text-4); }
.dk-enrich-slot:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }
.dk-enrich-slot.is-filled{ border-style:solid; border-color:var(--dk-raise-border); cursor:default; }
.dk-enrich-slot-label{ font-size:11px; font-weight:600; color:var(--iio-text-2); max-width:100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dk-enrich-slot-fmt{ font-family:var(--iio-font-mono); font-size:9.5px; letter-spacing:.06em; color:var(--iio-text-4); }
.dk-enrich-slot-thumb{ width:40px; height:40px; border-radius:var(--iio-r-sm); object-fit:contain; background:var(--iio-bg-3); border:1px solid var(--iio-border-subtle); }
.dk-enrich-slot-box{ width:40px; height:40px; border-radius:var(--iio-r-sm); background:var(--iio-bg-3); border:1px solid var(--iio-border-subtle); display:flex; align-items:center; justify-content:center; font-family:var(--iio-font-mono); font-size:9.5px; font-weight:600; letter-spacing:.05em; color:var(--iio-text-2); }
.dk-enrich-slot-remove{ position:absolute; top:5px; right:5px; width:20px; height:20px; border:none; background:transparent; color:var(--iio-text-4); cursor:pointer; border-radius:var(--iio-r-xs); display:flex; align-items:center; justify-content:center; padding:0; }
.dk-enrich-slot-remove:hover{ color:var(--iio-text-1); background:var(--iio-bg-3); }
.dk-enrich-slot-remove svg{ width:11px; height:11px; stroke:currentColor; fill:none; stroke-width:2.2; stroke-linecap:round; }
.dk-enrich-slot .plus{ width:16px; height:16px; stroke:var(--iio-text-3); fill:none; stroke-width:2; stroke-linecap:round; }
.dk-enrich-dropzone{ border:1.5px dashed var(--iio-border); border-radius:var(--iio-r-md); padding:18px 14px; text-align:center; background:color-mix(in srgb, var(--iio-bg-0) 32%, var(--dk-raise)); cursor:pointer; transition:border-color .14s ease, background .14s ease; display:flex; flex-direction:column; align-items:center; gap:6px; }
.dk-enrich-dropzone:hover, .dk-enrich-dropzone.is-drag{ border-color:var(--iio-signal); background:color-mix(in srgb, var(--iio-signal) 5%, var(--iio-bg-2)); }
.dk-enrich-dropzone:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }
.dk-enrich-dropzone svg{ width:18px; height:18px; stroke:var(--iio-text-3); fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.dk-enrich-dz-label{ font-size:12px; font-weight:600; color:var(--iio-text-2); }
.dk-enrich-formats{ display:inline-flex; gap:10px; font-family:var(--iio-font-mono); font-size:10px; color:var(--iio-text-4); letter-spacing:.05em; }
.dk-enrich-textlbl{ font-size:10.5px; font-weight:600; letter-spacing:.08em; text-transform:uppercase; color:var(--iio-text-4); margin-top:2px; }
.dk-enrich-text{ width:100%; min-height:74px; resize:vertical; box-sizing:border-box; padding:9px 11px; font-family:inherit; font-size:12.5px; line-height:1.5; color:var(--iio-text-1); background:color-mix(in srgb, var(--iio-bg-0) 32%, var(--dk-raise)); border:1px solid var(--iio-border); border-radius:var(--iio-r-md); outline:none; transition:border-color .14s ease; }
.dk-enrich-text:focus{ border-color:var(--iio-brand-fg); }
.dk-enrich-text::placeholder{ color:var(--iio-text-4); }
.dk-enrich-chips{ list-style:none; padding:0; margin:0; display:flex; flex-direction:column; gap:4px; }
.dk-enrich-chip{ display:grid; grid-template-columns:46px minmax(0,1fr) 24px; align-items:center; gap:9px; padding:7px 8px; background:color-mix(in srgb, var(--iio-bg-0) 32%, var(--dk-raise)); border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-md); }
.dk-enrich-chip-fmt{ font-family:var(--iio-font-mono); font-size:9.5px; font-weight:600; letter-spacing:.06em; color:var(--iio-text-2); background:var(--iio-bg-3); border-radius:var(--iio-r-xs); padding:3px 0; text-align:center; }
.dk-enrich-chip-name{ font-size:12px; color:var(--iio-text-1); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dk-enrich-chip-x{ border:none; background:transparent; color:var(--iio-text-4); cursor:pointer; width:24px; height:24px; border-radius:var(--iio-r-xs); display:flex; align-items:center; justify-content:center; padding:0; }
.dk-enrich-chip-x:hover{ color:var(--iio-text-1); background:var(--iio-bg-3); }
.dk-enrich-chip-x svg{ width:11px; height:11px; stroke:currentColor; fill:none; stroke-width:2.2; stroke-linecap:round; }

/* Abgeschlossene Schritte klappen waehrend/nach der Generierung zu (Kopf klickbar) */
.dk-sh.clickable{ cursor:pointer; user-select:none; }
.dk-sh .chev{ width:16px; height:16px; stroke:var(--iio-text-3); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; transition:transform .25s ease; flex-shrink:0; }
.dk-sh .chev.open{ transform:rotate(180deg); }
.dk-techline{ display:flex; flex-wrap:wrap; gap:4px 16px; margin-top:5px; font-size:11px; color:var(--iio-text-3); }
.dk-techline b{ font-family:var(--iio-font-mono); font-variant-numeric:tabular-nums; color:var(--iio-text-2); font-weight:600; }
/* Legende am Ende der Zeile: sagt einmal, was die beiden Zahlen je Spalte bedeuten - sonst
   muesste jede Spalte "Output" und "Input" wiederholen. */
.dk-tech-legende{ color:var(--iio-text-4); font-size:10.5px; }

/* Ende-Kennzeichnung + Weitermachen-Liste */
.dk-sh .no.warn{ border-color:var(--iio-warning); color:var(--iio-warning); }
.dk-endmark{ display:flex; align-items:center; gap:9px; margin-top:24px; padding-top:13px; border-top:1px dashed var(--iio-border-subtle); font-size:12px; font-weight:600; color:var(--iio-text-2); user-select:none; }
.dk-endmark .bar{ width:2px; height:13px; flex-shrink:0; }
.dk-endmark .bar.ok{ background:var(--iio-success); } .dk-endmark .bar.warn{ background:var(--iio-warning); }
.dk-sesslist{ display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
@media(max-width:1180px){ .dk-sesslist{ grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media(max-width:760px){ .dk-sesslist{ grid-template-columns:1fr; } }
.dk-sess-searchwrap{ position:relative; display:inline-flex; align-items:center; }
.dk-sess-searchwrap > svg{ position:absolute; left:9px; width:13px; height:13px; stroke:var(--iio-text-4); fill:none; stroke-width:2; stroke-linecap:round; pointer-events:none; }
.dk-sess-search{ height:30px; width:230px; padding:0 10px 0 28px; border:1px solid var(--iio-border); border-radius:var(--iio-r-sm); background:var(--iio-bg-1); color:var(--iio-text-1); font-family:inherit; font-size:12px; outline:none; transition:border-color var(--iio-tx-fast,120ms ease); }
.dk-sess-search:focus{ border-color:var(--iio-brand-fg); }
.dk-sess-search::placeholder{ color:var(--iio-text-4); }
.dk-sess{ display:flex; align-items:stretch; gap:6px; }
.dk-sess .main{ flex:1; min-width:0; display:flex; align-items:center; gap:12px; padding:10px 12px; background:var(--dk-raise); border:1px solid var(--dk-raise-border); border-radius:var(--iio-r-md); box-shadow:var(--iio-shadow-sm); cursor:pointer; font-family:inherit; text-align:left; transition:border-color var(--iio-tx-fast,120ms ease), transform .2s cubic-bezier(.22,.61,.36,1); }
.dk-sess .main:hover{ transform:translateY(-1px); border-color:var(--iio-text-4); }
.dk-sess .main:focus-visible{ outline:2px solid var(--iio-info); outline-offset:2px; }
.dk-sess .ic{ width:30px; height:30px; border-radius:var(--iio-r-sm); background:var(--iio-bg-3); display:flex; align-items:center; justify-content:center; color:var(--iio-brand-fg); flex-shrink:0; }
.dk-sess .ic svg{ width:15px; height:15px; stroke:currentColor; fill:none; stroke-width:1.8; stroke-linecap:round; stroke-linejoin:round; }
.dk-sess .tx{ flex:1; min-width:0; }
.dk-sess .tx b{ display:block; font-size:13px; font-weight:700; color:var(--iio-text-1); overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.dk-sess .tx span{ font-size:11px; color:var(--iio-text-3); }
.dk-sess .st{ display:inline-flex; align-items:center; gap:7px; font-size:10.5px; font-weight:700; letter-spacing:.05em; text-transform:uppercase; flex-shrink:0; }
.dk-sess .st::before{ content:''; width:2px; height:11px; background:currentColor; }
.dk-sess .st.ok{ color:var(--iio-success); } .dk-sess .st.warn{ color:var(--iio-warning); } .dk-sess .st.idle{ color:var(--iio-text-4); }
.dk-sess .go{ width:15px; height:15px; stroke:var(--iio-text-4); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; }
.dk-sess .del{ width:34px; border:1px solid var(--iio-border-subtle); border-radius:var(--iio-r-md); background:transparent; color:var(--iio-text-4); cursor:pointer; display:flex; align-items:center; justify-content:center; transition:color var(--iio-tx-fast,120ms ease), border-color var(--iio-tx-fast,120ms ease); }
.dk-sess .del:hover{ color:var(--iio-critical); border-color:var(--iio-critical); }
.dk-sess .del svg{ width:12px; height:12px; stroke:currentColor; fill:none; stroke-width:2.2; stroke-linecap:round; }

/* Tokenlimit-Abfrage: aktives Nachfragen statt stiller Abschneidung */
.dk-tokenlimit{ display:flex; align-items:flex-start; gap:12px; padding:12px 16px; border:1px solid var(--iio-warning); border-left-width:3px; border-radius:var(--iio-r-md); background:color-mix(in srgb, var(--iio-warning) 8%, var(--iio-bg-1)); font-size:12.5px; line-height:1.55; color:var(--iio-text-1); margin-bottom:14px; flex-wrap:wrap; }
.dk-tokenlimit > svg{ width:18px; height:18px; stroke:var(--iio-warning); fill:none; stroke-width:2; stroke-linecap:round; stroke-linejoin:round; flex-shrink:0; margin-top:1px; }
.dk-tokenlimit .tx{ flex:1 1 320px; min-width:0; }
.dk-tokenlimit .tx b{ color:var(--iio-warning); }
.dk-tokenlimit .tx .mono{ font-family:var(--iio-font-mono); font-variant-numeric:tabular-nums; color:var(--iio-text-1); font-weight:600; }
.dk-tokenlimit .acts{ display:flex; gap:8px; align-items:center; }

/* Alt-Bausteine (Tenant-Browser, Dropzone, Canvas-Karten) auf die Raise-Grammatik heben:
   Light unveraendert (raise=bg-2), Dark tonal erhaben (raise=bg-3) - Befund: Elemente
   soffen im Dark Mode tongleich ab und hoben sich nicht von der Sektion ab. */
.dk-root .iio-doku-tn-row select{ background:var(--dk-raise); border-color:var(--dk-raise-border); box-shadow:var(--iio-shadow-sm); }
.dk-root .iio-doku-tn-list{ background:var(--dk-raise); border-color:var(--dk-raise-border); box-shadow:var(--iio-shadow-sm); }
.dk-root .iio-doku-tn-search{ background:color-mix(in srgb, var(--iio-bg-0) 35%, var(--dk-raise)); border-bottom-color:var(--dk-raise-border); }
/* Kopfzone und Modus-Streifen auf denselben Ton wie das Feld, sonst laeuft im Dark Mode eine
   sichtbare Naht mitten durch die Zone, die als EINE gelesen werden soll. */
.dk-root .iio-doku-tn-kopf, .dk-root .iio-doku-tn-modus{ background:color-mix(in srgb, var(--iio-bg-0) 35%, var(--dk-raise)); }
.dk-root .iio-doku-tn-modus{ border-bottom-color:var(--dk-raise-border); }
.dk-root .iio-doku-tn-flow{ border-bottom-color:color-mix(in srgb, var(--iio-border-subtle) 55%, var(--dk-raise)); }
.dk-root .iio-doku-drop{ background:var(--dk-raise); box-shadow:var(--iio-shadow-sm); }
.dk-root .iio-doku-node{ background:var(--dk-raise); }
.dk-root .iio-doku-tn-flow > svg{ color:var(--iio-brand-fg); }
[data-theme="dark"] .dk-root .iio-doku-tn-flow .add{ border-color:var(--iio-text-2); color:var(--iio-text-1); }
[data-theme="dark"] .dk-root .iio-doku-tn-flow .add:hover:not(:disabled){ background:var(--iio-text-1); color:var(--iio-bg-1); border-color:var(--iio-text-1); }

@media(prefers-reduced-motion:reduce){
  .dk-root *{ animation:none!important; transition:none!important; }
  .dk-sect,.dk-agc{ opacity:1; transform:none; }
  .dk-rseg.fill i,.dk-pbar i{ transform:scaleX(1); }
}
`;

// ZEITGRENZE auf jede Anfrage. Ohne sie kann ein haengender Aufruf die Oberflaeche dauerhaft
// blockieren: der Knopf dreht weiter, es kommt keine Meldung, und der naechste Versuch prallt am
// Wachposten ab - bis zum Neuladen. Die Frist laeuft bis zum ANTWORTKOPF; danach darf ein
// Datenstrom beliebig lange liefern (clearTimeout in finally auf dem fetch-Versprechen).
const mitFrist = (url, body, ms = 90000) => {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), ms);
  // Die Sprache haengt hier und nicht an den vier Aufrufstellen: eine Anpassung,
  // die auf Deutsch eingearbeitet wird, waere in einem englischen Dokument
  // sichtbarer Bruch. /api/doku/intent ignoriert das Feld.
  const nutzlast = { ...body, sprache: i18n.language === "en" ? "en" : "de" };
  return fetch(url, { method: "POST", signal: ac.signal,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
    body: JSON.stringify(nutzlast) }).finally(() => clearTimeout(t));
};

// DOM -> Markdown Rueckserialisierung fuer den Selbst-schreiben-Modus. Invertiert genau das
// Subset, das renderMarkdown erzeugt. Nur GEAENDERTE Sections werden serialisiert (dirty-Set),
// alle anderen behalten ihr Original-Markdown - so bleibt der Verlust minimal begrenzt.
// Bekannte Grenze: nummerierte Listen einer editierten Section werden zu "- " (der Renderer
// unterscheidet sie nicht); Figuren tragen data-marker und werden verbatim zurueckgeschrieben.
function domSectionToMarkdown(el) {
  const inline = (node) => {
    let out = "";
    node.childNodes.forEach(ch => {
      if (ch.nodeType === 3) out += ch.textContent;
      else if (ch.nodeType !== 1) return;
      else if (ch.tagName === "BR") out += "\n";
      else if (ch.tagName === "STRONG" || ch.tagName === "B") out += `**${inline(ch)}**`;
      else if (ch.tagName === "EM" || ch.tagName === "I") out += `*${inline(ch)}*`;
      else if (ch.tagName === "CODE") out += `\`${ch.textContent}\``;
      else out += inline(ch);
    });
    return out;
  };
  const parts = [];
  el.childNodes.forEach(ch => {
    if (ch.nodeType === 3) { const t = ch.textContent; if (t.trim()) parts.push(inline({ childNodes: [ch] })); return; }
    if (ch.nodeType !== 1) return;
    const tag = ch.tagName;
    if (/^H[1-6]$/.test(tag)) { parts.push(`${"#".repeat(Number(tag[1]))} ${inline(ch)}\n`); return; }
    if (tag === "BR") { parts.push("\n"); return; }
    if (tag === "DIV" && ch.getAttribute("style")?.includes("height:")) { parts.push("\n"); return; } // Absatz-Spacer
    if (tag === "PRE") { const code = ch.querySelector("code"); parts.push("```\n" + (code ? code.textContent : ch.textContent).replace(/\n$/, "") + "\n```\n"); return; }
    if (tag === "LI") { parts.push(`- ${inline(ch)}\n`); return; }
    if (tag === "FIGURE") { const mk = ch.getAttribute("data-marker"); if (mk) parts.push(`\n${mk}\n`); return; }
    if (tag === "TABLE") {
      const rows = [...ch.querySelectorAll("tr")].map(tr => [...tr.children].map(td => inline(td).replace(/\|/g, "\\|").replace(/\n/g, " ").trim()));
      if (rows.length) {
        parts.push(`| ${rows[0].join(" | ")} |\n|${rows[0].map(() => "---").join("|")}|\n`);
        rows.slice(1).forEach(r => parts.push(`| ${r.join(" | ")} |\n`));
        parts.push("\n");
      }
      return;
    }
    parts.push(inline(ch)); // unbekannte Wrapper: Inhalt als Text erhalten (kein stiller Verlust)
  });
  let md = parts.join("");
  md = md.replace(/\n{3,}/g, "\n\n").replace(/^\n+/, "");
  if (!md.endsWith("\n\n")) md = md.replace(/\n*$/, "\n\n");
  return md;
}

const _normName = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
// Ersetzt [[DIAGRAMM:<id>|<caption>]]-Marker an Ort und Stelle durch das passende Figure.
// figFor(kind, svg, caption) liefert das HTML (renderMarkdown und buildPrintHtml stylen unterschiedlich).
// Liefert {html, usedBpmn:Set<index>, leftInline:Array} fuer den Fallback-Sammeldump.
const placeDiagramMarkers = (html, bpmnItems = [], inlineItems = [], figFor) => {
  const usedBpmn = new Set();
  const usedInline = new Set();
  const sp = '(?:<br\\/>|<div style="height:\\d+px"><\\/div>)*';
  const out = html.replace(new RegExp(`${sp}\\s*\\[\\[\\s*DIAGRAMM\\s*:\\s*([^\\]|]+?)\\s*(?:\\|\\s*([^\\]]*?))?\\s*\\]\\]\\s*${sp}`, "gi"), (_, id, cap) => {
    id = (id || "").trim();
    cap = (cap || "").trim();
    const m = id.match(/^bpmn\s*:\s*(.+)$/i);
    if (m) {
      const want = _normName(m[1]);
      let idx = bpmnItems.findIndex((b, i) => !usedBpmn.has(i) && _normName(b.name) === want);
      if (idx < 0) idx = bpmnItems.findIndex((b, i) => !usedBpmn.has(i) && _normName(b.name) && (_normName(b.name).includes(want) || want.includes(_normName(b.name))));
      if (idx < 0) return "";
      usedBpmn.add(idx);
      const b = bpmnItems[idx];
      return figFor("bpmn", b.svg || "", cap || (b.name ? i18n.t("doku:dokument.prozessdiagrammMitName", { name: b.name }) : i18n.t("doku:dokument.prozessdiagramm")), b.name || "");
    }
    // Landschafts-/Beziehungsdiagramm per Slug-Id (z.B. systemlandschaft, inbound-systeme)
    const want = _normName(id);
    let idx = inlineItems.findIndex((x, i) => !usedInline.has(i) && _normName(x.id) === want);
    if (idx < 0 && /^systemlandschaft$/i.test(id)) idx = inlineItems.findIndex((x, i) => !usedInline.has(i) && (_normName(x.id) === "systemlandscape" || _normName(x.id) === "systemlandschaft"));
    if (idx < 0 && /^systemlandschaft$/i.test(id)) idx = inlineItems.findIndex((x, i) => !usedInline.has(i));
    if (idx < 0) return "";
    usedInline.add(idx);
    const s = inlineItems[idx];
    return figFor("landscape", s.svg || "", cap || s.title || i18n.t("doku:dokument.systemlandschaft"), s.id || "");
  });
  const leftInline = inlineItems.filter((_, i) => !usedInline.has(i));
  return { html: out, usedBpmn, leftInline };
};

const ensurePrintStyles = () => {
  if (document.getElementById("doku-print-css")) return;
  const s = document.createElement("style");
  s.id = "doku-print-css";
  s.textContent = `@media print{body>*:not(#doku-print-root){display:none!important}#doku-print-root{position:fixed;inset:0;z-index:99999;background:#fff;overflow:auto;display:block!important}#doku-print-root .doku-no-print{display:none!important}#doku-print-root .doku-print-body{padding:0;max-height:none;overflow:visible;overflow-wrap:anywhere}#doku-print-root .doku-print-body table{max-width:100%}#doku-print-root .doku-svg-figure{page-break-inside:avoid;max-width:100%}#doku-print-root .doku-print-body table{page-break-inside:avoid}@page{size:A4;margin:20mm 18mm 25mm 18mm}}`;
  document.head.appendChild(s);
};

const readFileAsBase64 = (f) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result.split(",")[1]);
  r.onerror = reject;
  r.readAsDataURL(f);
});
const readFileAsDataUrl = (f) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(f);
});

// Dateiformat-Kuerzel fuer Mono-Tags (Verfeinerungs-Board)
const dokuFmtOf = (mime, name) => {
  const s = `${mime || ""} ${name || ""}`.toLowerCase();
  if (s.includes("pdf")) return "PDF";
  if (s.includes("presentation") || s.includes(".pptx") || s.includes(".ppt")) return "PPTX";
  return "DOCX";
};

// CI-Einwurf-Slot: leer = dashed Einwurf (Label + Format), befuellt = Thumbnail/Format-Kaestchen
// + Dateiname + Entfernen. Bewusst KEIN Klick-Overwrite im befuellten Zustand (Fehlbedienung).
function EnrichSlot({ label, fmt, accept, file, onChange, isImage, disabled }) {
  const { t } = useTranslation("doku");
  const ref = useRef(null);
  const pick = () => { if (!disabled && !file) ref.current?.click(); };
  return (
    <div className={"dk-enrich-slot" + (file ? " is-filled" : "")} role="button" tabIndex={disabled || file ? -1 : 0}
      onClick={pick} onKeyDown={e => { if ((e.key === "Enter" || e.key === " ") && !file) { e.preventDefault(); pick(); } }}>
      <input ref={ref} type="file" accept={accept} style={{ display: "none" }} onChange={async e => {
        const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
        const base64 = await readFileAsBase64(f);
        onChange({ name: f.name, base64, mime: f.type });
      }} />
      {file ? (<>
        {isImage && file.base64
          ? <img className="dk-enrich-slot-thumb" alt="" src={`data:${file.mime || "image/png"};base64,${file.base64}`} />
          : <span className="dk-enrich-slot-box">{dokuFmtOf(file.mime, file.name)}</span>}
        <span className="dk-enrich-slot-label" title={file.name}>{file.name}</span>
        <button type="button" className="dk-enrich-slot-remove" aria-label={t("stil.beigabeEntfernen", { name: label })} onClick={e => { e.stopPropagation(); onChange(null); }}>
          <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </>) : (<>
        <svg className="plus" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>
        <span className="dk-enrich-slot-label">{label}</span>
        <span className="dk-enrich-slot-fmt">{fmt}</span>
      </>)}
    </div>
  );
}

// Fachlicher Kontext: EINE gemeinsame Multi-Dropzone (Drag & Drop + Klick), Dateien landen als Chips.
function EnrichDropzone({ onAdd, disabled }) {
  const { t } = useTranslation("doku");
  const ref = useRef(null);
  const [drag, setDrag] = useState(false);
  const handle = async (fileList) => {
    const ok = Array.from(fileList).filter(f => /\.(pdf|pptx?|docx?)$/i.test(f.name));
    if (!ok.length) return;
    const docs = [];
    for (const f of ok) docs.push({ name: f.name, base64: await readFileAsBase64(f), mime: f.type });
    onAdd(docs);
  };
  return (
    <div className={"dk-enrich-dropzone" + (drag ? " is-drag" : "")} role="button" tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && ref.current?.click()}
      onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); if (!disabled) ref.current?.click(); } }}
      onDragOver={e => { e.preventDefault(); if (!disabled) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={e => { e.preventDefault(); setDrag(false); if (!disabled && e.dataTransfer?.files?.length) handle(e.dataTransfer.files); }}>
      <input ref={ref} type="file" accept=".pdf,.pptx,.ppt,.docx,.doc" multiple style={{ display: "none" }}
        onChange={e => { if (e.target.files?.length) handle(e.target.files); e.target.value = ""; }} />
      <svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" /></svg>
      <span className="dk-enrich-dz-label">{t("stil.ablegeZone")}</span>
      <span className="dk-enrich-formats"><span>.PDF</span><span>.PPTX</span><span>.DOCX</span></span>
    </div>
  );
}

// Personenmarke: der RING traegt die Farbe, die Initialen tragen den Kontrast. Weisse
// Initialen auf gefuellter Flaeche erreichen nur 2,3 bis 3,5:1 - der Ring loest dasselbe
// bei 12,6:1 und ist zugleich der Formkanal gegen die eckigen Diff-Plaettchen.
// aria-hidden, weil der Name als Text daneben steht (sonst liest der Screenreader doppelt).
const dokuInitialen = (name) => String(name || "?").trim().split(/\s+/)
  .slice(0, 2).map(t => t[0]).join("").toUpperCase();
const Avatar = ({ farbe, name, gross }) => (
  <i className={"dk-avatar" + (gross ? " gr" : "")}
    data-farbe={farbe == null ? "x" : farbe} title={name} aria-hidden="true">{dokuInitialen(name)}</i>
);

function sseReader(response, onEvent) {
  return new Promise(async (resolve, reject) => {
    try {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const raw = line.slice(6);
          if (raw === "[DONE]") continue;
          try { onEvent(JSON.parse(raw)); } catch {}
        }
      }
      resolve();
    } catch (e) { reject(e); }
  });
}

let flowIdCounter = 0;

const DokuPage = ({ spaces = [], branding }) => {
  const { t, i18n: i18nInst } = useTranslation(["doku", "gemeinsam"]);
  // Zahlen und Datumsangaben folgen der Sprache: ein festes de-DE macht im Englischen die ZAHL
  // falsch (18.859 liest sich dort als achtzehn Komma acht).
  const gebiet = i18nInst.language === "en" ? "en-GB" : "de-DE";
  // Spiegel fuer alles, was in useCallback laeuft: so bleibt keine Abhaengigkeitsliste anzufassen
  // und ein Sprachwechsel loest keinen Schnittstellenaufruf aus.
  const tRef = useRef(t);
  tRef.current = t;
  // Kennung bleibt woertlich, Beschriftung kommt aus dem Katalog. Eine wiederhergestellte Sitzung
  // kann eine unbekannte Kennung mitbringen - dann steht sie selbst da statt eines Schluessels.
  const stilTitel = (id) => t(`stil.${id}.titel`, { defaultValue: id || "" });
  const kiHinweis = t("dokument.kiHinweis");
  const [flows, setFlows] = useState([]);
  const [processName, setProcessName] = useState("");
  const [style, setStyle] = useState("technical");
  const [docContent, setDocContent] = useState("");
  const [svgs, setSvgs] = useState([]);
  const [bpmnSvgs, setBpmnSvgs] = useState([]);
  const [validation, setValidation] = useState(null);
  // Befundprotokoll: standardmaessig zugeklappt. Die Kopfzeile nennt die Anzahl, die Belege
  // liest wer sie braucht - aufgeklappt verdeckt die Liste sonst das Dokument.
  const [befundeOffen, setBefundeOffen] = useState(false);
  // Faktenbasis der Generierung. Ohne sie ist jede spaetere Korrektur quellenlos.
  const [docFacts, setDocFacts] = useState("");
  const [loading, setLoading] = useState(false);
  const [activePhase, setActivePhase] = useState(0);
  const [phaseDone, setPhaseDone] = useState({});
  const [error, setError] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef(null);
  const abortRef = useRef(null);
  const depAbortRef = useRef(null);
  const depMetaRef = useRef(null); // Pipeline-Metadaten der letzten Abhaengigkeitssuche (fuer die Doku-Generierung)
  const contentRef = useRef(null);
  const printRootRef = useRef(null);

  const [ciLogo, setCiLogo] = useState(null);
  const [ciPpt, setCiPpt] = useState(null);
  const [ciPdf, setCiPdf] = useState(null);
  const [bizDocs, setBizDocs] = useState([]);
  const [bizText, setBizText] = useState(""); // Freitext-Fachkontext (fliesst in die Generierung ein)

  // Tokenlimit-Dialog: {sessionId, usedOutputTokens, maxTokens, estimated?} vom Backend,
  // wenn die Redaktion am Output-Limit abgeschnitten wurde -> User entscheidet aktiv.
  const [tokenLimit, setTokenLimit] = useState(null);
  const [contLoading, setContLoading] = useState(false);

  // Abgeschlossene Schritte (1-3) klappen waehrend/nach der Generierung zu; manuell aufklappbar.
  const [sectOpen, setSectOpen] = useState({});
  // Technische Zusammenfassung vom Backend: {model, inputTokens, outputTokens, durationMs}
  const [genStats, setGenStats] = useState(null);
  // Verbrauch der NACHTRAeGLICHEN Bearbeitungen, getrennt von der Erzeugung: der Nutzer soll
  // sehen, was ihn das Erstellen und was ihn das Nacharbeiten gekostet hat.
  const [editStats, setEditStats] = useState(null);   // {input,output,calls}
  const zaehleVerbrauch = useCallback((v) => {
    if (!v || (!v.input && !v.output)) return;
    setEditStats(a => ({ input:(a?.input||0)+(v.input||0), output:(a?.output||0)+(v.output||0), calls:(a?.calls||0)+1 }));
  }, []);
  // Ende-Kennzeichnung: "complete" | "truncated" (Tokenlimit) | "cancelled" (manuell)
  const [genEnd, setGenEnd] = useState(null);
  // Gespeicherte Doku-Sessions (Weitermachen-Liste) + aktive Session
  const [dokuSessions, setDokuSessions] = useState([]);
  const [sessQuery, setSessQuery] = useState("");   // Suche in der Weitermachen-Liste
  const [sessShown, setSessShown] = useState(5);    // Seitenweise: 5, dann "Mehr anzeigen"
  const [restoredMeta, setRestoredMeta] = useState(null); // {flowNames} nach Wiederherstellung
  const sessIdRef = useRef(null);
  const saveTimerRef = useRef(null);

  const [appendInput, setAppendInput] = useState("");
  const [appendLoading, setAppendLoading] = useState(false);
  const [fbRating, setFbRating] = useState(null);   // 'up' | 'down' | null
  const [fbText, setFbText] = useState("");
  const [fbSent, setFbSent] = useState(false);
  const [figPopup, setFigPopup] = useState(null);   // {kind,id,cap,screenX,screenY,instruction,msg}
  const [figLoading, setFigLoading] = useState(false);
  const [addFigOpen, setAddFigOpen] = useState(false);
  const [addFigText, setAddFigText] = useState("");
  const [addFigLoading, setAddFigLoading] = useState(false);
  const [addFigMsg, setAddFigMsg] = useState("");

  // Dynamische Agenten-Pipeline (Map-Reduce: je iFlow ein Analyse-Agent)
  const [pipeline, setPipeline] = useState(null);
  const [agentStatus, setAgentStatus] = useState({});
  const [analyseProg, setAnalyseProg] = useState({ done: 0, total: 0 });

  // Quelle: ZIP-Upload oder iFlow direkt aus einer Instanz waehlen
  const [sourceMode, setSourceMode] = useState("upload");
  const [tnSpaceId, setTnSpaceId] = useState("");
  const [tnInstances, setTnInstances] = useState([]);
  const [tnInstId, setTnInstId] = useState("");
  const [tnPkgId, setTnPkgId] = useState("");
  // EIN Eingabefeld fuer beide Wege. Leer = Paketauswahl regiert die Liste, Text = Suche ueber
  // die ganze Instanz regiert sie. Nie beides gleichzeitig - zwei Wege zum selben Ziel
  // nebeneinander sind keine Wahlfreiheit, sondern eine Frage, die niemand beantworten kann.
  const [tnGlobalTreffer, setTnGlobalTreffer] = useState(null);
  const [tnGlobalLaeuft, setTnGlobalLaeuft] = useState(false);
  const [tnPackages, setTnPackages] = useState([]);
  const [tnFlows, setTnFlows] = useState([]);
  const [tnSearch, setTnSearch] = useState("");
  const [tnLoading, setTnLoading] = useState({});
  const [tnError, setTnError] = useState(null);

  const [editPopup, setEditPopup] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Selbst-schreiben-Modus: contentEditable auf dem Dokument; Aenderungen werden section-genau
  // zurueck nach Markdown serialisiert (dirty-Tracking), KI-Popups sind dabei unterdrueckt.
  const [selfEdit, setSelfEdit] = useState(false);
  // Spiegel fuer saveSession: die Speicherfunktion haengt an keinem Zustand und saehe sonst
  // einen veralteten Wert - genau dann, wenn es darauf ankommt (Konflikt waehrend des Tippens).
  const selfEditRef = useRef(false);
  selfEditRef.current = selfEdit;
  // Jede Nutzereingabe wird eine kleine Version: Anweisung, Aktion, Vorher, Nachher und die
  // betroffenen Abschnitte. Damit ist nachvollziehbar, WAS sich auf welche Anweisung hin
  // geaendert hat - vorher war jede Aenderung eine Blackbox.
  const [revisionen, setRevisionen] = useState([]);

  // === Teilen: Mitwirkende, eigene Rolle, Schreibkonflikt ===
  // Ein Schalter, ein Zustand: alles Neue haengt an "geteilt" (mehr als eine Person hat
  // Zugriff). Ohne Mitwirkende wird kein einziges der neuen Elemente gerendert.
  const [mitwirkende, setMitwirkende] = useState([]);
  const [meineRolle, setMeineRolle] = useState("besitzer");
  const [sessId, setSessId] = useState(null);       // Spiegel von sessIdRef fuer das Rendern
  const [ich, setIch] = useState(null);             // eigener Benutzer (fuer den Urheberstempel)
  const [teilenOffen, setTeilenOffen] = useState(false);
  const [einladungen, setEinladungen] = useState([]);
  const [neuerLink, setNeuerLink] = useState(null);
  // Einladung aus der Adresszeile (?einladung=...): Annahme-Ansicht statt Dokument.
  const [einladungAnnahme, setEinladungAnnahme] = useState(null);
  const [annahmeBusy, setAnnahmeBusy] = useState(false);
  const [teilenRolle, setTeilenRolle] = useState("lesen");  // harmlose Rolle ist die Vorbelegung
  const [teilenBusy, setTeilenBusy] = useState(false);
  const [teilenMeldung, setTeilenMeldung] = useState(null); // {art:"ok"|"fehler", text}
  const [bestaetigung, setBestaetigung] = useState(null);   // {userId, art:"hoch"|"entzug"}
  // Ein Speichervorgang ersetzt Text UND Verlauf vollstaendig. basisStand ist der zuletzt
  // bekannte Serverstand - bewusst ein Ref, weil er kein Rendern ausloesen darf.
  const basisStandRef = useRef(null);
  const konfliktRef = useRef(false);
  const [konflikt, setKonflikt] = useState(null);   // {text} - nicht wegklickbar, siehe unten

  // Der EINE Schalter. Besitzer allein = ein Eintrag, also nicht geteilt. Ehemalige Mitwirkende
  // zaehlen mit: im Verlauf stehen weiterhin Aenderungen zweier Personen, die zuzuordnen sind.
  const geteilt = mitwirkende.length > 1;
  // Der Knopf spricht dagegen ueber ZUGRIFF, nicht ueber Urheberschaft - "Geteilt · 3" waere
  // eine Falschaussage, wenn zwei davon keinen Zugriff mehr haben.
  const zugriffAnzahl = mitwirkende.filter(m => !m.entzogen).length;
  const besitzer = mitwirkende.find(m => m.istBesitzer) || null;
  const istBesitzer = meineRolle === "besitzer";
  const darfSchreiben = meineRolle !== "lesen";
  // Farbe eines Urhebers. Altbestand ohne "wer" ist sicher dem Besitzer zuzuordnen - vor der
  // Teilen-Funktion konnte niemand sonst aendern. Ehemalige Mitwirkende stehen weiterhin in der
  // Liste, ihr Farbplatz wird nie neu vergeben - sonst wuerden alte Aenderungen rueckwirkend
  // einer anderen Person zugeschrieben.
  const farbeVon = useCallback((userId) => {
    const uid = userId || besitzer?.userId;
    return uid ? (mitwirkende.find(x => x.userId === uid)?.farbe ?? null) : null;
  }, [mitwirkende, besitzer]);

  const [revOffen, setRevOffen] = useState(null);   // Index der aufgeklappten Vorher-Ansicht
  const [revsOffen, setRevsOffen] = useState(false); // Liste im Kopf: zugeklappt, sonst frisst sie Platz
  const [zeigeAenderungen, setZeigeAenderungen] = useState(true);
  const [chgPop, setChgPop] = useState(null);       // Popup am Info-Symbol: {idx, pos, screenX, screenY}
  const [docDomKey, setDocDomKey] = useState(0); // erzwingt Re-Mount des gerenderten DOMs (Verwerfen/Uebernehmen)
  const dirtySectionsRef = useRef(new Set());

  // Verstrichene Zeit waehrend der Generierung (Fortschrittszeile)
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    if (!loading) return;
    setElapsed(0);
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Math.floor((Date.now() - t0) / 1000)), 1000);
    return () => clearInterval(iv);
  }, [loading]);

  const [connections, setConnections] = useState([]);
  const [depStatus, setDepStatus] = useState(null); // {loading, message, capReached, unresolved}
  const [editConn, setEditConn] = useState(null);
  const [drawingConn, setDrawingConn] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [cardPos, setCardPos] = useState({});
  const [measureTick, setMeasureTick] = useState(0);
  const canvasRef = useRef(null);
  const scrollRef = useRef(null);
  const cardRefs = useRef({});
  const flowsRef = useRef([]);
  flowsRef.current = flows;

  // Bei Fensteraenderung neu vermessen (Umbruch der Karten haengt von der Breite ab).
  useEffect(() => {
    let raf = 0;
    const onResize = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setMeasureTick(t => t + 1)); };
    window.addEventListener("resize", onResize);
    return () => { window.removeEventListener("resize", onResize); cancelAnimationFrame(raf); };
  }, []);

  // Auto-Scroll des Canvas, wenn die Maus beim Ziehen an den Rand kommt.
  const edgeAutoScroll = useCallback((clientX) => {
    const el = scrollRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const edge = 64, step = 16;
    if (clientX < r.left + edge) el.scrollLeft -= step;
    else if (clientX > r.right - edge) el.scrollLeft += step;
  }, []);

  useEffect(() => { ensurePrintStyles(); }, []);

  const addFiles = useCallback(async (fileList) => {
    const zips = Array.from(fileList).filter(f => f.name.toLowerCase().endsWith(".zip"));
    if (!zips.length) { setError(tRef.current("meldung.keineZip")); return; }
    setError(null);
    for (const f of zips) {
      const id = ++flowIdCounter;
      setFlows(prev => [...prev, { id, file: f, zipInfo: null, inspecting: true }]);
      try {
        const base64 = await readFileAsBase64(f);
        const res = await fetch("/api/doku/inspect", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify({ zipBase64: base64 }) });
        if (!res.ok) throw new Error((await res.json()).error || tRef.current("meldung.zipLesen"));
        const info = await res.json();
        setFlows(prev => prev.map(fl => fl.id === id ? { ...fl, zipInfo: info, inspecting: false } : fl));
      } catch (e) {
        setFlows(prev => prev.filter(fl => fl.id !== id));
        setError(e.message);
      }
    }
  }, []);

  const removeFlow = useCallback((id) => {
    setFlows(prev => prev.filter(fl => fl.id !== id));
    setConnections(prev => prev.filter(c => c.fromId !== id && c.toId !== id));
    delete cardRefs.current[id];
  }, []);

  const onDrop = useCallback((e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files); }, [addFiles]);

  // readyFlows = analysierte iFlows (Haupt-iFlow + alle ladbaren Abhaengigkeiten inkl. Pipeline-Backbone).
  const readyFlows = useMemo(() => flows.filter(fl => fl.kind !== "artifact" && fl.zipInfo && !fl.inspecting && (fl.zipBase64 || fl.file)), [flows]);
  const isMulti = flows.length > 1;

  // === iFlow direkt aus einer Instanz waehlen ===
  // Instanzen pro Space laden (spaces aus /api/spaces enthalten sie nicht inline).
  useEffect(() => {
    if (!tnSpaceId) { setTnInstances([]); setTnInstId(""); return; }
    let cancelled = false;
    setTnInstances([]); setTnInstId(""); setTnError(null);
    setTnLoading(p => ({ ...p, instances: true }));
    api(`/api/spaces/${tnSpaceId}/instances`)
      .then(d => { if (!cancelled) setTnInstances(Array.isArray(d) ? d : []); })
      .catch(e => { if (!cancelled) setTnError(tRef.current("meldung.instanzenLaden", { grund: e.message })); })
      .finally(() => { if (!cancelled) setTnLoading(p => ({ ...p, instances: false })); });
    return () => { cancelled = true; };
  }, [tnSpaceId]);

  // Packages laden, sobald eine Instanz gewaehlt ist.
  // Der Suchtext wird dabei geleert: er gehoert zur alten Instanz. Bliebe er stehen, waere die
  // Oberflaeche im Suchmodus mit gesperrter Paketauswahl, obwohl gerade eine neue Instanz
  // gewaehlt wurde - ein Zustand, den niemand beabsichtigt hat.
  useEffect(() => {
    if (!tnInstId) { setTnPackages([]); setTnPkgId(""); setTnSearch(""); setTnGlobalTreffer(null); return; }
    let cancelled = false;
    setTnPackages([]); setTnPkgId(""); setTnFlows([]); setTnError(null);
    setTnSearch(""); setTnGlobalTreffer(null);
    setTnLoading(p => ({ ...p, packages: true }));
    api(`/api/${tnInstId}/content/packages`)
      .then(d => { if (!cancelled) setTnPackages(Array.isArray(d) ? d : []); })
      .catch(e => { if (!cancelled) setTnError(tRef.current("meldung.paketeLaden", { grund: e.message })); })
      .finally(() => { if (!cancelled) setTnLoading(p => ({ ...p, packages: false })); });
    return () => { cancelled = true; };
  }, [tnInstId]);

  // iFlows des gewaehlten Packages laden
  useEffect(() => {
    if (!tnInstId || !tnPkgId) { setTnFlows([]); return; }
    let cancelled = false;
    setTnFlows([]); setTnError(null);
    setTnLoading(p => ({ ...p, flows: true }));
    api(`/api/${tnInstId}/content/packages/${encodeURIComponent(tnPkgId)}/flows`)
      .then(d => { if (!cancelled) setTnFlows(Array.isArray(d) ? d : []); })
      .catch(e => { if (!cancelled) setTnError(tRef.current("meldung.flowsLaden", { grund: e.message })); })
      .finally(() => { if (!cancelled) setTnLoading(p => ({ ...p, flows: false })); });
    return () => { cancelled = true; };
  }, [tnInstId, tnPkgId]);

  // Automatische Abhängigkeitssuche: holt den Graph, fügt abhängige iFlows + geteilte Artefakte als Knoten + Kanten ein.
  const searchDependencies = useCallback(async (rootLocalId, rootFlowId, iid, packageId) => {
    if (depAbortRef.current) depAbortRef.current.abort();
    const ctrl = new AbortController(); depAbortRef.current = ctrl;
    setDepStatus({ loading: true, message: tRef.current("fluss.abhaengigkeitenSuchen") });
    try {
      const graph = await api(`/api/${iid}/doku/dependencies`, { method: "POST", body: JSON.stringify({ flowId: rootFlowId, packageId }), timeout: 300000, signal: ctrl.signal });
      depMetaRef.current = (graph.pipeline && graph.pipeline.isMember) ? graph.pipeline : null;
      const existing = flowsRef.current;
      const nodeToLocal = new Map([[rootFlowId, rootLocalId]]);
      const newFlows = [];
      const iflowNodes = (graph.nodes || []).filter(n => n.kind === "iflow" && n.id !== rootFlowId);
      // Alle abhaengigen iFlows (ProcessDirect-Abhaengigkeiten UND Pipeline-Backbone) werden geladen und
      // mit-analysiert. Der vom Benutzer gewaehlte iFlow bleibt der Haupt-iFlow (auto=false); die ergaenzten
      // sind unterstuetzender Kontext (auto=true) mit Relation/Step/Rolle fuer die Beschriftung.
      await Promise.all(iflowNodes.map(async (n) => {
        const ex = existing.find(fl => fl.source === "tenant" && fl.flowId === n.id && fl.iid === iid);
        if (ex) { nodeToLocal.set(n.id, ex.id); return; }
        const lid = ++flowIdCounter;
        nodeToLocal.set(n.id, lid);
        const base = { id: lid, source: "tenant", iid, flowId: n.id, packageId: n.packageId, auto: true, relation: n.relation, pipelineStep: n.pipelineStep, pipelineRole: n.pipelineRole };
        if (n.contentRestricted) { newFlows.push({ ...base, zipInfo: { flowName: n.flowName, scriptCount: 0 }, restricted: true }); return; }
        try {
          const r = await api("/api/doku/fetch-iflow", { method: "POST", body: JSON.stringify({ iid, flowId: n.id, packageId: n.packageId }), signal: ctrl.signal });
          if (r.contentRestricted) newFlows.push({ ...base, zipInfo: { flowName: n.flowName, scriptCount: 0 }, restricted: true });
          else newFlows.push({ ...base, zipBase64: r.zipBase64, zipInfo: { flowName: n.flowName || r.flowName, scriptCount: r.scriptCount || 0 } });
        } catch { newFlows.push({ ...base, zipInfo: { flowName: n.flowName, scriptCount: 0 }, restricted: true }); }
      }));
      for (const n of (graph.nodes || []).filter(n => n.kind === "artifact")) {
        const exA = existing.find(fl => fl.kind === "artifact" && fl.artifactName === n.name && fl.artifactType === n.artifactType);
        if (exA) { nodeToLocal.set(n.id, exA.id); continue; }
        const lid = ++flowIdCounter;
        nodeToLocal.set(n.id, lid);
        newFlows.push({ id: lid, kind: "artifact", artifactType: n.artifactType, artifactName: n.name, referencedBy: n.referencedBy || [], auto: true });
      }
      const newConns = [];
      for (const e of (graph.edges || [])) {
        const from = nodeToLocal.get(e.from), to = nodeToLocal.get(e.to);
        if (from != null && to != null && from !== to) newConns.push({ fromId: from, toId: to, label: e.label || "", auto: true });
      }
      if (newFlows.length) setFlows(prev => { const have = new Set(prev.map(f => f.id)); return [...prev, ...newFlows.filter(f => !have.has(f.id))]; });
      if (newConns.length) setConnections(prev => { const k = c => `${c.fromId}->${c.toId}`; const have = new Set(prev.map(k)); return [...prev, ...newConns.filter(c => !have.has(k(c)))]; });
      const cnt = newFlows.length, unres = (graph.unresolved || []).length;
      const pl = depMetaRef.current;
      const plMsg = pl
        ? (pl.rootStep
            ? tRef.current("fluss.pipelineErkanntStep", { bereich: pl.scope || "", step: `${String(pl.rootStep).padStart(2, "0")}${pl.rootRole ? " " + pl.rootRole : ""}` })
            : tRef.current("fluss.pipelineErkannt", { bereich: pl.scope || "" }))
        : "";
      setDepStatus((cnt || unres || graph.capReached || pl) ? {
        loading: false,
        message: cnt ? `${tRef.current("fluss.positionen", { count: cnt })}${plMsg ? " · " + plMsg : ""}` : (plMsg || tRef.current("fluss.keineAbhaengigkeiten")),
        capReached: !!graph.capReached, unresolved: unres,
      } : null);
    } catch (e) {
      if (e.name === "AbortError" || ctrl.signal.aborted) { setDepStatus(null); return; }
      setDepStatus({ loading: false, message: tRef.current("meldung.abhaengigkeitenFehlgeschlagen", { grund: e.message || tRef.current("meldung.fehler") }) });
    } finally {
      if (depAbortRef.current === ctrl) depAbortRef.current = null;
    }
  }, []);

  // pkgOverride: aus der instanzweiten Suche kommt das Paket am Treffer mit, nicht aus der
  // Auswahlliste - sonst landete ein gesuchter iFlow unter dem gerade gewaehlten fremden Paket.
  const addTenantFlow = useCallback(async (flow, pkgOverride) => {
    if (flows.some(fl => fl.source === "tenant" && fl.flowId === flow.Id && fl.iid === tnInstId)) return;
    // Auf undefined pruefen, NICHT auf falsy: liefert der Tenant kein Paket am Artefakt, ist der
    // Wert ein leerer String - mit "||" fiele die Kette dann auf die gerade gesperrte und gar
    // nicht sichtbare Paketauswahl zurueck und der iFlow landete unter einem fremden Paket.
    // Ohne Paket faellt lediglich der paketbezogene Policy-Benutzer weg (ein konfigurierter
    // Standardbenutzer traegt weiter); das ist ein sauberes Scheitern statt einer falschen Ablage.
    const pkg = pkgOverride !== undefined ? pkgOverride : (flow.PackageId || tnPkgId);
    setTnError(null);
    setTnLoading(p => ({ ...p, adding: flow.Id }));
    try {
      const r = await api("/api/doku/fetch-iflow", { method: "POST", body: JSON.stringify({ iid: tnInstId, flowId: flow.Id, packageId: pkg }) });
      if (r.contentRestricted) {
        setTnError(tRef.current("meldung.nichtVerwendbar", { name: flow.Name || flow.Id, grund: r.reason || tRef.current("meldung.contentNichtLesbar") }));
        return;
      }
      const id = ++flowIdCounter;
      setFlows(prev => [...prev, {
        id, source: "tenant", iid: tnInstId, flowId: flow.Id, packageId: pkg,
        zipBase64: r.zipBase64,
        zipInfo: { flowName: flow.Name || r.flowName || flow.Id, scriptCount: r.scriptCount || 0 },
        inspecting: false,
      }]);
      // Verbindungssuche ist optional geworden - Button "Verbindungen suchen" in Sektion 2.
    } catch (e) {
      setTnError(tRef.current("meldung.iflowLaden", { grund: e.message }));
    } finally {
      setTnLoading(p => ({ ...p, adding: null }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flows, tnInstId, tnPkgId]);

  // Der eine Schalter: sobald Text im Feld steht, gilt die Suche und nichts anderes.
  const sucheAktiv = tnSearch.trim().length > 0;
  const sucheRef = useRef(null);

  // Suche ueber die ganze Instanz, entkoppelt vom Paket. Erst ab zwei Zeichen und mit Verzoegerung:
  // die erste Anfrage je Instanz muss im schlechtesten Fall jedes Paket einzeln lesen.
  useEffect(() => {
    const q = tnSearch.trim();
    if (!tnInstId || q.length < 2) { setTnGlobalTreffer(null); setTnGlobalLaeuft(false); return; }
    setTnGlobalLaeuft(true);
    let weg = false;
    const t = setTimeout(() => {
      api(`/api/${tnInstId}/content/iflow-suche?q=${encodeURIComponent(q)}`)
        .then(d => { if (!weg) setTnGlobalTreffer(d); })
        // t ist hier die Zeitschaltung (const t = setTimeout), also laeuft die Uebersetzung
        // ueber tRef - sonst waere die Uebersetzungsfunktion ueberschattet.
        .catch(e => { if (!weg) { setTnGlobalTreffer(null); setTnError(tRef.current("meldung.sucheFehlgeschlagen", { grund: e.message })); } })
        .finally(() => { if (!weg) setTnGlobalLaeuft(false); });
    }, 400);
    return () => { weg = true; clearTimeout(t); };
  }, [tnSearch, tnInstId]);

  // === Drag & Drop Reorder ===
  const handleDragStart = useCallback((e, idx) => {
    setDragIdx(idx);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  }, []);
  const handleDragOver = useCallback((e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setFlows(prev => {
      const arr = [...prev];
      const [moved] = arr.splice(dragIdx, 1);
      arr.splice(idx, 0, moved);
      return arr;
    });
    setDragIdx(idx);
  }, [dragIdx]);
  const handleDragEnd = useCallback(() => setDragIdx(null), []);

  // === Connection Drawing ===
  const startConnection = useCallback((fromId, e) => {
    if (loading) return;
    e.preventDefault();
    e.stopPropagation();
    const cr = canvasRef.current?.getBoundingClientRect();
    if (!cr) return;
    const portEl = e.currentTarget;
    const pr = portEl.getBoundingClientRect();
    setDrawingConn({ fromId, startX: pr.left + pr.width / 2 - cr.left, startY: pr.top + pr.height / 2 - cr.top, mouseX: pr.left + pr.width / 2 - cr.left, mouseY: pr.top + pr.height / 2 - cr.top });
  }, [loading]);

  const finishConnection = useCallback((toId) => {
    if (!drawingConn || drawingConn.fromId === toId) { setDrawingConn(null); return; }
    const exists = connections.find(c => c.fromId === drawingConn.fromId && c.toId === toId);
    if (!exists) setConnections(prev => [...prev, { fromId: drawingConn.fromId, toId, label: "" }]);
    setDrawingConn(null);
  }, [drawingConn, connections]);

  const removeConnection = useCallback((idx) => {
    setConnections(prev => prev.filter((_, i) => i !== idx));
  }, []);

  const updateConnectionLabel = useCallback((idx, label) => {
    setConnections(prev => prev.map((c, i) => i === idx ? { ...c, label } : c));
  }, []);

  useEffect(() => {
    if (!drawingConn) return;
    const onMove = (e) => {
      edgeAutoScroll(e.clientX);
      const cr = canvasRef.current?.getBoundingClientRect();
      if (!cr) return;
      setDrawingConn(prev => prev ? { ...prev, mouseX: e.clientX - cr.left, mouseY: e.clientY - cr.top } : null);
    };
    const onUp = () => setDrawingConn(null);
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => { document.removeEventListener("mousemove", onMove); document.removeEventListener("mouseup", onUp); };
  }, [drawingConn !== null]);

  // === Card Position Tracking for SVG ===
  const prevCardPosRef = useRef({});
  useLayoutEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !flows.length) return;
    const cr = canvas.getBoundingClientRect();
    const pos = {};
    let changed = false;
    for (const fl of flows) {
      const el = cardRefs.current[fl.id];
      if (!el) continue;
      const r = el.getBoundingClientRect();
      const p = { x: Math.round(r.left - cr.left), y: Math.round(r.top - cr.top), w: Math.round(r.width), h: Math.round(r.height) };
      const prev = prevCardPosRef.current[fl.id];
      if (!prev || prev.x !== p.x || prev.y !== p.y || prev.w !== p.w) changed = true;
      pos[fl.id] = p;
    }
    if (changed) { prevCardPosRef.current = pos; setCardPos(pos); }
  }, [flows, connections.length, loading, measureTick]);

  // Semantische Verbindung: Bogen OBERHALB der horizontalen Kette (Top-Center -> Top-Center).
  const connPath = useCallback((fromId, toId, offset = 0) => {
    const f = cardPos[fromId], t = cardPos[toId];
    if (!f || !t) return null;
    const ax = f.x + f.w / 2, ay = f.y;
    const bx = t.x + t.w / 2, by = t.y;
    const dist = Math.abs(bx - ax);
    const lift = Math.min(36 + dist * 0.09, 60) + Math.abs(offset);
    const apexY = Math.min(ay, by) - lift;
    const c1x = ax + (bx - ax) * 0.25, c1y = apexY;
    const c2x = bx - (bx - ax) * 0.25, c2y = apexY;
    const d = `M ${ax} ${ay} C ${c1x} ${c1y}, ${c2x} ${c2y}, ${bx} ${by}`;
    const chipX = (ax + bx) / 2 + (offset || 0);
    const chipY = 0.125 * (ay + by) + 0.75 * apexY;
    return { d, chipX, chipY };
  }, [cardPos]);

  // Neutrale Ketten-Segmente zwischen benachbarten Karten (zeigt die Reihenfolge).
  const chainSeg = useCallback((fromId, toId) => {
    const a = cardPos[fromId], b = cardPos[toId];
    if (!a || !b) return null;
    return { x1: a.x + a.w, y1: a.y + a.h / 2, x2: b.x, y2: b.y + b.h / 2 };
  }, [cardPos]);

  // Label-Chips an die jeweilige ZIELkarte setzen (verteilt sich, da die Ziele ueber den Canvas verstreut sind)
  // und kollidierende Chips nach oben staffeln, damit alle Beschreibungen lesbar bleiben.
  const chipPositions = useMemo(() => {
    const CHIP_W = 200, CHIP_H = 26;
    const items = [];
    connections.forEach((conn, ci) => {
      const t = cardPos[conn.toId], f = cardPos[conn.fromId];
      if (!t || !f) return;
      const isBidiSecond = connections.some((c, oi) => oi !== ci && c.fromId === conn.toId && c.toId === conn.fromId)
        && connections.findIndex(c => c.fromId === conn.toId && c.toId === conn.fromId) < ci;
      items.push({ ci, conn, x: t.x + t.w / 2, y: t.y - 16 - (isBidiSecond ? CHIP_H : 0) });
    });
    items.sort((a, b) => a.y - b.y || a.x - b.x);
    const placed = [];
    for (const it of items) {
      let y = it.y, guard = 0;
      while (guard++ < 60 && placed.some(p => Math.abs(p.x - it.x) < CHIP_W && Math.abs(p.y - y) < CHIP_H)) y -= CHIP_H;
      placed.push({ ...it, y });
    }
    return placed;
  }, [connections, cardPos]);

  const generate = useCallback(async (extraContext = "") => {
    if (!readyFlows.length) return;
    // Laufende Abhaengigkeitssuche abbrechen - Generieren hat Vorrang.
    if (depAbortRef.current) { depAbortRef.current.abort(); depAbortRef.current = null; }
    setDepStatus(null);
    setLoading(true); setDocContent(""); setSvgs([]); setBpmnSvgs([]); setValidation(null); setError(null); setActivePhase(1); setPhaseDone({});
    setTokenLimit(null); setGenStats(null); setSectOpen({}); setGenEnd(null); setRestoredMeta(null);
    setBefundeOffen(false);
    // Aenderungsverlauf gehoert zum alten Dokument. Ohne dieses Zuruecksetzen markieren die
    // Anker der Vorgaenger-Doku gleichnamige Kapitel der neuen - eine Falschaussage.
    setRevisionen([]); setRevOffen(null); setRevsOffen(false); setChgPop(null);
    // Session ab dem Klick auf "Generieren" anlegen (Weitermachen-Liste)
    sessIdRef.current = null; setSessId(null);
    // Neue Dokumentation: sie gehoert mir allein, alle Teilen-Zustaende starten leer.
    basisStandRef.current = null; konfliktRef.current = false; setKonflikt(null);
    setMitwirkende([]); setMeineRolle("besitzer"); setTeilenOffen(false); setBestaetigung(null); setTeilenMeldung(null);
    try {
      const names = readyFlows.map(fl => fl.zipInfo?.flowName || fl.file?.name?.replace(/\.zip$/i, "") || fl.flowId || "iFlow");
      const r0 = await api("/api/doku/sessions", { method: "POST", body: JSON.stringify({ flowNames: names, style, processName: processName.trim() || undefined, status: "running" }) });
      sessIdRef.current = r0?.id || null;
      setSessId(r0?.id || null);
      basisStandRef.current = r0?.updatedAt || null;
    } catch { /* Speichern ist Komfort */ }
    setFbSent(false); setFbRating(null); setFbText("");
    setPipeline(null); setAgentStatus({}); setAnalyseProg({ done: 0, total: 0 });
    const ctrl = new AbortController(); abortRef.current = ctrl;
    try {
      const flowsPayload = [];
      for (const fl of readyFlows) {
        const base64 = fl.zipBase64 || (fl.file ? await readFileAsBase64(fl.file) : null);
        // main = vom Benutzer gewaehlter Haupt-iFlow (nicht auto-ergaenzt); auto-ergaenzte sind Pipeline/Abhaengigkeit (Kontext).
        if (base64) flowsPayload.push({ zipBase64: base64, main: !fl.auto, relation: fl.relation || null, pipelineStep: fl.pipelineStep ?? null, pipelineRole: fl.pipelineRole || null });
      }
      const connPayload = connections.map(c => {
        const fi = readyFlows.findIndex(fl => fl.id === c.fromId);
        const ti = readyFlows.findIndex(fl => fl.id === c.toId);
        return fi >= 0 && ti >= 0 ? { fromIndex: fi, toIndex: ti, label: c.label || "" } : null;
      }).filter(Boolean);
      const body = { flows: flowsPayload, connections: connPayload, style, processName: processName.trim() || undefined };
      if (ciLogo || ciPpt || ciPdf) {
        body.ci = {};
        if (ciLogo) body.ci.logo = ciLogo;
        if (ciPpt) body.ci.ppt = ciPpt;
        if (ciPdf) body.ci.pdf = ciPdf;
      }
      if (bizDocs.length) body.bizContext = bizDocs;
      if (bizText.trim()) body.bizText = bizText.trim();
      if (sessIdRef.current) body.sessionId = sessIdRef.current; // Server sichert Ergebnis in die Session (ueberlebt Seitenwechsel)
      const sharedArtifacts = flows.filter(fl => fl.kind === "artifact").map(fl => ({ type: fl.artifactType, name: fl.artifactName, referencedBy: fl.referencedBy || [] }));
      if (sharedArtifacts.length) body.sharedArtifacts = sharedArtifacts;
      if (typeof extraContext === "string" && extraContext.trim()) body.extraContext = extraContext.trim();
      if (depMetaRef.current && depMetaRef.current.isMember) body.pipeline = depMetaRef.current;
      // Die erzeugte Dokumentation folgt der Oberflaechensprache. Ohne diese
      // Angabe schriebe der Server immer deutsch, weil seine Prompts deutsch sind.
      body.sprache = i18n.language === "en" ? "en" : "de";
      const res = await fetch("/api/doku/generate", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }, body: JSON.stringify(body), signal: ctrl.signal });
      if (!res.ok) throw new Error((await res.json()).error || "Fehler");
      let sawTokenLimit = false;
      await sseReader(res, ev => {
        if (ev.ping) return;
        if (ev.pipeline) setPipeline(ev.pipeline);
        if (ev.agent) {
          if (ev.status) setAgentStatus(p => ({ ...p, [ev.agent]: ev.status }));
          if (ev.agent === "analyse" && typeof ev.done === "number") setAnalyseProg({ done: ev.done, total: ev.total || 0 });
        }
        if (ev.phase && ev.status === "running") setActivePhase(ev.phase);
        if (ev.phase && ev.status === "done") setPhaseDone(p => ({ ...p, [ev.phase]: true }));
        if (ev.text) setDocContent(prev => prev + ev.text);
        if (ev.svg) setSvgs(prev => [...prev, ev.svg]);
        if (ev.bpmnXml) { try { setBpmnSvgs(prev => [...prev, { name: ev.bpmnName || "", svg: renderIflowSvgString(ev.bpmnXml) }]); } catch {} }
        if (ev.validation) setValidation(ev.validation);
        if (ev.docReplace) setDocContent(ev.docReplace);
        if (ev.tokenLimit) { sawTokenLimit = true; setTokenLimit(ev.tokenLimit); }
        if (ev.stats) setGenStats(ev.stats);
        if (ev.error) { setError(ev.error); setActivePhase(0); }
      });
      // Einmal nach dem Strom, nicht je Token: eine Ueberschrift kann ueber zwei Tokens gehen.
      setDocContent(md => repariereMarkdownSyntax(md));
      setGenEnd(sawTokenLimit ? "truncated" : "complete");
    } catch (e) { if (e.name !== "AbortError") setError(e.message); else setGenEnd("cancelled"); }
    finally { setLoading(false); setActivePhase(0); abortRef.current = null; }
  }, [readyFlows, connections, style, processName, ciLogo, ciPpt, ciPdf, bizDocs, bizText, flows]);

  // Tokenlimit-Fortsetzung: setzt die Redaktion serverseitig nahtlos fort (Assistant-Prefill),
  // neuer Text wird angehaengt. Bei erneutem Limit kommt wieder ein tokenLimit-Event.
  const continueGeneration = useCallback(async () => {
    const sid = tokenLimit?.sessionId;
    if (!sid || contLoading) return;
    setContLoading(true); setError(null); setTokenLimit(null);
    try {
      const res = await fetch("/api/doku/continue", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ sessionId: sid }) });
      if (!res.ok) throw new Error((await res.json()).error || "Fortsetzung fehlgeschlagen");
      let againLimited = false;
      await sseReader(res, ev => {
        if (ev.text) setDocContent(prev => prev + ev.text);
        if (ev.tokenLimit) { againLimited = true; setTokenLimit(ev.tokenLimit); }
        // Die Fortsetzung gehoert zur ERZEUGUNG, ihre Tokens werden addiert. Vorher wurden die
        // Zahlen der Erzeugung schlicht ueberschrieben - die Anzeige log nach jedem Fortsetzen.
        if (ev.stats) setGenStats(g => ({ ...(g || {}), ...ev.stats,
          inputTokens:(g?.inputTokens||0)+(ev.stats.inputTokens||0),
          outputTokens:(g?.outputTokens||0)+(ev.stats.outputTokens||0),
          durationMs:(g?.durationMs||0)+(ev.stats.durationMs||0) }));
        if (ev.error) setError(ev.error);
      });
      setDocContent(md => repariereMarkdownSyntax(md));
      setGenEnd(againLimited ? "truncated" : "complete");
    } catch (e) { setError(e.message); }
    finally { setContLoading(false); }
  }, [tokenLimit, contLoading]);

  // === Doku-Sessions: speichern ab "Generieren", Weitermachen-Liste, Wiederherstellung ===
  // Bewusst eigenes fetch statt api(): der 409-Koerper traegt Name UND Farbe der Person, die
  // zwischenzeitlich geschrieben hat. api() reicht nur Status und Meldungstext durch - der Name
  // muesste sonst per Zeichenkettensuche aus dem Satz gefischt werden.
  const speichereSession = async (b) => {
    const r = await fetch("/api/doku/sessions", { method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
      body: JSON.stringify(b) });
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok, status: r.status, data };
  };
  const saveSession = useCallback(async (patch) => {
    if (!sessIdRef.current) return;
    if (konfliktRef.current) return; // erst entscheiden, dann wieder speichern
    const body = { id: sessIdRef.current, ...patch };
    if (basisStandRef.current) body.basisStand = basisStandRef.current;
    // Zu gross (413)? Erst ohne Diagramme, dann auch ohne Aenderungsverlauf - lieber der Text
    // ohne Markierungen als gar kein gesicherter Stand.
    const varianten = [
      body,
      { ...body, svgs: undefined, bpmnSvgs: undefined },
      { ...body, svgs: undefined, bpmnSvgs: undefined, revisionen: undefined },
    ];
    for (const b of varianten) {
      let r;
      try { r = await speichereSession(b); } catch { continue; } // Netzfehler: naechste Variante
      if (r.ok) { if (r.data?.updatedAt) basisStandRef.current = r.data.updatedAt; return; }
      // Ein 409 ist eine Aussage ueber fremde Aenderungen, kein Groessenproblem. Er wird sofort
      // gemeldet und NIE verkleinert wiederholt - sonst verwandelt ihn die 413-Wiederholung in
      // einen stillen Fehlschlag und der Nutzer haelt seinen Stand faelschlich fuer gesichert.
      if (r.status === 409) {
        konfliktRef.current = true;
        setKonflikt({
          werName: r.data?.werName || r.data?.von || tRef.current("konflikt.unbekanntePerson"),
          farbe: r.data?.farbe ?? null,
          // Der Ton folgt dem, was auf dem Spiel steht: eigener Text, der noch nicht auf dem
          // Server liegt. Genau dieser Speichervorgang ist gescheitert, also ist alles, was er
          // tragen sollte, ungesichert.
          riskant: selfEditRef.current || dirtySectionsRef.current.size > 0 || typeof patch?.docContent === "string",
          gesichert: false, frage: false,
        });
        return;
      }
    }
    /* alle Varianten gescheitert: Speichern ist Komfort, nie blockierend */
  }, []);

  // Einladungslink geoeffnet: erst pruefen, was mit ihr ist, dann fragen. Angenommen wird nur
  // auf Klick - ein Link, der beim blossen Oeffnen Zugriff herstellt, nimmt der Person die Wahl.
  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get("einladung");
    if (!token) return;
    api(`/api/doku/einladung/${encodeURIComponent(token)}`)
      .then(d => setEinladungAnnahme({ ...d, token }))
      .catch(e => setEinladungAnnahme({ token, zustand: "fehler", error: e?.message || tRef.current("meldung.einladungUngueltig") }));
  }, []);

  useEffect(() => { // Liste beim Betreten laden
    api("/api/doku/sessions").then(d => setDokuSessions(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  // Eigener Benutzer: nur fuer den Urheberstempel frisch erfasster Aenderungen. Ohne ihn wuerde
  // eine eigene Aenderung bis zum naechsten Laden in der Farbe des Besitzers erscheinen - eine
  // Falschaussage. Verbindlich stempelt weiterhin der Server.
  useEffect(() => { api("/api/auth/me").then(setIch).catch(() => {}); }, []);

  // Nach Ende der Generierung (oder Abbruch) den vollen Stand sichern
  const prevLoadingRef = useRef(false);
  useEffect(() => {
    const was = prevLoadingRef.current; prevLoadingRef.current = loading;
    if (!was || loading) return; // nur auf true->false reagieren
    if (!sessIdRef.current || (!docContent && !svgs.length)) return;
    saveSession({ docContent, svgs, bpmnSvgs, validation, genStats, editStats,
      status: genEnd === "truncated" ? "truncated" : genEnd === "cancelled" ? "cancelled" : "complete" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  // Edits/Ergaenzungen/Fortsetzungen debounced nachspeichern. Der Aenderungsverlauf gehoert
  // dazu: er ist der Grund, warum die Markierungen ein Neuladen ueberstehen.
  useEffect(() => {
    if (loading || !sessIdRef.current || !docContent) return;
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveSession({ docContent, svgs, bpmnSvgs, validation, genStats, editStats, revisionen: schlankeRevisionen(revisionen),
        status: genEnd === "truncated" ? "truncated" : genEnd === "cancelled" ? "cancelled" : "complete" });
    }, 2500);
    return () => clearTimeout(saveTimerRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docContent, svgs, bpmnSvgs, genEnd, revisionen]);

  const restoreSession = useCallback(async (id) => {
    try {
      const sSess = await api(`/api/doku/sessions/${id}`);
      sessIdRef.current = sSess.id;
      setSessId(sSess.id);
      // Stand des Servers als Basis merken: von hier an erkennt das Speichern fremde Aenderungen.
      basisStandRef.current = sSess.updatedAt || null;
      konfliktRef.current = false; setKonflikt(null);
      setMitwirkende(Array.isArray(sSess.mitwirkende) ? sSess.mitwirkende : []);
      setMeineRolle(sSess.meineRolle || "besitzer");
      setTeilenOffen(false); setBestaetigung(null); setTeilenMeldung(null);
      if (sSess.style) setStyle(sSess.style);
      // Auch beim Oeffnen reparieren: Dokumente, die vor diesem Fix beschaedigt wurden, haetten
      // sonst dauerhaft verschmolzene Kapitel und verrutschte Aenderungsmarkierungen.
      setDocContent(repariereMarkdownSyntax(sSess.docContent || ""));
      setSvgs(Array.isArray(sSess.svgs) ? sSess.svgs : []);
      // Bevorzugt frisch aus den XMLs rendern: so bekommen auch aeltere Sessions die
      // aktuelle Einheits-Bildsprache. Gespeicherte SVG-Strings sind nur der Fallback.
      let bp = [];
      if (Array.isArray(sSess.bpmnXmls) && sSess.bpmnXmls.length) {
        bp = sSess.bpmnXmls.map(x => { try { return { name: x.name || "", svg: renderIflowSvgString(x.xml) }; } catch { return null; } }).filter(Boolean);
      }
      if (!bp.length && Array.isArray(sSess.bpmnSvgs)) bp = sSess.bpmnSvgs;
      setBpmnSvgs(bp);
      setValidation(sSess.validation || null);
      setDocFacts(sSess.facts || "");
      setGenStats(sSess.genStats || null);
      setEditStats(sSess.editStats || null);
      // Aenderungsverlauf mitbringen: sonst ist nach dem Neuladen oder beim Oeffnen einer
      // aelteren Dokumentation jede Markierung weg, obwohl der korrigierte Text dasteht.
      setRevisionen(Array.isArray(sSess.revisionen) ? sSess.revisionen : []);
      setRevOffen(null); setRevsOffen(false); setChgPop(null);
      setGenEnd(sSess.status === "truncated" ? "truncated" : sSess.status === "cancelled" ? "cancelled" : "complete");
      setTokenLimit(null); setError(null); setSectOpen({});
      setRestoredMeta({ flowNames: sSess.flowNames || [] });
      setDocDomKey(k => k + 1);
      setTimeout(() => { const el = document.getElementById("dk-sec4r"); if (el) el.scrollIntoView({ behavior: "smooth", block: "start" }); }, 120);
    } catch (e) { setError(tRef.current("meldung.wiederherstellen", { grund: e.message || e })); }
  }, []);

  const deleteSession = useCallback(async (id) => {
    // Ein Mitwirkender darf nicht loeschen (403). Das still zu schlucken und die Zeile trotzdem
    // aus der Liste zu nehmen waere eine Luege - die Dokumentation waere beim naechsten Laden
    // wieder da. Also: Grund nennen, Zeile stehen lassen.
    try { await api(`/api/doku/sessions/${id}`, { method: "DELETE" }); }
    catch (e) { setError(e?.message || tRef.current("meldung.nichtEntfernt")); return; }
    setDokuSessions(prev => prev.filter(x => x.id !== id));
  }, []);

  // === Teilen: Zugriff vergeben, aendern, entziehen (nur der Besitzer) ===
  // Weitergabe von Zugriff ist die einzige Handlung, die den Kreis der Mitwissenden dauerhaft
  // erweitert - sie bleibt an einer Person.
  const teilenOeffnen = useCallback(async () => {
    if (!sessIdRef.current) return;
    setTeilenOffen(true); setTeilenRolle("lesen"); setNeuerLink(null);
    setBestaetigung(null); setTeilenMeldung(null);
    try {
      const d = await api(`/api/doku/sessions/${sessIdRef.current}/freigaben`);
      setMitwirkende(Array.isArray(d.mitwirkende) ? d.mitwirkende : []);
      setEinladungen(Array.isArray(d.einladungen) ? d.einladungen : []);
    } catch (e) { setTeilenMeldung({ art: "fehler", text: e?.message || tRef.current("meldung.freigabenLaden") }); }
  }, []);

  const uebernehmeFreigaben = useCallback((d, text) => {
    if (Array.isArray(d?.mitwirkende)) setMitwirkende(d.mitwirkende);
    if (Array.isArray(d?.einladungen)) setEinladungen(d.einladungen);
    setTeilenMeldung({ art: "ok", text });
  }, []);

  // Ein Einladungslink statt einer Personenauswahl: wer sonst im System angemeldet ist, geht
  // den Teilenden nichts an. Der Link gilt 24 Stunden und fuer genau eine Person.
  const linkErzeugen = useCallback(async () => {
    if (!sessIdRef.current || teilenBusy) return;
    setTeilenBusy(true); setTeilenMeldung(null); setNeuerLink(null);
    try {
      const d = await api(`/api/doku/sessions/${sessIdRef.current}/einladungen`, { method: "POST", body: JSON.stringify({ rolle: teilenRolle }) });
      // Der Ursprung kommt aus dem Browser: nur er weiss sicher, unter welchem Namen diese
      // Umgebung gerade aufgerufen wird.
      setNeuerLink({ ...d, url: d.pfad ? window.location.origin + d.pfad : d.url });
      const f = await api(`/api/doku/sessions/${sessIdRef.current}/freigaben`);
      if (Array.isArray(f.einladungen)) setEinladungen(f.einladungen);
    } catch (e) { setTeilenMeldung({ art: "fehler", text: e?.message || tRef.current("meldung.linkNichtErstellt") }); }
    finally { setTeilenBusy(false); }
  }, [teilenRolle, teilenBusy]);

  const linkKopieren = useCallback(async (url) => {
    try { await navigator.clipboard.writeText(url); setTeilenMeldung({ art: "ok", text: tRef.current("teilen.linkKopiert") }); }
    catch { setTeilenMeldung({ art: "fehler", text: tRef.current("teilen.linkNichtKopiert") }); }
  }, []);

  const einladungAnnehmen = useCallback(async () => {
    if (!einladungAnnahme?.token || annahmeBusy) return;
    setAnnahmeBusy(true);
    try {
      const d = await api(`/api/doku/einladung/${encodeURIComponent(einladungAnnahme.token)}`, { method: "POST" });
      // Adresszeile bereinigen: der verbrauchte Link soll beim Neuladen nicht erneut greifen.
      window.history.replaceState({}, "", "/doku");
      setEinladungAnnahme(null);
      const l = await api("/api/doku/sessions").catch(() => []);
      setDokuSessions(Array.isArray(l) ? l : []);
      if (d?.sessionId) await restoreSession(d.sessionId);
    } catch (e) { setEinladungAnnahme(a => ({ ...a, zustand: "fehler", error: e?.message || tRef.current("meldung.einladungNichtAngenommen") })); }
    finally { setAnnahmeBusy(false); }
  }, [einladungAnnahme, annahmeBusy, restoreSession]);

  const einladungSchliessen = useCallback(() => {
    window.history.replaceState({}, "", "/doku");
    setEinladungAnnahme(null);
  }, []);

  const linkZurueckziehen = useCallback(async (token) => {
    if (!sessIdRef.current || teilenBusy) return;
    setTeilenBusy(true); setTeilenMeldung(null);
    try {
      const d = await api(`/api/doku/sessions/${sessIdRef.current}/einladungen/${encodeURIComponent(token)}`, { method: "DELETE" });
      uebernehmeFreigaben(d, tRef.current("teilen.einladungZurueckgezogen"));
      setNeuerLink(n => (n && n.token === token ? null : n));
    } catch (e) { setTeilenMeldung({ art: "fehler", text: e?.message || tRef.current("meldung.einladungNichtZurueckgezogen") }); }
    finally { setTeilenBusy(false); }
  }, [teilenBusy, uebernehmeFreigaben]);

  const setzeRolle = useCallback(async (m, rolle) => {
    if (!sessIdRef.current || teilenBusy) return;
    setTeilenBusy(true); setTeilenMeldung(null);
    try {
      const d = await api(`/api/doku/sessions/${sessIdRef.current}/freigaben`, { method: "POST", body: JSON.stringify({ userId: m.userId, rolle }) });
      uebernehmeFreigaben(d, tRef.current("teilen.rolleGeaendert", {
        name: m.name,
        rolle: rolle === "bearbeiten" ? tRef.current("teilen.rolleGeaendertBearbeiten") : tRef.current("teilen.rolleGeaendertLesen"),
      }));
      setBestaetigung(null);
    } catch (e) { setTeilenMeldung({ art: "fehler", text: e?.message || tRef.current("meldung.rolleNichtGeaendert") }); }
    finally { setTeilenBusy(false); }
  }, [teilenBusy, uebernehmeFreigaben]);

  // Nur die Richtung, die Rechte VERGIBT, kostet einen zweiten Blick. Herunterstufen ist
  // harmlos und greift sofort.
  const rolleAendern = useCallback((m, rolle) => {
    if (rolle === m.rolle) return;
    if (rolle === "bearbeiten") { setBestaetigung({ userId: m.userId, art: "hoch" }); return; }
    setzeRolle(m, "lesen");
  }, [setzeRolle]);

  const entziehen = useCallback(async (m) => {
    if (!sessIdRef.current || teilenBusy) return;
    setTeilenBusy(true); setTeilenMeldung(null);
    try {
      const d = await api(`/api/doku/sessions/${sessIdRef.current}/freigaben/${encodeURIComponent(m.userId)}`, { method: "DELETE" });
      uebernehmeFreigaben(d, tRef.current("teilen.zugriffEntzogen", { name: m.name }));
      setBestaetigung(null);
    } catch (e) { setTeilenMeldung({ art: "fehler", text: e?.message || tRef.current("meldung.zugriffNichtEntzogen") }); }
    finally { setTeilenBusy(false); }
  }, [teilenBusy, uebernehmeFreigaben]);

  // Neu laden holt den fremden Stand. Das ist die einzige Aufloesung: ein Erzwingen des eigenen
  // Standes wuerde ueber fremden Text hinweg entscheiden. Der eigene Text wird stattdessen als
  // Markdown gesichert (downloadMd) - das braucht kein neues Backend.
  const konfliktNeuLaden = useCallback(() => {
    const id = sessIdRef.current;
    konfliktRef.current = false; setKonflikt(null);
    // Der Nutzer hat den Verlust ausdruecklich angenommen; die Merkliste der selbst geaenderten
    // Abschnitte zeigt sonst auf Abschnitte einer Fassung, die es nicht mehr gibt.
    dirtySectionsRef.current = new Set();
    if (id) restoreSession(id);
  }, [restoreSession]);

  // Solange ungesicherte Eingaben im Konflikt stehen, fragt auch das Neuladen ueber den Browser
  // nach. Ohne das umgeht ein Tastendruck (F5) alle vier Sperren des Streifens.
  useEffect(() => {
    if (!konflikt?.riskant || konflikt.gesichert) return;
    const onLeave = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onLeave);
    return () => window.removeEventListener("beforeunload", onLeave);
  }, [konflikt]);

  // Teilen-Dialog: Escape schliesst, der Fokus bleibt im Dialog (Fokusfalle).
  const teilenRef = useRef(null);
  useEffect(() => {
    if (!teilenOffen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setTeilenOffen(false); return; }
      if (e.key !== "Tab") return;
      const el = teilenRef.current; if (!el) return;
      const f = [...el.querySelectorAll('button,[href],input,select,textarea,[tabindex]:not([tabindex="-1"])')].filter(x => !x.disabled && x.offsetParent !== null);
      if (!f.length) return;
      const erste = f[0], letzte = f[f.length - 1];
      if (e.shiftKey && document.activeElement === erste) { e.preventDefault(); letzte.focus(); }
      else if (!e.shiftKey && document.activeElement === letzte) { e.preventDefault(); erste.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [teilenOffen]);

  // Verbindungssuche ist jetzt OPTIONAL: sucht Abhaengigkeiten fuer alle manuell
  // gewaehlten Tenant-iFlows (sequenziell - searchDependencies bricht Parallel-Laeufe ab).
  const searchAllConnections = useCallback(async () => {
    const roots = flowsRef.current.filter(fl => fl.source === "tenant" && !fl.auto && fl.flowId);
    for (const fl of roots) {
      // eslint-disable-next-line no-await-in-loop
      await searchDependencies(fl.id, fl.flowId, fl.iid, fl.packageId);
    }
  }, [searchDependencies]);

  const cancel = useCallback(() => { if (abortRef.current) abortRef.current.abort(); }, []);

  const userScrolledUp = useRef(false);
  useEffect(() => {
    const el = contentRef.current; if (!el) return;
    const onScroll = () => { userScrolledUp.current = el.scrollTop + el.clientHeight < el.scrollHeight - 80; };
    el.addEventListener("scroll", onScroll);
    return () => el.removeEventListener("scroll", onScroll);
  }, [docContent, bpmnSvgs]);
  useEffect(() => {
    if (contentRef.current && loading && !userScrolledUp.current) contentRef.current.scrollTop = contentRef.current.scrollHeight;
  }, [docContent, loading]);

  // === Exports ===
  // Titel/Dateiname auf den/die Haupt-iFlow(s) stuetzen (nicht auto-ergaenzte Pipeline-/Abhaengigkeits-iFlows).
  const titleFlows = readyFlows.some(fl => !fl.auto) ? readyFlows.filter(fl => !fl.auto) : readyFlows;
  const flowName = processName.trim() || (titleFlows.length === 1 ? (titleFlows[0].zipInfo?.flowName || titleFlows[0].file?.name?.replace(/\.zip$/i, "")) : titleFlows.map(fl => fl.zipInfo?.flowName || "").filter(Boolean).join(" + ")) || t("dokument.prozessFallback");
  const styleLabel = stilTitel(style);

  // Aktives Nutzer-Feedback zur generierten Doku (fire-and-forget; wird serverseitig klassifiziert und gemerkt).
  const submitFeedback = useCallback(async (ratingArg) => {
    const r = ratingArg || fbRating;
    if (!r || fbSent) return;
    setFbSent(true);
    try {
      await fetch("/api/doku/feedback", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ rating: r, text: fbText.trim(), style, projectId: tnInstId || "" }) });
    } catch { /* Dank wird trotzdem gezeigt */ }
  }, [fbRating, fbText, fbSent, style, tnInstId]);

  // Klick-Delegation im gerenderten HTML: oeffnet das Grafik-Anpassen-Popup (Button kommt aus figFor).
  const handleContentClick = useCallback((e) => {
    // Info-Symbol an einer geaenderten Stelle: Vorher/Nachher aufklappen. Bewusst im
    // BESTEHENDEN Handler, der schon am Container haengt - eine zweite Deklaration desselben
    // Namens waere ein Syntaxfehler gewesen.
    const chg = e.target?.closest?.(".dk-chg-info");
    if (chg) {
      e.preventDefault(); e.stopPropagation();
      const si = Number(chg.getAttribute("data-chg"));
      // Am SYMBOL ausrichten, nicht am Mauszeiger: per Tastatur ausgeloest traegt das Ereignis
      // keine Koordinaten (0/0), das Popup klebte dann in der Bildschirmecke.
      const r = chg.getBoundingClientRect();
      const BREITE = 470, HOEHE = 340;
      setChgPop(p => (p && p.idx === si ? null : {
        idx: si, pos: 0,
        screenX: Math.min(Math.max(r.left - BREITE + 44, 12), Math.max(12, window.innerWidth - BREITE - 12)),
        screenY: Math.min(r.bottom + 8, Math.max(12, window.innerHeight - HOEHE)),
      }));
      return;
    }
    const btn = e.target.closest && e.target.closest(".doku-fig-edit");
    if (!btn || loading) return;
    e.preventDefault(); e.stopPropagation();
    setFigPopup({
      kind: btn.getAttribute("data-fig-kind") || "landscape",
      id: btn.getAttribute("data-fig-id") || "",
      cap: btn.getAttribute("data-fig-cap") || "",
      screenX: Math.min(e.clientX, window.innerWidth - 400),
      screenY: Math.min(e.clientY + 8, window.innerHeight - 220),
      instruction: "", msg: "",
    });
  }, [loading]);

  // Grafik anpassen: Landschafts-Diagramme werden per LLM angepasst + neu gerendert; BPMN wird nur gelernt.
  const submitFigEdit = useCallback(async () => {
    if (!figPopup?.instruction?.trim() || figLoading) return;
    setFigLoading(true);
    try {
      const entry = figPopup.kind === "landscape" ? svgs.find(s => (s.id || "") === figPopup.id) : null;
      const res = await fetch("/api/doku/edit-diagram", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ diagramId: figPopup.id, kind: figPopup.kind, title: (entry && entry.title) || figPopup.cap, spec: (entry && entry.spec) || null, instruction: figPopup.instruction.trim(), docExcerpt: (docContent || "").slice(0, 4000), projectId: tnInstId || "" }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler");
      zaehleVerbrauch(data.verbrauch);
      if (data.svg) {
        setSvgs(prev => prev.map(s => (s.id || "") === figPopup.id ? { ...s, svg: data.svg, spec: data.spec || s.spec, title: data.title || s.title } : s));
        setFigPopup(null);
      } else {
        const msg = data.reason === "bpmn"
          ? tRef.current("grafik.bpmnNichtAenderbar")
          : data.reason === "no-spec"
            ? tRef.current("grafik.ohneBearbeitungsdaten")
            : tRef.current("grafik.nurDarstellung", { zusatz: data.note ? `: ${data.note}` : "." });
        setFigPopup(p => p ? { ...p, msg } : p);
      }
    } catch (e2) { setFigPopup(p => p ? { ...p, msg: tRef.current("meldung.fehlerMit", { grund: e2.message }) } : p); }
    finally { setFigLoading(false); }
  }, [figPopup, figLoading, svgs, docContent, tnInstId]);

  // Zusaetzliche Grafik: liest die Doku als Kontext, erzeugt Diagramm + bettet Marker an passender Stelle ein.
  const submitAddFig = useCallback(async () => {
    if (!addFigText.trim() || addFigLoading) return;
    setAddFigLoading(true); setAddFigMsg("");
    try {
      const res = await fetch("/api/doku/add-diagram", { method: "POST", headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body: JSON.stringify({ instruction: addFigText.trim(), docMarkdown: docContent || "", existingIds: svgs.map(s => s.id).filter(Boolean), existing: svgs.map(s => ({ id: s.id, title: s.title, center: s.spec && s.spec.center && s.spec.center.label })), projectId: tnInstId || "" }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Fehler");
      zaehleVerbrauch(data.verbrauch);
      setSvgs(prev => [...prev, { id: data.id, title: data.title, svg: data.svg, spec: data.spec }]);
      const markerLine = `[[DIAGRAMM:${data.id}|${data.caption || data.title || ""}]]`;
      setDocContent(md => {
        const lines = md.split("\n");
        const anchor = String(data.anchorHeading || "").trim();
        let idx = anchor ? lines.findIndex(l => l.trim() === anchor) : -1;
        if (idx < 0 && anchor) { const t = anchor.replace(/^#+\s*/, "").toLowerCase(); idx = lines.findIndex(l => /^#{1,4}\s/.test(l) && l.replace(/^#+\s*/, "").toLowerCase().includes(t)); }
        if (idx < 0) return md + `\n\n${markerLine}\n`;
        lines.splice(idx + 1, 0, "", markerLine);
        return lines.join("\n");
      });
      setAddFigOpen(false); setAddFigText("");
    } catch (e2) { setAddFigMsg(tRef.current("meldung.fehlerMit", { grund: e2.message })); }
    finally { setAddFigLoading(false); }
  }, [addFigText, addFigLoading, docContent, svgs, tnInstId]);
  const nowStr = new Date().toLocaleDateString(gebiet, { day: "2-digit", month: "2-digit", year: "numeric" });

  const copyToClipboard = useCallback(() => { navigator.clipboard.writeText(`*${tRef.current("dokument.kiHinweis")}*` + "\n\n" + docContent); }, [docContent]);

  const downloadMd = useCallback(() => {
    const blob = new Blob([`*${tRef.current("dokument.kiHinweis")}*` + "\n\n" + docContent], { type: "text/markdown" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${flowName}_${style}.md`; a.click(); URL.revokeObjectURL(a.href);
  }, [docContent, flowName, style]);

  const downloadDocx = useCallback(async () => {
    try {
      const blob = await markdownToDocx(`*${tRef.current("dokument.kiHinweis")}*` + "\n\n" + docContent, { flowName, styleLabel, date: nowStr });
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `${flowName}_${style}.docx`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { setError(tRef.current("meldung.wordExport", { grund: e.message })); }
  }, [docContent, flowName, styleLabel, nowStr, style]);

  const downloadPdf = useCallback(() => {
    const html = buildPrintHtml();
    if (!html) return;
    const w = window.open("", "_blank");
    if (!w) return;
    // Kein Inline-Script mehr im Druckfenster: unter der strengen CSP (script-src 'self') wuerde
    // ein <script> im about:blank-Kind blockiert. Der Druck wird stattdessen vom Opener ausgeloest.
    // ⛔ Hier MUSS interpoliert-escaped werden. i18n laeuft global mit
    // escapeValue:false, weil React seine Ausgabe selbst schuetzt - dieser eine
    // Weg geht aber per document.write an React vorbei, und flowName kommt aus
    // dem Tenant bzw. aus einem hochgeladenen Dateinamen. Ein iFlow namens
    // "</title><img src=x onerror=...>" braeche sonst aus dem Titel aus, im
    // selben Ursprung wie die Anwendung.
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>${tRef.current("dokument.druckTitel", { name: flowName, interpolation: { escapeValue: true } })}</title><style>@page{size:A4;margin:20mm 18mm 25mm 18mm}body{margin:0;padding:24px;font-family:Source Sans 3,Segoe UI,sans-serif}h2{page-break-before:auto}figure,table,pre{page-break-inside:avoid}svg{max-width:100%;height:auto}</style></head><body>${html}</body></html>`);
    w.document.close();
    setTimeout(() => { try { w.focus(); w.print(); } catch { /* Nutzer kann manuell drucken */ } }, 400);
  }, [docContent, svgs, bpmnSvgs, isMulti, style]);

  // === Feature 2: Append ===
  // Eine Aenderung als kleine Version festhalten. Bewusst nur der Abschnittsumfang, nicht das
  // ganze Dokument je Version: der Session-Speicher ist begrenzt (2,5 MB) und wirft im
  // Ueberlauf Diagramme weg.
  const REV_MAX = 25;
  const erfasseRevision = useCallback((anweisung, aktion, vorher, nachher) => {
    if (!vorher || !nachher || vorher === nachher) return;
    const abschnitte = geaenderteAbschnitte(vorher, nachher);
    // stellen traegt je angefasstes Kapitel den Anker plus Text davor/danach. Daran haengt die
    // dauerhafte Markierung: sie ueberlebt spaetere Aenderungen und das Umnummerieren, waehrend
    // ein Abschnittsindex danach auf ein fremdes Kapitel zeigen wuerde.
    const stellen = stellenFuerRevision(vorher, nachher);
    const umfang = diffUmfang(vorher, nachher);
    setRevisionen(alt => [...alt, {
      id: `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
      ts: Date.now(), anweisung: String(anweisung || "").slice(0, 400), aktion,
      // Urheber vorlaeufig selbst stempeln, damit die eigene Aenderung sofort die eigene Farbe
      // traegt. Verbindlich ist der Serverstempel; er ueberschreibt das beim naechsten Laden.
      ...(ich?.id ? { wer: ich.id, werName: ich.displayName || ich.username || "" } : {}),
      vorher, nachher, abschnitte, stellen, umfang,
    }].slice(-REV_MAX));
    setRevOffen(null);
  }, [ich]);

  // Zuruecknehmen ist bewusst nur fuer die JUENGSTE Version moeglich. Eine aeltere
  // zurueckzunehmen wuerde bedeuten, spaetere Aenderungen darauf neu anzuwenden - das koennen
  // wir nicht zusagen, also sagen wir es nicht zu.
  const nimmRevisionZurueck = useCallback((idx) => {
    setRevisionen(alt => {
      if (idx !== alt.length - 1) return alt;
      const r = alt[idx];
      // Ohne Vorfassung (aus einer frueheren Sitzung wiederhergestellt) wird NICHT geraten -
      // ein leeres setDocContent haette das Dokument geloescht.
      if (!r.vorher) return alt;
      setDocContent(r.vorher);
      setRevOffen(null);
      return alt.slice(0, idx);
    });
  }, []);

  const handleAppend = useCallback(async () => {
    if (!appendInput.trim() || appendLoading) return;
    const instr = appendInput;
    setAppendLoading(true); setError(null);
    const backup = docContent;
    try {
      // Die Absicht wird BESTIMMT, nicht geraten. Vorher entschied ein Regex zwischen
      // "anhaengen" und "alles neu" - eine Korrekturabsicht landete damit zwangslaeufig im
      // Anhaengen-Pfad, dessen Prompt ein neues Kapitel verlangt. Genau das war der gemeldete
      // Fehler: die falsche Zeile blieb stehen, darunter kam eine zweite Tabelle.
      const iv = await mitFrist("/api/doku/intent", { instruction: instr, hasSelection: false }, 30000);
      const absicht = iv.ok ? (await iv.json()).absicht : "korrektur";

      if (absicht === "neuaufsetzen") {
        if (docContent && !window.confirm(tRef.current("meldung.neuErzeugenFrage"))) { setAppendLoading(false); return; }
        setAppendInput("");
        if (readyFlows.length) { setAppendLoading(false); generate(instr); return; }
        let got = "";
        const res = await mitFrist("/api/doku/edit", { mode: "append", forceGlobal: true, instruction: instr, fullDocMarkdown: docContent, style });
        if (!res.ok) throw new Error((await res.json()).error || tRef.current("meldung.fehler"));
        await sseReader(res, ev => { if (ev.replace) setDocContent(""); if (ev.text) { got += ev.text; setDocContent(prev => prev + ev.text); } if (ev.verbrauch) zaehleVerbrauch(ev.verbrauch); if (ev.error) { setError(ev.error); setActivePhase(0); } });
        // Der Strom kommt tokenweise und laeuft an der serverseitigen Reparatur vorbei - eine
        // Ueberschrift ohne Leerzeichen wuerde sonst zwei Kapitel verschmelzen lassen.
        if (!got.trim()) setDocContent(backup);
        else { const rein = repariereMarkdownSyntax(got); setDocContent(rein); erfasseRevision(instr, "neuaufsetzen", backup, rein); }
        return;
      }

      if (absicht === "korrektur" || absicht === "loeschen") {
        // AN DER STELLE aendern statt anzuhaengen.
        const res = await mitFrist("/api/doku/edit", { mode: absicht === "loeschen" ? "delete" : "correct", instruction: instr, fullDocMarkdown: docContent, style, projectId: tnInstId || "", markers: (svgs || []).map(x => x.id), facts: docFacts });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || tRef.current("meldung.fehler"));
        zaehleVerbrauch(data.verbrauch);
        if (data.unveraendert) { setError(data.hinweis || tRef.current("meldung.keineAenderung")); return; }
        if (data.newDocMarkdown) {
          setDocContent(data.newDocMarkdown);
          erfasseRevision(instr, data.aktion || absicht, backup, data.newDocMarkdown);
          setAppendInput("");
        }
        return;
      }

      if (absicht === "verschieben") {
        // Fiel bisher ins Anhaengen, und der Anhaengen-Prompt liess das Modell eine
        // Entschuldigung INS DOKUMENT schreiben ("Ich kann die bestehende Dokumentation nicht
        // direkt umstrukturieren..."). Im Test aufgetreten.
        const res = await mitFrist("/api/doku/edit", { mode: "move", instruction: instr, fullDocMarkdown: docContent, style, projectId: tnInstId || "" });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || tRef.current("meldung.fehler"));
        zaehleVerbrauch(data.verbrauch);
        if (data.newDocMarkdown && data.newDocMarkdown.trim()) {
          setDocContent(data.newDocMarkdown);
          erfasseRevision(instr, "verschieben", backup, data.newDocMarkdown);
          setAppendInput("");
        } else setError(tRef.current("meldung.verschiebenOhneErgebnis"));
        return;
      }

      // Ergaenzung: echtes neues Kapitel, das ist hier der richtige Weg.
      let got = "";
      const res = await mitFrist("/api/doku/edit", { mode: "append", instruction: instr, fullDocMarkdown: docContent, style });
      if (!res.ok) throw new Error((await res.json()).error || tRef.current("meldung.fehler"));
      await sseReader(res, ev => { if (ev.replace) setDocContent(""); if (ev.text) { got += ev.text; setDocContent(prev => prev + ev.text); } if (ev.verbrauch) zaehleVerbrauch(ev.verbrauch); if (ev.error) { setError(ev.error); setActivePhase(0); } });
      if (got.trim()) {
        const rein = repariereMarkdownSyntax(backup + got);
        setDocContent(rein);
        erfasseRevision(instr, "ergaenzung", backup, rein);
      }
      setAppendInput("");
    } catch (e) {
      setError(e?.name === "AbortError"
        ? tRef.current("meldung.anpassungZeit")
        : (e?.message || tRef.current("meldung.anpassungFehlgeschlagen")));
      setDocContent(backup);
    }
    finally { setAppendLoading(false); }
  }, [appendInput, appendLoading, docContent, style, generate, readyFlows, tnInstId, svgs, docFacts, erfasseRevision]);

  // === Feature 4: Inline Edit ===
  const handleContentMouseUp = useCallback(() => {
    if (loading || editLoading) return;
    if (!darfSchreiben) return; // nur zum Lesen freigegeben - Markieren oeffnet kein KI-Popup
    if (selfEdit) return; // im Selbst-schreiben-Modus keine KI-Popups - Markieren gehoert dem Editor
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || !sel.toString().trim()) { return; }
    const range = sel.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const containerRect = contentRef.current?.getBoundingClientRect();
    if (!containerRect) return;
    let sectionEl = range.startContainer;
    while (sectionEl && sectionEl !== contentRef.current) {
      if (sectionEl.dataset?.section !== undefined) break;
      sectionEl = sectionEl.parentElement;
    }
    const sectionIndex = sectionEl?.dataset?.section !== undefined ? parseInt(sectionEl.dataset.section) : -1;
    const popupY = Math.min(rect.bottom + 8, window.innerHeight - 220);
    const popupX = Math.max(8, Math.min(rect.left, window.innerWidth - 380));
    setEditPopup({
      screenY: popupY,
      screenX: popupX,
      selectedText: sel.toString().trim(),
      sectionIndex,
      instruction: "",
    });
    sel.removeAllRanges();
  }, [loading, editLoading, selfEdit, darfSchreiben]);

  // === Selbst schreiben: Aenderungen section-genau uebernehmen / verwerfen ===
  const startSelfEdit = useCallback(() => { dirtySectionsRef.current = new Set(); setSelfEdit(true); }, []);
  const markDirty = useCallback((e) => {
    // Das input-Ereignis feuert bei contentEditable am EDITING-HOST, nicht am bearbeiteten Kind
    // (im Browser gegengeprueft: target=DIV ohne data-section, closest findet nichts). Damit
    // blieb dirtySectionsRef LEER und "Aenderungen uebernehmen" schrieb nichts zurueck.
    // Die betroffene Sektion kommt daher aus der Auswahl, nicht aus dem Ereignisziel.
    let sec = e.target?.closest?.("[data-section]") || null;
    if (!sec) {
      const knoten = window.getSelection()?.anchorNode;
      const el = knoten ? (knoten.nodeType === 1 ? knoten : knoten.parentElement) : null;
      sec = el?.closest?.("[data-section]") || null;
    }
    if (sec) dirtySectionsRef.current.add(parseInt(sec.dataset.section));
  }, []);
  const discardSelfEdit = useCallback(() => {
    dirtySectionsRef.current = new Set();
    setSelfEdit(false);
    setDocDomKey(k => k + 1); // DOM-Mutationen des Users wegwerfen -> frisch aus docContent rendern
  }, []);
  // Esc verlaesst den Selbst-schreiben-Modus (Rueckfrage nur bei ungespeicherten Aenderungen)
  useEffect(() => {
    if (!selfEdit) return;
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      if (dirtySectionsRef.current.size && !window.confirm(tRef.current("meldung.eigeneAenderungenVerwerfen"))) return;
      discardSelfEdit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selfEdit, discardSelfEdit]);
  const commitSelfEdit = useCallback(() => {
    try {
      const dirty = dirtySectionsRef.current;
      if (dirty.size && contentRef.current) {
        const sections = docContent.split(/(?=^## )/gm);
        const domSections = contentRef.current.querySelectorAll("[data-section]");
        const next = sections.map((orig, i) => {
          if (!dirty.has(i)) return orig;
          const el = [...domSections].find(d => parseInt(d.dataset.section) === i);
          return el ? domSectionToMarkdown(el) : orig;
        });
        setDocContent(next.join(""));
      }
    } catch (e2) {
      setError(tRef.current("meldung.uebernahmeFehlgeschlagen", { grund: e2.message || e2 }));
    }
    dirtySectionsRef.current = new Set();
    setSelfEdit(false);
    setDocDomKey(k => k + 1);
  }, [docContent]);

  const submitEdit = useCallback(async () => {
    if (!editPopup?.instruction?.trim() || editLoading) return;
    setEditLoading(true); setError(null);
    const sections = docContent.split(/(?=^## )/gm);
    // Verschiebe-Anweisung? Dann darf die Passage die eigene Section verlassen -> die GANZE Doku
    // wird bearbeitet und komplett ersetzt (sektionslokale Umschreibung kann "ganz an den Anfang"
    // strukturell nicht abbilden). Sonst: bewaehrte section-lokale Umschreibung.
    // Auch hier: Absicht bestimmen statt raten. dokuIsMove war ein Regex.
    let absicht = "korrektur";
    const vorherDoc = docContent;
    let isMove = false;
    try {
      try {
        const iv = await mitFrist("/api/doku/intent", { instruction: editPopup.instruction, hasSelection: true }, 30000);
        if (iv.ok) absicht = (await iv.json()).absicht || "korrektur";
      } catch { /* Vorgabe korrektur */ }
      isMove = absicht === "verschieben";
      if (isMove) {
        const srcHeading = editPopup.sectionIndex >= 0 && editPopup.sectionIndex < sections.length
          ? (sections[editPopup.sectionIndex].match(/^#{1,4}\s+.+$/m)?.[0] || "") : "";
        const res = await mitFrist("/api/doku/edit", ({ mode: "move", selectedText: editPopup.selectedText, instruction: editPopup.instruction, sourceHeading: srcHeading, fullDocMarkdown: docContent, style, projectId: tnInstId || "" }));
        if (!res.ok) throw new Error((await res.json()).error || tRef.current("meldung.fehler"));
        const data = await res.json();
        if (data.newDocMarkdown && data.newDocMarkdown.trim()) {
          setDocContent(data.newDocMarkdown);
          erfasseRevision(editPopup.instruction, "verschieben", vorherDoc, data.newDocMarkdown);
        }
        setEditPopup(null);
      } else {
        const sectionMd = editPopup.sectionIndex >= 0 && editPopup.sectionIndex < sections.length ? sections[editPopup.sectionIndex] : "";
        const res = await mitFrist("/api/doku/edit", ({ mode: absicht === "loeschen" ? "delete" : "rewrite", sectionMarkdown: sectionMd, markers: (svgs || []).map(x => x.id), sectionIndex: editPopup.sectionIndex, selectedText: editPopup.selectedText, instruction: editPopup.instruction, fullDocMarkdown: docContent, style, projectId: tnInstId || "" }));
        if (!res.ok) throw new Error((await res.json()).error || tRef.current("meldung.fehler"));
        const data = await res.json();
        zaehleVerbrauch(data.verbrauch);
        if (data.unveraendert) { setError(data.hinweis || tRef.current("meldung.keineAenderung")); setEditPopup(null); return; }
        if (data.newDocMarkdown && data.newDocMarkdown.trim()) {
          // Der Loesch-Pfad arbeitet auf dem GANZEN Dokument und liefert es zurueck.
          setDocContent(data.newDocMarkdown);
          erfasseRevision(editPopup.instruction, data.aktion || "loeschen", vorherDoc, data.newDocMarkdown);
        } else if (data.rewrittenSection && editPopup.sectionIndex >= 0) {
          const newSections = [...sections];
          // WURZEL des gemeldeten Fehlers: Der Server liefert den Abschnitt getrimmt (kein
          // abschliessender Umbruch), zusammengesetzt wird mit join("") - der Abschnittstrenner
          // ging dabei verloren und die naechste Kapitelueberschrift klebte an der letzten
          // Textzeile ("Text korrigiert.## 3. Ablauf"). Damit ist sie keine Ueberschrift mehr:
          // weder fuer den Renderer noch fuer die Abschnittszerlegung, an der die
          // Aenderungsmarkierungen haengen.
          const roh = String(data.rewrittenSection).replace(/\s+$/, "");
          const istLetzter = editPopup.sectionIndex === sections.length - 1;
          newSections[editPopup.sectionIndex] = roh + (istLetzter ? "\n" : "\n\n");
          const neuDoc = repariereMarkdownSyntax(newSections.join(""));
          setDocContent(neuDoc);
          erfasseRevision(editPopup.instruction, absicht === "loeschen" ? "loeschen" : "korrektur", vorherDoc, neuDoc);
        } else if (editPopup.sectionIndex < 0) {
          // Vorher ein stiller Leerlauf: Anfrage ging raus, Antwort wurde verworfen, der Nutzer
          // sah weder Fehler noch Aenderung.
          setError(tRef.current("meldung.ausserhalbKapitel"));
        }
        setEditPopup(null);
      }
    } catch (e) {
      console.error("[DOKU-EDIT]", e);
      setError(e?.name === "AbortError"
        ? tRef.current("meldung.bearbeitungZeit")
        : (e?.message || tRef.current("meldung.bearbeitungFehlgeschlagen")));
    }
    finally { setEditLoading(false); }
  }, [editPopup, editLoading, docContent, style, tnInstId, svgs, erfasseRevision]);

  // Sicherheitsnetz: ohne offenes Popup darf die Bearbeitung nicht als laufend gelten. Sonst
  // blockiert ein einmal haengender Zustand jede weitere Bearbeitung bis zum Neuladen.
  useEffect(() => { if (!editPopup && editLoading) setEditLoading(false); }, [editPopup, editLoading]);

  // === Markdown Renderer (section-aware for inline editing) ===
  const renderMarkdown = (md, inlineSvgs, bpmnSvgItems, markSections) => {
    if (!md) return null;
    // Diese Funktion baut HTML-Zeichenketten und rendert sie per dangerouslySetInnerHTML.
    // Alles, was aus einem Anzeigenamen kommt, ist Fremdeingabe und geht ausschliesslich
    // ueber escAttr in ein Attribut - als HTML-Textinhalt landet hier kein Name.
    const escAttr = x => String(x || "").replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");
    const sections = md.split(/(?=^## )/gm);
    const sectionHtmls = sections.map((sec, si) => {
      let html = sec.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html
        .replace(/```svg\n([\s\S]*?)```/g, (_, c) => { const d = c.replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&amp;/g, "&"); return `<figure class="doku-svg-figure" style="margin:16px 0;text-align:center">${d}</figure>`; })
        .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:var(--iio-bg-2,#f5f5f5);padding:14px 16px;border-radius:6px;overflow-x:auto;font-size:12.5px;line-height:1.6;margin:10px 0;border:1px solid var(--iio-border,#e5e5e5)"><code>$2</code></pre>')
        .replace(/^#{5,6}\s+(.+)$/gm, '<h5 style="font-size:13px;font-weight:600;margin:14px 0 4px;color:var(--iio-text-1,#1a1a1a)">$1</h5>')
        .replace(/^####\s+(.+)$/gm, '<h4 style="font-size:13.5px;font-weight:600;margin:16px 0 5px;color:var(--iio-text-1,#1a1a1a)">$1</h4>')
        .replace(/^###\s+(.+)$/gm, '<h3 style="font-size:14.5px;font-weight:600;margin:20px 0 6px;color:var(--iio-text-1,#1a1a1a)">$1</h3>')
        .replace(/^##\s+(.+)$/gm, '<h2 style="font-size:16.5px;font-weight:700;margin:28px 0 10px;color:var(--iio-text-1,#1a1a1a);padding-bottom:6px;border-bottom:1px solid var(--iio-border,#e5e5e5)">$1</h2>')
        .replace(/^#\s+(.+)$/gm, '<h1 style="font-size:21px;font-weight:700;margin:0 0 16px;color:var(--iio-text-1,#1a1a1a)">$1</h1>')
        .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>")
        .replace(/`([^`\n]+)`/g, '<code style="background:var(--iio-bg-2,#f5f5f5);padding:1px 5px;border-radius:3px;font-size:0.9em;border:1px solid var(--iio-border,#e5e5e5)">$1</code>')
        .replace(/^- (.+)$/gm, '<li style="margin:3px 0;padding-left:4px">$1</li>')
        .replace(/^\d+\.\s+(.+)$/gm, '<li style="margin:3px 0;padding-left:4px">$1</li>');
      html = html.replace(/(\|[^\n]+\|\n\|[-| :]+\|\n(?:\|[^\n]+\|\n?)*)/g, (table) => {
        const rows = table.trim().split("\n").filter(r => !r.match(/^\|[-| :]+\|$/));
        if (rows.length < 1) return table;
        const pr = r => r.split("|").slice(1, -1).map(c => c.trim());
        const hdr = pr(rows[0]); const bd = rows.slice(1).map(pr);
        return `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:13px"><thead><tr>${hdr.map(h => `<th style="text-align:left;padding:8px 12px;border-bottom:1.5px solid var(--iio-text-3,#888);font-weight:600;color:var(--iio-text-1,#1a1a1a)">${h}</th>`).join("")}</tr></thead><tbody>${bd.map(r => `<tr>${r.map(c => `<td style="padding:7px 12px;border-bottom:1px solid var(--iio-border,#eee);color:var(--iio-text-2,#444)">${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
      });
      html = html.replace(/\n\n/g, '<div style="height:10px"></div>').replace(/\n/g, "<br/>");
      const eintraege = markSections?.get?.(si) || null;
      const istGeaendert = !!(eintraege && eintraege.length);
      const anzahl = eintraege ? eintraege.length : 0;
      // Geteiltes Dokument: der Streifen beantwortet "wer war hier drin". Er wird GETEILT statt
      // auf die juengste Person reduziert - sonst erschiene ein Kapitel, das Person A geschrieben
      // und Person B korrigiert hat, vollstaendig als das Kapitel von B. Gedeckelt bei drei
      // Baendern, danach ein neutrales Sammelband: unter 6px Bandhoehe ist keine Farbe mehr lesbar.
      const personen = [];
      if (geteilt && eintraege) for (const e of eintraege) {
        const f = farbeVon(e.rev?.wer);
        if (f != null && !personen.includes(f)) personen.push(f);
      }
      const baender = personen.slice(0, 3);
      if (personen.length > 3) baender[2] = "x";
      const balken = baender.length
        ? `<span class="dk-wer-bar doku-no-print" aria-hidden="true">${baender.map(f => `<i data-farbe="${f}"></i>`).join("")}</span>`
        : "";
      const werName = geteilt ? (eintraege?.[0]?.rev?.werName || "") : "";
      const werTxt = werName ? t("dokument.kapitelZuletztVon", { name: werName }) : "";
      const titel = anzahl > 1
        ? t("dokument.kapitelMehrere", { count: anzahl, wer: werTxt })
        : t("dokument.kapitelEine", { wer: werTxt });
      return `<div data-section="${si}"${istGeaendert ? ' class="dk-changed"' : ""}>${istGeaendert
        ? balken + `<button type="button" class="dk-chg-info doku-no-print"${personen[0] != null ? ` data-farbe="${personen[0]}"` : ""} data-chg="${si}" title="${escAttr(titel)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12" stroke-linecap="round"/><circle cx="12" cy="8" r="1.05" fill="currentColor" stroke="none"/></svg>${anzahl > 1 ? `<i class="n">${anzahl}</i>` : ""}</button>`
        : ""}${html}</div>`;
    });

    let combined = sectionHtmls.join("");
    // withMarker: nur im Fliesstext platzierte Figuren tragen den Original-Marker (data-marker),
    // damit der Selbst-schreiben-Modus sie verbatim zurueck nach Markdown serialisieren kann.
    const figFor = (withMarker) => (kind, svg, cap, ident) => {
      const inner = kind === "bpmn"
        ? `<div style="background:#fff;border:1px solid var(--iio-border,#e5e5e5);border-radius:6px;padding:12px;overflow-x:auto">${svg}</div>`
        : `<div style="background:#fff;border:1px solid var(--iio-border,#e5e5e5);border-radius:6px;padding:16px;text-align:center;overflow-x:auto">${svg}</div>`;
      const anpassen = escAttr(t("dokument.grafikAnpassen"));
      const btn = `<button type="button" class="doku-fig-edit doku-no-print" data-fig-kind="${kind}" data-fig-id="${escAttr(ident)}" data-fig-cap="${escAttr(cap)}" title="${anpassen}">${anpassen}</button>`;
      const marker = withMarker ? ` data-marker="${escAttr(`[[DIAGRAMM:${kind === "bpmn" ? "bpmn:" : ""}${ident}${cap ? "|" + cap : ""}]]`)}"` : "";
      return `<figure class="doku-svg-figure doku-fig" contenteditable="false"${marker} style="margin:20px 0;page-break-inside:avoid;position:relative"><figcaption style="font-weight:600;font-size:13px;margin-bottom:8px;color:var(--iio-text-2,#444)">${cap}</figcaption>${btn}${inner}</figure>`;
    };
    const placed = placeDiagramMarkers(combined, bpmnSvgItems || [], inlineSvgs || [], figFor(true));
    combined = placed.html;
    const leftB = (bpmnSvgItems || []).filter((_, i) => !placed.usedBpmn.has(i));
    const leftS = placed.leftInline;
    if (leftB.length || leftS.length) {
      // Appendix ist NICHT Teil des Markdowns -> vom Selbst-schreiben-Modus ausgenommen
      combined += '<div class="doku-appendix" contenteditable="false">';
      combined += `<h2 style="font-size:16.5px;font-weight:700;margin:32px 0 12px;color:var(--iio-text-1,#1a1a1a);padding-bottom:6px;border-bottom:1px solid var(--iio-border,#e5e5e5)">${t("dokument.weitereDiagramme")}</h2>`;
      for (const b of leftB) combined += figFor(false)("bpmn", b.svg, b.name ? t("dokument.prozessdiagrammMitName", { name: b.name }) : t("dokument.prozessdiagramm"), b.name || "");
      for (const s of leftS) combined += figFor(false)("landscape", s.svg || "", s.title || "", s.id || "");
      combined += '</div>';
    }
    return <div dangerouslySetInnerHTML={{ __html: sauberesHtml(combined) }} />;
  };

  const buildPrintHtml = () => {
    if (!docContent) return "";
    let body = docContent
      .replace(/```svg\n([\s\S]*?)```/g, (_, svg) => `<figure class="doku-svg-figure" style="margin:16px auto;text-align:center;page-break-inside:avoid">${svg}</figure>`)
      .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:#fff;padding:10px 12px;font-size:11.5px;line-height:1.5;margin:8px 0;border:1px solid #e4eaec;page-break-inside:avoid"><code>$2</code></pre>')
      .replace(/^#{5,6}\s+(.+)$/gm, '<h5 style="font-size:12px;font-weight:600;margin:12px 0 3px;color:#0A3D4A">$1</h5>')
      .replace(/^####\s+(.+)$/gm, '<h4 style="font-size:12.5px;font-weight:600;margin:14px 0 4px;color:#0A3D4A">$1</h4>')
      .replace(/^###\s+(.+)$/gm, '<h3 style="font-size:13.5px;font-weight:600;margin:16px 0 4px;color:#0A3D4A">$1</h3>')
      .replace(/^##\s+(.+)$/gm, '<h2 style="font-size:15px;font-weight:700;margin:24px 0 8px;color:#0A3D4A;padding-bottom:5px;border-bottom:1px solid #e4eaec">$1</h2>')
      .replace(/^#\s+(.+)$/gm, '<h1 style="font-size:19px;font-weight:700;margin:0 0 12px;color:#0A3D4A">$1</h1>')
      .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>")
      .replace(/`([^`\n]+)`/g, '<code style="background:#f4f7f8;padding:1px 4px;font-size:0.88em">$1</code>')
      .replace(/^- (.+)$/gm, "<li>$1</li>").replace(/^\d+\.\s+(.+)$/gm, "<li>$1</li>");
    body = body.replace(/(\|[^\n]+\|\n\|[-| :]+\|\n(?:\|[^\n]+\|\n?)*)/g, (table) => {
      const rows = table.trim().split("\n").filter(r => !r.match(/^\|[-| :]+\|$/));
      if (rows.length < 1) return table;
      const pr = r => r.split("|").slice(1, -1).map(c => c.trim());
      const hdr = pr(rows[0]); const bd = rows.slice(1).map(pr);
      return `<table style="width:100%;border-collapse:collapse;margin:12px 0;font-size:11.5px;page-break-inside:avoid"><thead><tr>${hdr.map(h => `<th style="text-align:left;padding:6px 10px;border-bottom:1.5px solid #0A3D4A;font-weight:600;color:#0A3D4A">${h}</th>`).join("")}</tr></thead><tbody>${bd.map(r => `<tr>${r.map(c => `<td style="padding:6px 10px;border-bottom:1px solid #e4eaec;color:#26343a">${c}</td>`).join("")}</tr>`).join("")}</tbody></table>`;
    });
    body = body.replace(/\n\n/g, '<div style="height:8px"></div>').replace(/\n/g, "<br/>");
    const figFor = (kind, svg, cap) => `<figure class="doku-svg-figure" style="margin:16px 0;page-break-inside:avoid${kind === "landscape" ? ";text-align:center" : ""}"><figcaption style="font-weight:600;font-size:12px;color:#73828a;margin-bottom:6px">${cap}</figcaption><div style="border:1px solid #e4eaec;padding:12px;background:#fff">${svg}</div></figure>`;
    const placed = placeDiagramMarkers(body, bpmnSvgs || [], svgs || [], figFor);
    body = placed.html;
    const leftB = (bpmnSvgs || []).filter((_, i) => !placed.usedBpmn.has(i));
    const leftS = placed.leftInline;
    if (leftB.length || leftS.length) {
      body += `<h2 style="font-size:15px;font-weight:700;margin:24px 0 8px;color:#0A3D4A;padding-bottom:5px;border-bottom:1px solid #e4eaec">${t("dokument.weitereDiagramme")}</h2>`;
      for (const b of leftB) body += figFor("bpmn", b.svg, b.name ? t("dokument.prozessdiagrammMitName", { name: b.name }) : t("dokument.prozessdiagramm"));
      for (const s of leftS) body += figFor("landscape", s.svg || "", s.title || "");
    }
    // ⛔ Der gesamte docContent-abgeleitete Druckkoerper wird vor der Ausgabe (document.write)
    // bereinigt - sonst fuehrt ein <script> aus geteiltem Doku-Inhalt beim PDF-Druck Code aus.
    return sauberesHtml(`<div style="font-family:Source Sans 3,Segoe UI,sans-serif;color:#26343a;font-size:13px;line-height:1.7"><div style="margin-bottom:30px"><div style="font-size:10px;text-transform:uppercase;letter-spacing:.16em;color:#73828a;margin-bottom:8px">${isMulti ? t("dokument.eyebrowMulti") : t("dokument.eyebrowEinzel")}</div><div style="font-size:23px;font-weight:700;color:#0A3D4A;letter-spacing:-.01em">${flowName}</div><div style="width:46px;height:2px;background:#E85D2E;margin:12px 0 10px"></div><div style="font-size:12px;color:#73828a">${styleLabel} · ${nowStr}</div></div><div class="doku-print-body" style="overflow-wrap:anywhere"><p style="font-style:italic;font-size:11px;line-height:1.5;color:#5b6b72;margin:0 0 16px;padding:8px 12px;border-left:3px solid #E85D2E;background:#f7f9fa">${kiHinweis}</p>${body}</div><div style="margin-top:32px;padding-top:10px;border-top:1px solid #e4eaec;font-size:10px;color:#9aa6ac">${String(branding?.title || t("dokument.markeFallback")).replace(/&/g, "&amp;").replace(/</g, "&lt;")} · ${nowStr}</div></div>`);
  };

  const hasResult = docContent || svgs.length > 0 || bpmnSvgs.length > 0;

  // Alle Aenderungen bleiben im Text markiert, nicht nur die juengste. Je Kapitel sammeln wir
  // die Aenderungen, die es angefasst haben (neueste zuerst) - daraus entstehen das Info-Symbol
  // und der Inhalt seines Popups. Aufgeloest wird ueber den Kapitel-Anker, damit eine Markierung
  // nach spaeteren Aenderungen weiter am richtigen Kapitel haengt.
  const markierungen = useMemo(() => {
    if (!zeigeAenderungen || selfEdit || !docContent || !revisionen.length) return null;
    const karte = new Map();
    revisionen.forEach((r, revIdx) => {
      for (const [idx, stelle] of loeseStellen(docContent, r.stellen || [])) {
        if (!karte.has(idx)) karte.set(idx, []);
        karte.get(idx).unshift({ revIdx, rev: r, stelle });   // unshift -> neueste vorn
      }
    });
    return karte.size ? karte : null;
  }, [docContent, revisionen, zeigeAenderungen, selfEdit]);

  // Wortvergleich als Anzeige. Rand-Leerzeichen weg (ein zusaetzlicher Umbruch ist keine
  // Aenderung), lange unveraenderte Passagen gekuerzt.
  const diffAnsicht = (vorher, nachher) => {
    const a = String(vorher || "").replace(/\s+$/, ""), b = String(nachher || "").replace(/\s+$/, "");
    if (!a && !b) return <span className="g">{t("verlauf.keinText")}</span>;
    return wortDiff(a, b).map((t, ti) => t.typ === "gleich"
      ? <span key={ti} className="g">{t.text.length > 240 ? `${t.text.slice(0, 120)} […] ${t.text.slice(-120)}` : t.text}</span>
      : <span key={ti} className={t.typ}>{t.text}</span>);
  };

  // Popup schliessen, sobald seine Stelle nicht mehr existiert (z.B. nach dem Zuruecknehmen).
  useEffect(() => {
    if (chgPop && !markierungen?.get(chgPop.idx)?.length) setChgPop(null);
  }, [chgPop, markierungen]);

  useEffect(() => {
    if (!chgPop) return;
    const zu = (e) => { if (e.key === "Escape") setChgPop(null); };
    window.addEventListener("keydown", zu);
    return () => window.removeEventListener("keydown", zu);
  }, [chgPop]);

  // Schritt 1-3 gelten ab Generierungsstart als abgeschlossen -> kompakter Kopf statt leerem Riesen-Kasten
  const autoCollapse = !!(loading || hasResult);
  const sectIsOpen = (k) => !autoCollapse || !!sectOpen[k];
  const toggleSect = (k) => { if (!autoCollapse) return; setSectOpen(p => ({ ...p, [k]: !p[k] })); if (k === "s2") setMeasureTick(t => t + 1); };
  const chev = (k) => autoCollapse && (
    <svg className={"chev" + (sectIsOpen(k) ? " open" : "")} viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
  );
  const doneNo = (n) => autoCollapse
    ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    : n;

  // Stepper: aktueller Schritt aus dem echten Seitenzustand abgeleitet (klickbar = scrollt zur Sektion)
  const stepIdx = (loading || hasResult) ? 3 : readyFlows.length ? 2 : flows.length ? 1 : 0;
  const STEP_LABELS = [t("schritt.quelle"), t("schritt.fluss"), t("schritt.stil"), t("schritt.erstellen")];
  const scrollToSect = (i) => {
    const el = document.getElementById(["dk-sec1", "dk-sec2", "dk-sec3", "dk-sec4"][i]) || (i === 3 ? document.getElementById("dk-sec4r") : null);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="iio dk-root" style={{ width: "100%", maxWidth: 1520, margin: "0 auto", padding: "30px 36px 90px", boxSizing: "border-box" }}>
      <style>{DK_CSS}</style>
      <div className="crumb">{t("kopf.bereich")} <b>&gt;&gt;</b> {t("kopf.seite")}</div>
      <h1 className="dk-h1">{t("kopf.titel")}</h1>
      <div className="dk-sub">{t("kopf.untertitel")}</div>

      <div className="dk-rail">
        {STEP_LABELS.map((lbl, i) => (
          <Fragment key={i}>
            {i > 0 && <span className={"dk-rseg" + (i <= stepIdx ? " fill" : "")}><i /></span>}
            <button type="button" className={"dk-rnode" + (i === stepIdx ? " act" : i < stepIdx ? " done" : " future")}
              onClick={() => { if (i <= stepIdx) scrollToSect(i); }} tabIndex={i > stepIdx ? -1 : 0} aria-disabled={i > stepIdx}>
              <span className="dk-rdot">{i < stepIdx ? <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg> : i + 1}</span>
              <span className="lbl">{lbl}</span>
            </button>
          </Fragment>
        ))}
      </div>

      <div className="dk-grid">

      {/* Sektion 1: Quelle (ZIP-Upload oder iFlow aus Tenant) + Agenten-Team */}
      <section className="dk-sect" id="dk-sec1">
        <div className={"dk-sh" + (autoCollapse ? " clickable" : "")} onClick={() => toggleSect("s1")}>
          <span className={"no" + (autoCollapse ? " ok" : "")}>{doneNo("1")}</span>
          <div>
            <h2>{t("quelle.titel")}</h2>
            <div className="shsub">{autoCollapse
              ? (flows.length === 0 && restoredMeta
                  ? t("quelle.wiederhergestellt", { namen: (restoredMeta.flowNames || []).slice(0, 4).join(", ") || t("quelle.wiederhergestelltOhneNamen") })
                  : `${t("quelle.bereitAnzahl", { count: readyFlows.length })} · ${sourceMode === "tenant" ? t("quelle.ausTenant") : t("quelle.perZip")}`)
              : t("quelle.frage")}</div>
          </div>
          <span className="right">
            {!autoCollapse && readyFlows.length > 0 && <>{t("quelle.bereitKurz", { count: readyFlows.length })}{flows.some(f => f.inspecting) ? t("quelle.wirdGelesen") : ""}</>}
            {chev("s1")}
          </span>
        </div>
        {sectIsOpen("s1") && (
        <div className="dk-s1">
        <div>
        <div className="dk-scn-row" style={{ marginBottom: 16 }}>
          <button type="button" className={"dk-scn" + (sourceMode === "tenant" ? " is-active" : "")} onClick={() => setSourceMode("tenant")} disabled={loading}>
            <span className="ic"><svg viewBox="0 0 24 24"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg></span>
            <span className="t">{t("quelle.tenantTitel")}</span>
            <span className="d">{t("quelle.tenantText")}</span>
          </button>
          <button type="button" className={"dk-scn" + (sourceMode === "upload" ? " is-active" : "")} onClick={() => setSourceMode("upload")} disabled={loading}>
            <span className="ic"><svg viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg></span>
            <span className="t">{t("quelle.zipTitel")}</span>
            <span className="d">{t("quelle.zipText")}</span>
          </button>
        </div>

        {sourceMode === "upload" ? (
          <div className={`iio-doku-drop${dragOver ? " iio-doku-drop--over" : ""}`}
            onDragOver={e => { e.preventDefault(); setDragOver(true); }} onDragLeave={() => setDragOver(false)} onDrop={onDrop}
            onClick={() => !loading && fileRef.current?.click()} style={{ cursor: loading ? "default" : "pointer" }}>
            <input ref={fileRef} type="file" accept=".zip" multiple style={{ display: "none" }} onChange={e => { if (e.target.files?.length) addFiles(e.target.files); e.target.value = ""; }} />
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
            <div style={{ marginTop: 8, color: v("text-1"), fontSize: 14, fontWeight: 600 }}>{flows.length ? t("quelle.ablegenWeitere") : t("quelle.ablegen")}</div>
            <div style={{ color: v("text-3"), fontSize: 11, marginTop: 6, letterSpacing: "0.01em" }}>{t("quelle.ablegenSub")}</div>
          </div>
        ) : (
          <div>
            <div className="iio-doku-tn-row">
              <label><span>{t("quelle.space")}</span>
                <select value={tnSpaceId} onChange={e => setTnSpaceId(e.target.value)}>
                  <option value="">{t("quelle.spaceWaehlen")}</option>
                  {spaces.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label><span>{t("quelle.instanz")}</span>
                <select value={tnInstId} onChange={e => setTnInstId(e.target.value)} disabled={!tnSpaceId || tnLoading.instances}>
                  <option value="">{tnLoading.instances ? t("quelle.laedt") : t("quelle.instanzWaehlen")}</option>
                  {tnInstances.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                </select>
              </label>
              {/* Solange gesucht wird, regiert die Suche die Liste - dann darf die Paketauswahl
                  nicht gleichzeitig bedienbar sein, sonst stehen zwei Wege nebeneinander. */}
              {/* Der Titel sitzt am label, NICHT am select: auf deaktivierten Formularelementen
                  zeigen Chrome und Safari keinen Tooltip an. */}
              <label className={sucheAktiv ? "aus" : ""}
                title={sucheAktiv ? t("quelle.paketGesperrt") : undefined}>
                <span>{t("quelle.paket")}</span>
                <select value={tnPkgId} onChange={e => setTnPkgId(e.target.value)}
                  disabled={!tnInstId || tnLoading.packages || sucheAktiv}>
                  <option value="">{tnLoading.packages ? t("quelle.laedt") : t("quelle.paketWaehlen")}</option>
                  {tnPackages.map(p => <option key={p.Id} value={p.Id}>{p.Name || p.Id}</option>)}
                </select>
              </label>
            </div>

            {tnError && <div className="iio-doku-tn-err">{tnError}</div>}

            {/* EIN Feld, EINE Liste. Leer: die Liste zeigt das gewaehlte Paket. Mit Text: sie
                zeigt Treffer aus der ganzen Instanz und die Paketauswahl ist gesperrt. Nie
                beides - genau das war der Fehler der ersten Fassung. */}
            {tnInstId && (
              <div className={"iio-doku-tn-list" + (sucheAktiv ? " suche" : "")}>
                {/* Feld und Modus-Streifen bilden EINE Kopfzone und bleiben beim Scrollen stehen -
                    sonst stuende oben nur noch das ausgegraute Package ueber fremden Treffern. */}
                <div className="iio-doku-tn-kopf">
                  <input className="iio-doku-tn-search" ref={sucheRef} value={tnSearch}
                    aria-label={t("quelle.sucheAria")}
                    onChange={e => { setTnSearch(e.target.value); if (tnError) setTnError(null); }}
                    placeholder={tnPkgId ? t("quelle.suchePlatzhalterMitPaket") : t("quelle.suchePlatzhalterOhnePaket")} />
                  {sucheAktiv && (
                    <div className="iio-doku-tn-modus">
                      {/* Der Sperrgrund steht IMMER hier - auch ohne gewaehltes Package ist die
                          Auswahl grau, und dann ist dies die einzige Erklaerung dafuer. */}
                      {/* Die Zahl nur zeigen, wenn sie zur aktuellen Eingabe gehoert - waehrend
                          einer laufenden Anfrage stuende sonst die Zahl der vorigen darueber. */}
                      <span>{t("quelle.sucheModus")}
                        {!tnGlobalLaeuft && tnGlobalTreffer && tnGlobalTreffer.gesamt > tnGlobalTreffer.treffer.length
                          ? t("quelle.sucheGezeigt", { gesamt: tnGlobalTreffer.gesamt, gezeigt: tnGlobalTreffer.treffer.length })
                          : ""}</span>
                      <button type="button" onClick={() => { setTnSearch(""); setTnError(null); sucheRef.current?.focus(); }}>{t("quelle.sucheVerlassen")}</button>
                    </div>
                  )}
                </div>
                {sucheAktiv ? (<>
                  {tnSearch.trim().length < 2 ? (
                    <div className="iio-doku-tn-empty">{t("quelle.mindestensZweiZeichen")}</div>
                  ) : tnGlobalLaeuft ? (
                    <div className="iio-doku-tn-empty"><span className="iio-spinner" style={{ width: 14, height: 14 }} /> {t("quelle.wirdGesucht")}</div>
                  ) : !tnGlobalTreffer ? null : tnGlobalTreffer.treffer.length === 0 ? (
                    <div className="iio-doku-tn-empty">{t("quelle.keinTreffer", { durchsucht: tnGlobalTreffer.durchsucht })}</div>
                  ) : (<>
                    {tnGlobalTreffer.treffer.map(f => {
                      const added = flows.some(fl => fl.source === "tenant" && fl.flowId === f.Id && fl.iid === tnInstId);
                      const adding = tnLoading.adding === f.Id;
                      return (
                        <div key={f.Id} className="iio-doku-tn-flow">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>
                          <span className="nm" title={`${f.Name || f.Id}${f.PackageName || f.PackageId ? ` · ${f.PackageName || f.PackageId}` : ""}`}>{f.Name || f.Id}
                            {(f.PackageName || f.PackageId) && <span className="pk">{f.PackageName || f.PackageId}</span>}</span>
                          <button className="add" disabled={added || adding || loading} onClick={() => addTenantFlow(f, f.PackageId)}>
                            {adding ? <span className="iio-spinner" style={{ width: 12, height: 12 }} /> : added ? t("quelle.hinzugefuegt") : t("quelle.hinzufuegen")}
                          </button>
                        </div>
                      );
                    })}
                  </>)}
                </>) : !tnPkgId ? (
                  <div className="iio-doku-tn-empty">{t("quelle.keinPaket")}</div>
                ) : tnLoading.flows ? (
                  <div className="iio-doku-tn-empty"><span className="iio-spinner" style={{ width: 14, height: 14 }} /> {t("quelle.flowsLaden")}</div>
                ) : tnFlows.length === 0 ? (
                  <div className="iio-doku-tn-empty">{t("quelle.keineFlows")}</div>
                ) : tnFlows.map(f => {
                  const added = flows.some(fl => fl.source === "tenant" && fl.flowId === f.Id && fl.iid === tnInstId);
                  const adding = tnLoading.adding === f.Id;
                  return (
                    <div key={f.Id} className="iio-doku-tn-flow">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      <span className="nm" title={f.Name || f.Id}>{f.Name || f.Id}</span>
                      <button className="add" disabled={added || adding || loading} onClick={() => addTenantFlow(f)}>
                        {adding ? <span className="iio-spinner" style={{ width: 12, height: 12 }} /> : added ? t("quelle.hinzugefuegt") : t("quelle.hinzufuegen")}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="iio-doku-tn-hint">{t("quelle.pruefhinweis")}</div>
          </div>
        )}

        {flows.length === 0 && (
          <div className="dk-stats">
            <div className="dk-stat"><span className="n">2</span><span className="l"><b>{t("quelle.stat.quellenTitel")}</b><span>{t("quelle.stat.quellenText")}</span></span></div>
            <div className="dk-stat"><span className="n">4</span><span className="l"><b>{t("quelle.stat.agentenTitel")}</b><span>{t("quelle.stat.agentenText")}</span></span></div>
            <div className="dk-stat"><span className="n">3</span><span className="l"><b>{t("quelle.stat.exporteTitel")}</b><span>{t("quelle.stat.exporteText")}</span></span></div>
          </div>
        )}
        </div>

        <aside className="dk-agents">
          <div className="ah">
            <span className="mark">{t("team.marke")}</span>
            <div><b>{t("team.titel")}</b><span>{t("team.untertitel")}</span></div>
          </div>
          <div className="dk-agrow"><span className="dk-agdot"><svg viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span><span className="tx"><b>{t("agent.analyse.titel")}</b><span>{t("agent.analyse.kurz")}</span></span></div>
          <div className="dk-aglink"><i /></div>
          <div className="dk-agrow"><span className="dk-agdot"><svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><path d="M10 6.5h7V14"/></svg></span><span className="tx"><b>{t("agent.viz.titel")}</b><span>{t("agent.viz.kurz")}</span></span></div>
          <div className="dk-aglink"><i /></div>
          <div className="dk-agrow"><span className="dk-agdot"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg></span><span className="tx"><b>{t("agent.redaktion.titel")}</b><span>{t("agent.redaktion.kurz")}</span></span></div>
          <div className="dk-aglink"><i /></div>
          <div className="dk-agrow"><span className="dk-agdot"><svg viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><path d="m9 12 2 2 4-4"/></svg></span><span className="tx"><b>{t("agent.validierung.titel")}</b><span>{t("agent.validierung.kurz")}</span></span></div>
          <div className="foot">{t("team.fussVor")}<b>{t("team.fussKnopf")}</b>{t("team.fussNach")}</div>
        </aside>
        </div>
        )}
      </section>

      {/* Weitermachen: pro User gespeicherte Dokumentationen (ab Klick auf "Generieren") */}
      {dokuSessions.length > 0 && !hasResult && !loading && (
        <section className="dk-sect" id="dk-sessions">
          <div className="dk-sh">
            <span className="no"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg></span>
            <div>
              <h2>{t("weitermachen.titel")}</h2>
              <div className="shsub">{t("weitermachen.untertitel")}</div>
            </div>
            <span className="right">
              <span className="dk-sess-searchwrap">
                <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></svg>
                <input className="dk-sess-search" value={sessQuery} placeholder={t("weitermachen.suchePlatzhalter")}
                  onChange={e => { setSessQuery(e.target.value); setSessShown(5); }} />
              </span>
            </span>
          </div>
          {(() => {
            const q = sessQuery.trim().toLowerCase();
            const filtered = !q ? dokuSessions : dokuSessions.filter(x =>
              (x.processName || "").toLowerCase().includes(q) || (x.flowNames || []).some(n => String(n).toLowerCase().includes(q)));
            const visible = filtered.slice(0, sessShown);
            return (<>
          {filtered.length === 0 && (
            <div className="dk-hintln" style={{ marginTop: 0 }}>{t("weitermachen.keinTreffer", { suche: sessQuery.trim() })}</div>
          )}
          <div className="dk-sesslist">
            {visible.map(sess => {
              const stl = stilTitel(sess.style);
              const when = sess.updatedAt ? new Date(sess.updatedAt).toLocaleString(gebiet, { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";
              const stCls = sess.status === "complete" ? "ok" : sess.status === "truncated" ? "warn" : "idle";
              const stLbl = sess.status === "complete" ? t("weitermachen.status.vollstaendig") : sess.status === "truncated" ? t("weitermachen.status.tokenlimit") : sess.status === "cancelled" ? t("weitermachen.status.abgebrochen") : t("weitermachen.status.unvollstaendig");
              return (
                <div key={sess.id} className="dk-sess">
                  <button type="button" className="main" onClick={() => restoreSession(sess.id)} title={t("weitermachen.wiederherstellen")}>
                    <span className="ic"><svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg></span>
                    <span className="tx">
                      <b>{sess.processName || (sess.flowNames || []).join(", ") || t("weitermachen.ohneNamen")}</b>
                      {/* Fremdes Dokument erkennbar machen: sonst sieht eine geteilte Doku in der
                          Liste aus wie eine eigene. */}
                      <span>{[stl, when, sess.istBesitzer === false && sess.besitzerName ? t("weitermachen.geteiltVon", { name: sess.besitzerName }) : ""].filter(Boolean).join(" · ")}</span>
                    </span>
                    <span className={"st " + stCls}>{stLbl}</span>
                    <svg className="go" viewBox="0 0 24 24"><path d="M9 18l6-6-6-6" /></svg>
                  </button>
                  {/* Loeschen darf nur der Besitzer - ein Knopf, der zuverlaessig 403 liefert,
                      waere ein Versprechen, das die Seite nicht halten kann. */}
                  {sess.istBesitzer !== false && (
                    <button type="button" className="del" aria-label={t("weitermachen.entfernen")} title={t("weitermachen.entfernen")} onClick={() => deleteSession(sess.id)}>
                      <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  )}
                </div>
              );
            })}
          </div>
          {filtered.length > sessShown && (
            <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
              <button type="button" className="dk-btn ghost sm" onClick={() => setSessShown(n => n + 5)}>
                <svg viewBox="0 0 24 24"><polyline points="6 9 12 15 18 9" /></svg>
                {t("weitermachen.mehrAnzeigen", { count: filtered.length - sessShown })}
              </button>
            </div>
          )}
            </>);
          })()}
        </section>
      )}

      {/* Sektion 2: Prozessfluss-Canvas (horizontale Kette) */}
      {flows.length > 0 && (
        <section className="dk-sect" id="dk-sec2">
          <div className={"dk-sh" + (autoCollapse ? " clickable" : "")} onClick={() => toggleSect("s2")}>
            <span className={"no" + (autoCollapse ? " ok" : "")}>{doneNo("2")}</span>
            <div>
              <h2>{t("fluss.titel")}</h2>
              <div className="shsub">{autoCollapse
                ? `${t("fluss.elemente", { count: flows.length })} · ${t("fluss.verbindungen", { count: connections.length })}`
                : t("fluss.frage")}</div>
            </div>
            <span className="right">
              {!autoCollapse && isMulti && <>{t("fluss.sortierhinweis")}</>}
              {!autoCollapse && (
                <button type="button" className="dk-btn ghost sm" title={t("fluss.verbindungenSuchenTitel")}
                  onClick={e => { e.stopPropagation(); searchAllConnections(); }}
                  disabled={loading || !!depStatus?.loading || !flows.some(f => f.source === "tenant" && !f.auto)}>
                  {depStatus?.loading
                    ? <span className="iio-spinner" style={{ width: 12, height: 12 }} />
                    : <svg viewBox="0 0 24 24"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" /></svg>}
                  {t("fluss.verbindungenSuchen")}
                </button>
              )}
              {chev("s2")}
            </span>
          </div>
          {sectIsOpen("s2") && (<>

          <div ref={scrollRef} className={`iio-doku-canvas${loading ? " iio-doku-canvas--busy" : ""}`}>
            <div ref={canvasRef} className={`iio-doku-track${flows.length >= 5 ? " iio-doku-track--dense" : ""}`} style={{ paddingTop: isMulti ? (connections.length ? 86 : 60) : 36 }}>
              {/* SVG: Kette + semantische Boegen + Zeichenlinie */}
              <svg className="iio-doku-svg">
                <defs>
                  {CONN_COLORS.map((color, i) => (
                    <marker key={i} id={`doku-arrow-${i}`} viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto-start-reverse">
                      <path d="M 0 1.5 L 10 5 L 0 8.5 z" fill={color} />
                    </marker>
                  ))}
                  <marker id="doku-arrow-draw" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="6.5" markerHeight="6.5" orient="auto">
                    <path d="M 0 1.5 L 10 5 L 0 8.5 z" fill="var(--iio-signal)" />
                  </marker>
                </defs>
                {/* Ketten-Segmente (Reihenfolge) - nur innerhalb einer Zeile, kein diagonaler Umbruch-Strich */}
                {isMulti && flows.slice(0, -1).map((fl, i) => {
                  const s = chainSeg(fl.id, flows[i + 1].id);
                  if (!s || Math.abs(s.y1 - s.y2) > 12 || s.x2 <= s.x1) return null;
                  return <line key={"ch" + fl.id} className="iio-doku-chain" x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2} />;
                })}
                {/* Semantische Verbindungen */}
                {connections.map((conn, ci) => {
                  const color = CONN_COLORS[ci % CONN_COLORS.length];
                  const isBidiSecond = connections.some((c, oi) => oi !== ci && c.fromId === conn.toId && c.toId === conn.fromId)
                    && connections.findIndex(c => c.fromId === conn.toId && c.toId === conn.fromId) < ci;
                  const offset = isBidiSecond ? 26 : 0;
                  const p = connPath(conn.fromId, conn.toId, offset);
                  if (!p) return null;
                  return <g key={ci} className="iio-doku-edge-g">
                    <path className="iio-doku-edge-glow" d={p.d} stroke={color} />
                    <path className="iio-doku-edge" d={p.d} stroke={color} markerEnd={`url(#doku-arrow-${ci % CONN_COLORS.length})`} />
                    <path className="iio-doku-edge-hit" d={p.d}
                      onClick={e => { e.stopPropagation(); setEditConn({ index: ci, screenX: e.clientX, screenY: e.clientY }); }} />
                  </g>;
                })}
                {/* Zeichen-Linie waehrend des Ziehens */}
                {drawingConn && (
                  <path className="iio-doku-draw" markerEnd="url(#doku-arrow-draw)"
                    d={`M ${drawingConn.startX} ${drawingConn.startY} L ${drawingConn.mouseX} ${drawingConn.mouseY}`} />
                )}
              </svg>

              {/* Knoten-Karten */}
              {flows.map((fl, idx) => {
                const isArtifact = fl.kind === "artifact";
                const artLabel = fl.artifactType === "valueMapping" ? "Value Mapping" : fl.artifactType === "scriptCollection" ? "Script Collection" : fl.artifactType === "messageMapping" ? "Message Mapping" : t("fluss.artefakt");
                return (
                <div key={fl.id} ref={el => { if (el) cardRefs.current[fl.id] = el; else delete cardRefs.current[fl.id]; }}
                  className={`iio-doku-node${isArtifact ? " iio-doku-node--artifact" : ""}${fl.restricted ? " iio-doku-node--restricted" : ""}${fl.auto ? " iio-doku-node--auto" : ""}${dragIdx === idx ? " iio-doku-node--drag" : ""}${drawingConn && drawingConn.fromId !== fl.id ? " iio-doku-node--target" : ""}`}
                  style={{ animationDelay: `${idx * 45}ms` }}
                  onDragOver={e => { e.preventDefault(); handleDragOver(e, idx); }}
                  onDrop={handleDragEnd}
                  onMouseUp={() => { if (drawingConn) finishConnection(fl.id); }}>
                  {isMulti && !isArtifact && <span className="iio-doku-node-num">{idx + 1}</span>}
                  {isMulti && !isArtifact && !fl.auto && (
                    <span className="iio-doku-grip" draggable={!loading} onDragStart={e => handleDragStart(e, idx)} onDragEnd={handleDragEnd} title={t("fluss.sortierenZiehen")}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor"><circle cx="8" cy="4" r="2.5"/><circle cx="16" cy="4" r="2.5"/><circle cx="8" cy="12" r="2.5"/><circle cx="16" cy="12" r="2.5"/><circle cx="8" cy="20" r="2.5"/><circle cx="16" cy="20" r="2.5"/></svg>
                    </span>
                  )}
                  {!loading && (
                    <button className="iio-doku-rm" onClick={() => removeFlow(fl.id)} title={t("fluss.entfernen")}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                    </button>
                  )}
                  <span className="iio-doku-node-ico">
                    {isArtifact
                      ? <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="12 2 22 8.5 12 15 2 8.5 12 2"/><polyline points="2 15.5 12 22 22 15.5"/></svg>
                      : <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>}
                  </span>
                  {(() => { const full = isArtifact ? (fl.artifactName || t("fluss.artefakt")) : (fl.zipInfo?.flowName || fl.file?.name?.replace(/\.zip$/i, "") || t("fluss.iflow")); return (
                  <span className="iio-doku-node-title" title={full}>{full}</span>
                  ); })()}
                  {isArtifact
                    ? <span className="iio-doku-node-meta">{artLabel}{fl.referencedBy?.length ? t("fluss.geteiltVon", { count: fl.referencedBy.length }) : ""}</span>
                    : fl.relation === "pipeline" && (fl.pipelineStep != null || fl.pipelineRole)
                      ? <span className="iio-doku-node-meta">{fl.pipelineStep != null ? t("fluss.pipelineStep", { nummer: String(fl.pipelineStep).padStart(2, "0") }) : t("fluss.pipeline")}{fl.pipelineRole ? ` · ${fl.pipelineRole}` : ""}</span>
                      : fl.restricted
                        ? <span className="iio-doku-node-meta">{t("fluss.nichtLesbar")}</span>
                        : fl.zipInfo && <span className="iio-doku-node-meta"><b>{fl.zipInfo.scriptCount}</b> {t("fluss.skripte", { count: fl.zipInfo.scriptCount })}</span>}
                  {fl.auto && !isArtifact && <span className="iio-doku-node-rel">{fl.relation === "calls" ? t("fluss.relation.calls") : fl.relation === "calledBy" ? t("fluss.relation.calledBy") : fl.relation === "pipeline" ? t("fluss.relation.pipeline") : t("fluss.relation.abhaengig")}</span>}
                  {fl.inspecting && <span className="iio-doku-node-busy">{t("fluss.liest")}</span>}
                  {isMulti && !isArtifact && <>
                    <span className="iio-doku-port iio-doku-port--in" title={t("fluss.portEin")} />
                    <span className={`iio-doku-port iio-doku-port--out${drawingConn?.fromId === fl.id ? " iio-doku-port--active" : ""}${!loading && connections.length === 0 && idx === 0 ? " iio-doku-port--hint" : ""}`}
                      onMouseDown={e => startConnection(fl.id, e)} title={t("fluss.portAus")} />
                  </>}
                </div>
                );
              })}

              {/* Verbindungs-Label-Chips: an die jeweilige Zielkarte gesetzt + entzerrt, Volltext im Tooltip */}
              {chipPositions.map(({ ci, conn, x, y }) => {
                const color = CONN_COLORS[ci % CONN_COLORS.length];
                return <div key={"chip" + ci} className="iio-doku-chip" style={{ left: x, top: y, borderColor: color, color }}
                  title={conn.label || ""}
                  onClick={e => setEditConn({ index: ci, screenX: e.clientX, screenY: e.clientY })}>
                  {conn.label || t("fluss.chipLeer")}
                </div>;
              })}
            </div>
          </div>

          {depStatus && (
            <div className={`iio-doku-depstatus${depStatus.loading ? " is-loading" : ""}`}>
              {depStatus.loading && <span className="iio-spinner" style={{ width: 12, height: 12 }} />}
              <span>{depStatus.message}</span>
              {!depStatus.loading && depStatus.capReached && <span className="muted">{t("fluss.grenzeErreicht")}</span>}
              {!depStatus.loading && depStatus.unresolved > 0 && <span className="muted">{t("fluss.nichtAufloesbar", { count: depStatus.unresolved })}</span>}
            </div>
          )}

          {isMulti && !loading && connections.length === 0 && readyFlows.length >= 2 && (
            <div className="iio-doku-hint">{t("fluss.ziehhinweis")}</div>
          )}

          {/* Verbindungs-Editor Popup */}
          {editConn && (<>
            <div onClick={() => setEditConn(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
            <div style={{ position: "fixed", top: Math.min(editConn.screenY || 300, window.innerHeight - 220), left: Math.min(editConn.screenX || 300, window.innerWidth - 400), zIndex: 9999, width: 360, background: "var(--iio-bg-2)", border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-lg)", padding: 16, boxShadow: "var(--iio-shadow-md)", animation: "dokuPopupIn 0.15s ease-out" }}
              onClick={e => e.stopPropagation()}>
              <div style={{ fontSize: 11, color: "var(--iio-text-3)", marginBottom: 6, fontWeight: 600 }}>{t("fluss.verbindungBeschreiben")}</div>
              <div style={{ fontSize: 12, color: "var(--iio-text-2)", padding: "8px 10px", background: "var(--iio-bg-1)", borderRadius: "var(--iio-r-sm)", marginBottom: 10 }}>
                {(() => { const c = connections[editConn.index]; if (!c) return ""; const fn = fl => flows.find(f => f.id === fl)?.zipInfo?.flowName || "?"; return `${fn(c.fromId)} >> ${fn(c.toId)}`; })()}
              </div>
              <input value={connections[editConn.index]?.label || ""} onChange={e => updateConnectionLabel(editConn.index, e.target.value)}
                placeholder={t("fluss.verbindungPlatzhalter")}
                autoFocus
                onKeyDown={e => { if (e.key === "Enter") setEditConn(null); if (e.key === "Escape") setEditConn(null); }}
                style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-sm)", fontSize: 13, boxSizing: "border-box", background: "var(--iio-bg-1)", color: "var(--iio-text-1)" }} />
              <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "space-between" }}>
                <button onClick={() => { removeConnection(editConn.index); setEditConn(null); }}
                  className="iio-btn iio-btn--ghost" style={{ fontSize: 12, padding: "6px 14px" }}>{t("gemeinsam:aktion.loeschen")}</button>
                <button className="iio-btn iio-btn--primary" onClick={() => setEditConn(null)}
                  style={{ fontSize: 12, padding: "6px 16px" }}>{t("fluss.fertig")}</button>
              </div>
            </div>
          </>)}
          </>)}
        </section>
      )}

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 14px", background: "color-mix(in srgb, var(--iio-critical) 8%, var(--iio-bg-1))", borderLeft: "3px solid var(--iio-critical)", borderRadius: "var(--iio-r-sm)", color: "var(--iio-text-1)", fontSize: 13 }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--iio-critical)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }}><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
          <span>{error}</span>
        </div>
      )}

      {/* Sektion 3: Stil + Kontext + Generieren */}
      {readyFlows.length > 0 && (
        <section className="dk-sect" id="dk-sec3">
        <div className={"dk-sh" + (autoCollapse ? " clickable" : "")} onClick={() => toggleSect("s3")}>
          <span className={"no" + (autoCollapse ? " ok" : "")}>{doneNo("3")}</span>
          <div>
            <h2>{t("stil.titel")}</h2>
            <div className="shsub">{autoCollapse
              ? t("stil.zusammenfassung", { stil: styleLabel, beigaben: t("stil.beigaben", { count: [ciLogo, ciPpt, ciPdf].filter(Boolean).length + bizDocs.length + (bizText.trim() ? 1 : 0) }) })
              : t("stil.frage")}</div>
          </div>
          <span className="right">{chev("s3")}</span>
        </div>
        {sectIsOpen("s3") && (
        <div className="dk-cfg">
        <div>{/* linke Spalte: Prozessname + Stil + CTA */}
        {isMulti && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: v("text-3"), marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.1em" }}>{t("stil.prozessname")} <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: "0.02em", marginLeft: 4 }}>{t("stil.optional")}</span></div>
            <input value={processName} onChange={e => setProcessName(e.target.value)}
              placeholder={t("stil.prozessnamePlatzhalter")}
              disabled={loading}
              style={{ width: "100%", padding: "9px 12px", border: "1px solid var(--iio-border)", borderRadius: "var(--iio-r-md)", fontSize: 13, background: v("bg-2"), color: v("text-1"), boxSizing: "border-box" }} />
          </div>
        )}

        <div>
          <div style={{ fontSize: 10, fontWeight: 600, color: v("text-3"), marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.1em" }}>{t("stil.dokumentationsstil")}</div>
          <div className="dk-stylecards">
            {DOC_STYLES.map(s => (
              <button type="button" key={s.id} className={"dk-scn" + (style === s.id ? " is-active" : "")} disabled={loading}
                onClick={() => { if (!loading) { setStyle(s.id); setDocContent(""); setSvgs([]); setBpmnSvgs([]); setValidation(null); } }}>
                <span className="t">{t(`stil.${s.id}.titel`)}</span>
                <span className="d">{t(`stil.${s.id}.beschreibung`)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="dk-actionrow">
          <span className="info">
            {t("stil.hinweisVor")}<b>{t("stil.hinweisIflows", { count: readyFlows.length })}</b>
            {flows.some(f => f.kind === "artifact") && <>{t("stil.hinweisUnd")}<b>{t("stil.hinweisArtefakte", { count: flows.filter(f => f.kind === "artifact").length })}</b></>}
            {connections.length > 0 && <>{t("stil.hinweisVerbindungen", { count: connections.length })}</>}.
          </span>
          <button type="button" className={"dk-btn " + (loading ? "ghost sm" : "prim")} onClick={e => { e.stopPropagation(); loading ? cancel() : generate(); }} disabled={!readyFlows.length}>
            {loading
              ? <><span className="iio-spinner" style={{ width: 13, height: 13 }} /> {t("gemeinsam:aktion.abbrechen")}</>
              : <><svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3" /></svg>{hasResult ? t("stil.neuGenerieren") : t("stil.generieren")}</>}
          </button>
        </div>
        </div>{/* Ende linke Spalte */}

        {/* rechte Spalte: Verfeinerungs-Board (Skala + CI-Slots + Fach-Kontext, alles optional) */}
        <aside className="dk-enrich">
          {(() => {
            const ciCount = [ciLogo, ciPpt, ciPdf].filter(Boolean).length;
            const bizCount = bizDocs.length + (bizText.trim() ? 1 : 0);
            const points = ciCount + Math.min(bizCount, 3);
            const level = points === 0 ? 0 : points <= 2 ? 1 : 2;
            const hint = level === 0
              ? t("stil.meterOhne")
              : level === 2
                ? t("stil.meterVoll")
                : ciCount && !bizCount
                  ? t("stil.meterNurCi")
                  : bizCount && !ciCount
                    ? t("stil.meterNurFach")
                    : t("stil.meterTeilweise");
            return (
              <div className="dk-enrich-meter">
                <span className="dk-enrich-eyebrow">{t("stil.verfeinerung")}</span>
                <ol className="dk-enrich-scale">
                  <li className="on">{t("stil.stufeBasis")}</li>
                  <li className={level >= 1 ? "on" : ""}>{t("stil.stufeAngereichert")}</li>
                  <li className={level >= 2 ? "on" : ""}>{t("stil.stufeMassgeschneidert")}</li>
                </ol>
                <p className="dk-enrich-hint" role="status">{hint}</p>
              </div>
            );
          })()}
          <section className="dk-enrich-card">
            <div className="dk-enrich-cardhead"><h3>{t("stil.ciTitel")}</h3><span className="dk-optional">{t("stil.optional")}</span></div>
            <p className="dk-enrich-lede">{t("stil.ciText")}</p>
            <div className="dk-enrich-slots">
              <EnrichSlot label={t("stil.ciLogo")} fmt=".PNG · .JPG" accept="image/*" file={ciLogo} onChange={setCiLogo} isImage disabled={loading} />
              <EnrichSlot label={t("stil.ciFolienmaster")} fmt=".PPTX" accept=".pptx,.ppt" file={ciPpt} onChange={setCiPpt} disabled={loading} />
              <EnrichSlot label={t("stil.ciReferenz")} fmt=".PDF" accept=".pdf" file={ciPdf} onChange={setCiPdf} disabled={loading} />
            </div>
          </section>
          <section className="dk-enrich-card">
            <div className="dk-enrich-cardhead"><h3>{t("stil.fachTitel")}</h3><span className="dk-optional">{t("stil.optional")}</span></div>
            <p className="dk-enrich-lede">{t("stil.fachText")}</p>
            <EnrichDropzone onAdd={docs => setBizDocs(prev => [...prev, ...docs])} disabled={loading} />
            {bizDocs.length > 0 && (
              <ul className="dk-enrich-chips">
                {bizDocs.map((doc, i) => (
                  <li key={i} className="dk-enrich-chip">
                    <span className="dk-enrich-chip-fmt">{dokuFmtOf(doc.mime, doc.name)}</span>
                    <span className="dk-enrich-chip-name" title={doc.name}>{doc.name}</span>
                    <button type="button" className="dk-enrich-chip-x" aria-label={t("stil.dateiEntfernen")} onClick={() => setBizDocs(prev => prev.filter((_, j) => j !== i))}>
                      <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                    </button>
                  </li>
                ))}
              </ul>
            )}
            <span className="dk-enrich-textlbl">{t("stil.freitextLabel")}</span>
            <textarea className="dk-enrich-text" value={bizText} onChange={e => setBizText(e.target.value)} disabled={loading} maxLength={8000}
              placeholder={t("stil.freitextPlatzhalter")} />
          </section>
        </aside>{/* Ende rechte Spalte */}
        </div>
        )}
        </section>
      )}

      {/* Sektion 4a: Agenten-Progress waehrend der Generierung (ehrliche %: Analyse real, Rest indeterminate) */}
      {loading && (() => {
        // Die Beschriftung kommt vom Server auf Deutsch. Uebersetzt wird ueber die Kennung, die
        // er mitschickt (analyse, synthese, viz, redaktion, validierung); ist sie unbekannt,
        // bleibt sein Text stehen.
        // id ist die KENNUNG ("analyse", "viz", ...), nicht die laufende Nummer:
        // der Server schickt sie so, und agentName wie die Beschreibung schluesseln
        // darueber. Vorher stand hier a.id (eine Zahl) und der Rueckfall traf keinen
        // einzigen Katalogeintrag.
        const agents = pipeline?.agents || AGENTS.map(a => ({ id: a.key, label: t(`agent.${a.key}.titel`) }));
        const agentName = (ag) => t(`agent.${ag.id}.titel`, { defaultValue: ag.label || "" });
        const AG_ICONS = [
          <svg key="i0" viewBox="0 0 24 24"><polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" /></svg>,
          <svg key="i1" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /><path d="M10 6.5h7V14" /></svg>,
          <svg key="i2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>,
          <svg key="i3" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" /><path d="m9 12 2 2 4-4" /></svg>,
        ];
        const activeIdx = agents.findIndex(ag => (pipeline ? agentStatus[ag.id] : (activePhase === ag.id ? "running" : null)) === "running");
        const fmtElapsed = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;
        return (
        <section className="dk-sect" id="dk-sec4">
          <div className="dk-genhead">
            <span className="bar2" />
            <b>{t("lauf.titel")}</b>
            <span>{t("lauf.untertitel", { iflows: t("lauf.iflows", { count: readyFlows.length }), stil: styleLabel })}</span>
            <span style={{ flex: 1 }} />
            <button type="button" className="dk-btn ghost sm" onClick={cancel}>
              <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>{t("gemeinsam:aktion.abbrechen")}
            </button>
          </div>
          <div className="dk-agcards">
            {agents.map((ag, i) => {
              const st = pipeline ? agentStatus[ag.id] : (phaseDone[ag.id] ? "done" : activePhase === ag.id ? "running" : null);
              const done = st === "done"; const active = st === "running";
              const isAnalyse = ag.id === "analyse" || i === 0;
              const total = isAnalyse ? (ag.total || analyseProg.total) : 0;
              const pct = isAnalyse && total ? Math.round((analyseProg.done / total) * 100) : 0;
              return (
                <div key={ag.id} className={"dk-agc" + (done ? " done" : active ? " run" : "")}>
                  <div className="head">
                    <span className="dk-agdot">{AG_ICONS[i % AG_ICONS.length]}</span>
                    {/* Ueber ag.id schluesseln, NICHT ueber die Position: der Server
                        schiebt bei mehreren iFlows "synthese" an Stelle 1 ein
                        (server.js:7428), die lokale AGENTS-Liste kennt sie nicht.
                        Ueber den Index verschob sich dadurch jede Beschreibung um
                        eins, und die Validierung bekam gar keine. agentName daneben
                        macht es seit jeher richtig. */}
                    <div><b>{agentName(ag)}</b><span>{t(`agent.${ag.id}.beschreibung`, { defaultValue: "" })}</span></div>
                  </div>
                  <span className={"st " + (done ? "ok" : active ? "run" : "wait")}>{done ? t("lauf.abgeschlossen") : active ? t("lauf.inArbeit") : t("lauf.wartet")}</span>
                  <div className={"dk-pbar" + (active && !(isAnalyse && total) ? " ind" : "")}>
                    <i className={done ? "ok" : "run"} style={active && !(isAnalyse && total) ? undefined : { transform: `scaleX(${done ? 1 : (isAnalyse && total ? pct / 100 : 0)})` }} />
                  </div>
                  <div className="dk-pmeta">
                    <span>{isAnalyse && total
                      ? <><b>{done ? total : analyseProg.done}/{total}</b>{t("lauf.analysiert")}</>
                      : done ? t("lauf.fertig") : active ? t("lauf.laeuft") : t("lauf.wartetAufVorgaenger")}</span>
                    <b>{done ? t("lauf.prozent", { wert: 100 }) : isAnalyse && total ? t("lauf.prozent", { wert: pct }) : t("lauf.keinWert")}</b>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="dk-genfoot">
            <span><b>{t("lauf.iflows", { count: readyFlows.length })}</b>{flows.some(f => f.kind === "artifact") ? t("lauf.plusArtefakte", { count: flows.filter(f => f.kind === "artifact").length }) : ""}{t("lauf.ausgewaehlt")}</span>
            <span className="sep" />
            <span>{activeIdx >= 0 ? <>{t("lauf.agentAktivVor")}<b>{t("lauf.agentAktivZahl", { nummer: activeIdx + 1, gesamt: agents.length })}</b>{t("lauf.agentAktivNach", { name: agentName(agents[activeIdx]) })}</> : t("lauf.agentenStarten")}</span>
            <span className="sep" />
            <span>{t("lauf.verstricheneZeit")}<b>{fmtElapsed}</b></span>
          </div>
        </section>);
      })()}

      {/* Sektion 4b: Ergebnis (auch waehrend des Streamings sichtbar) */}
      {hasResult && (
        <section className="dk-sect" id="dk-sec4r" style={{ position: "relative" }}>
          {/* Kopf oben ausgerichtet: aufgeklappt wird der Block hoch, mittig zentriert wuerde das
              Haekchen neben der Befundliste schweben statt neben der Ueberschrift zu stehen. */}
          {!loading && (
            <div className="dk-sh doku-no-print" style={{ marginBottom: 14, alignItems: "flex-start" }}>
              <span className={"no " + (genEnd === "truncated" || genEnd === "cancelled" ? "warn" : "ok")}>
                {genEnd === "truncated" || genEnd === "cancelled"
                  ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M12 9v4M12 17h.01" /><circle cx="12" cy="12" r="9" /></svg>
                  : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
              </span>
              <div>
                <h2>{genEnd === "truncated" ? t("ergebnis.abgeschnitten") : genEnd === "cancelled" ? t("ergebnis.abgebrochen") : t("ergebnis.erstellt")}</h2>
                <div className="shsub">{validation
                  ? (validation.fehler
                      ? t("ergebnis.pruefungFehler", { grund: validation.fehler })
                      : validation.ok ? t("ergebnis.pruefungOk")
                      : t("ergebnis.pruefungOffen", { count: validation.remaining }))
                  : t("ergebnis.standardUntertitel")}
                  {(svgs.length + bpmnSvgs.length) > 0 ? t("ergebnis.diagramme", { count: svgs.length + bpmnSvgs.length }) : ""}</div>
                {validation && Array.isArray(validation.befunde) && validation.befunde.length > 0 && (
                  <div className={"dk-befunde" + (befundeOffen ? " offen" : "")}>
                    <button type="button" className="bf-head" aria-expanded={befundeOffen}
                      onClick={() => setBefundeOffen(o => !o)}>
                      <svg className={"chev" + (befundeOffen ? " open" : "")} viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                      {t("ergebnis.befunde", { count: validation.befunde.length })}
                      <span className="bf-tip">{befundeOffen ? t("ergebnis.befundeEinklappen") : t("ergebnis.befundeAnsehen")}</span>
                    </button>
                    {befundeOffen && (
                      <>
                        <div className="bf-list">
                          {validation.befunde.map((b, bi) => (
                            /* Die Schwere traegt Wert UND Anzeige: die Klasse bleibt am Rohwert,
                               beschriftet wird ueber den Katalog. */
                            <div className={`bf-row sev-${b.severity}`} key={bi}>
                              <span className="bf-sev">{t(`ergebnis.schwere.${b.severity}`, { defaultValue: b.severity })}</span>
                              <span className="bf-txt">
                                {b.section ? <b>{b.section}: </b> : null}{b.text}
                                {b.beleg ? <span className="bf-beleg">{t("ergebnis.beleg", { text: b.beleg })}</span> : null}
                              </span>
                            </div>
                          ))}
                        </div>
                        <div className="bf-note">{t("ergebnis.befundeNote")}</div>
                      </>
                    )}
                  </div>
                )}
                {(genStats || elapsed > 0) && (
                  <div className="dk-techline">
                    <span>{t("ergebnis.dauer")}<b>{(() => { const ms = genStats?.durationMs ?? elapsed * 1000; const t = Math.round(ms / 1000); return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`; })()}</b></span>
                    {/* Erzeugung und Bearbeitung getrennt, dazu die Summe: die erste Zahl ist einmalig,
                        die zweite waechst mit jeder Anweisung. Beides in eine Zahl zu werfen wuerde
                        genau die Frage verwischen, die der Nutzer stellt. Die Bearbeitungsspalte
                        erscheint erst, wenn tatsaechlich nachgearbeitet wurde. */}
                    {genStats && typeof genStats.outputTokens === "number" && (() => {
                      const kurz = (n) => n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n || 0);
                      const eO = editStats?.output || 0, eI = editStats?.input || 0;
                      const paar = (o, i) => <><b>{kurz(o)}</b> / <b>{kurz(i)}</b></>;
                      return (<>
                        <span title={t("ergebnis.erzeugungTitel")}>
                          {t("ergebnis.erzeugung")}{paar(genStats.outputTokens, genStats.inputTokens || 0)}
                        </span>
                        {(eO > 0 || eI > 0) && (<>
                          <span title={t("ergebnis.bearbeitungTitel", { count: editStats.calls })}>
                            {t("ergebnis.bearbeitung")}{paar(eO, eI)}
                          </span>
                          <span title={t("ergebnis.gesamtTitel")}>
                            {t("ergebnis.gesamt")}{paar((genStats.outputTokens || 0) + eO, (genStats.inputTokens || 0) + eI)}
                          </span>
                        </>)}
                        <span className="dk-tech-legende">{t("ergebnis.legende")}</span>
                      </>);
                    })()}
                    {genStats?.model && <span>{t("ergebnis.modell")}<b>{genStats.model}</b></span>}
                  </div>
                )}
              </div>
            </div>
          )}
        {tokenLimit && !loading && (
          <div className="dk-tokenlimit doku-no-print" role="alert">
            <svg viewBox="0 0 24 24"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /><path d="M12 9v4M12 17h.01" /></svg>
            <span className="tx">
              <b>{t("ergebnis.tokenlimitTitel")}</b>{t("ergebnis.tokenlimitText")}
              {typeof tokenLimit.usedOutputTokens === "number" && <>{t("ergebnis.tokenlimitVerbrauchVor")}<span className="mono">{tokenLimit.usedOutputTokens.toLocaleString(gebiet)}</span>{tokenLimit.estimated ? t("ergebnis.tokenlimitGeschaetzt") : ""}{t("ergebnis.tokenlimitVon")}<span className="mono">{(tokenLimit.maxTokens || 0).toLocaleString(gebiet)}</span>{t("ergebnis.tokenlimitVerbrauchNach")}</>}
              {" "}{t("ergebnis.tokenlimitFrage")}
            </span>
            <span className="acts">
              <button type="button" className="dk-btn ghost sm" onClick={() => setTokenLimit(null)} disabled={contLoading}>{t("ergebnis.tokenlimitBelassen")}</button>
              <button type="button" className="dk-btn prim sm" onClick={continueGeneration} disabled={contLoading}>
                {contLoading ? <span className="iio-spinner" style={{ width: 13, height: 13 }} /> : <svg viewBox="0 0 24 24"><polygon points="5 3 19 12 5 21 5 3" /></svg>}
                {t("ergebnis.tokenlimitFortsetzen")}
              </button>
            </span>
          </div>
        )}
        {contLoading && (
          <div className="dk-hintln doku-no-print" style={{ marginBottom: 12, marginTop: 0 }}>
            <span className="iio-spinner" style={{ width: 13, height: 13 }} /> {t("ergebnis.wirdFortgesetzt")}
          </div>
        )}
        <div className={"dk-doc" + (geteilt ? " dk-shared" : "")}>
          <div className="dk-doc-tb doku-no-print">
            <div className="l">
              {/* Besitz und eigene Rolle sind Dokumentzustand - und .l ist der Ort, an dem
                  Dokumentzustand schon heute steht. */}
              {geteilt && !istBesitzer && besitzer && (
                <span className="dk-fremd">
                  <Avatar farbe={besitzer.farbe} name={besitzer.name} />
                  {t("werkzeug.geteiltVonVor")}<b>{besitzer.name}</b>{t("werkzeug.geteiltVonNach")}{darfSchreiben ? t("werkzeug.duerfenBearbeiten") : t("werkzeug.duerfenLesen")}
                </span>
              )}
              <b>{styleLabel}</b>
              {!loading && !selfEdit && darfSchreiben && (
                <span className="dk-edittag"><svg viewBox="0 0 24 24"><path d="M12 20h9M16 4l4 4-12 12H4v-4z" /></svg>{t("werkzeug.kiBereit")}</span>
              )}
              {selfEdit && (
                <span className="dk-edittag on"><svg viewBox="0 0 24 24"><path d="M12 20h9M16 4l4 4-12 12H4v-4z" /></svg>{t("werkzeug.selbstAktiv")}</span>
              )}
              {/* Gesperrte Knoepfe werden nicht ausgegraut, sondern nicht gerendert. Das Warum
                  steht dafuer einmal an zentraler Stelle - auch fuer den Pfad ohne Knopf
                  (Textauswahl oeffnet kein KI-Popup) ist diese Zeile die einzige Erklaerung. */}
              {!darfSchreiben && (
                <span className="dk-edittag ro">
                  <svg viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>
                  {t("werkzeug.nurLesen")}
                </span>
              )}
            </div>
            <div className="r">
              {selfEdit ? (<>
                <button type="button" className="dk-btn ghost sm" onClick={discardSelfEdit}>{t("werkzeug.verwerfen")}</button>
                <button type="button" className="dk-btn prim sm" onClick={commitSelfEdit}>
                  <svg viewBox="0 0 24 24"><polyline points="20 6 9 17 4 12" /></svg>{t("werkzeug.uebernehmen")}
                </button>
              </>) : (<>
                {/* Teilen aendert, WER das Dokument sieht, alle anderen aendern oder exportieren
                    WAS darin steht - die Haarlinie trennt zwei Handlungsklassen. */}
                {istBesitzer && sessId && (<>
                  <button type="button" className="dk-btn ghost sm" onClick={teilenOeffnen}
                    title={zugriffAnzahl > 1 ? t("werkzeug.teilenTitelVerwalten") : t("werkzeug.teilenTitelNeu")}>
                    <svg viewBox="0 0 24 24" fill={zugriffAnzahl > 1 ? "currentColor" : "none"}>
                      <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" />
                      <line x1="8.6" y1="10.5" x2="15.4" y2="6.5" /><line x1="8.6" y1="13.5" x2="15.4" y2="17.5" />
                    </svg>
                    {zugriffAnzahl > 1 ? t("werkzeug.geteiltAnzahl", { count: zugriffAnzahl }) : t("werkzeug.teilen")}
                  </button>
                  <span className="dk-tb-sep" aria-hidden="true" />
                </>)}
                {darfSchreiben && (
                  <button type="button" className="dk-btn ghost sm" onClick={startSelfEdit} disabled={loading || !docContent} title={t("werkzeug.selbstSchreibenTitel")}>
                    <svg viewBox="0 0 24 24"><path d="M12 20h9M16 4l4 4-12 12H4v-4z" /></svg>{t("werkzeug.selbstSchreiben")}
                  </button>
                )}
                {darfSchreiben && <button type="button" className="dk-btn ghost sm" onClick={() => { setAddFigOpen(true); setAddFigMsg(""); }} disabled={loading || !docContent}>{t("werkzeug.grafikHinzufuegen")}</button>}
                <button type="button" className="dk-btn ghost sm" onClick={copyToClipboard}>{t("werkzeug.kopieren")}</button>
                <button type="button" className="dk-btn ghost sm" onClick={downloadMd}>{t("werkzeug.markdown")}</button>
                <button type="button" className="dk-btn ghost sm" onClick={downloadDocx}>{t("werkzeug.word")}</button>
                <button type="button" className="dk-btn prim sm" onClick={downloadPdf}>
                  <svg viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>{t("werkzeug.pdf")}
                </button>
              </>)}
            </div>
          </div>
          {/* Legende: gehoert zum Dokument, nicht zum Aenderungsverlauf - eine frisch geteilte
              Doku hat noch keine Aenderungen, aber schon Mitwirkende. Beim Solo-Dokument gibt es
              keine Farben zu erklaeren, also existiert der Streifen dort gar nicht. */}
          {geteilt && (
            <div className="dk-mitwirkende doku-no-print">
              <span className="lbl">{t("werkzeug.mitwirkende")}</span>
              <ul>
                {mitwirkende.map(m => (
                  <li key={m.userId} data-farbe={m.farbe == null ? "x" : m.farbe} className={m.entzogen ? "weg" : ""}>
                    <Avatar farbe={m.farbe} name={m.name} />
                    <span className="nm">{m.name}</span>
                    {m.istBesitzer && <span className="rl">{t("werkzeug.besitzer")}</span>}
                    {m.rolle === "lesen" && <span className="rl">{t("werkzeug.liest")}</span>}
                    {m.entzogen && <span className="rl">{t("werkzeug.keinZugriff")}</span>}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <style>{`.doku-fig .doku-fig-edit{position:absolute;top:34px;right:10px;z-index:2;opacity:0;pointer-events:none;transition:opacity .12s ease;font-family:var(--iio-font-sans);font-size:11.5px;font-weight:600;padding:4px 10px;border-radius:var(--iio-r-sm,6px);border:1px solid var(--iio-border,#ccc);background:var(--iio-bg-2,#fff);color:var(--iio-text-2,#444);cursor:pointer}.doku-fig:hover .doku-fig-edit{opacity:1;pointer-events:auto}.doku-fig .doku-fig-edit:hover{color:var(--iio-text-1,#111);border-color:var(--iio-text-3,#999)}`}</style>
          {/* Aenderungsverlauf: jede Nutzereingabe ist eine kleine Version. Vorher war jede
              Aenderung eine Blackbox - der Nutzer sah nur, dass sich irgendwo etwas geaendert hat. */}
          {revisionen.length > 0 && !selfEdit && (
            <div className={"dk-revs doku-no-print" + (revsOffen ? " offen" : "")}>
              <div className="dk-revs-head">
                <button type="button" className="revsbtn" aria-expanded={revsOffen} onClick={() => setRevsOffen(o => !o)}>
                  <svg className={"chev" + (revsOffen ? " open" : "")} viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                  <span className="ttl">{t("verlauf.titel", { count: revisionen.length })}</span>
                  <span className="tip">{revsOffen ? t("verlauf.einklappen") : t("verlauf.auflisten")}</span>
                </button>
                <label className="dk-revs-toggle">
                  <input type="checkbox" checked={zeigeAenderungen} onChange={e => setZeigeAenderungen(e.target.checked)}/>
                  <span>{t("verlauf.hervorheben")}</span>
                </label>
              </div>
              {revsOffen && revisionen.slice().reverse().map((r, ri) => {
                const idx = revisionen.length - 1 - ri;
                const offen = revOffen === idx;
                const istJuengste = idx === revisionen.length - 1;
                // r.aktion ist ein gespeicherter Wert und bleibt roh - beschriftet wird ueber den Katalog.
                const aktLbl = t(`verlauf.aktion.${r.aktion}`, { defaultValue: r.aktion });
                return (
                  <div className={`dk-rev${offen ? " offen" : ""}`} key={r.id}>
                    <div className="dk-rev-row" onClick={() => setRevOffen(o => (o === idx ? null : idx))}>
                      <span className={`dk-rev-akt a-${r.aktion}`}>{aktLbl}</span>
                      {geteilt && <Avatar farbe={farbeVon(r.wer)} name={r.werName || besitzer?.name || ""} />}
                      <span className="dk-rev-txt" title={r.anweisung}>{r.anweisung || t("verlauf.ohneAnweisung")}</span>
                      <span className="dk-rev-um">
                        {r.umfang.weg ? <span className="weg">-{r.umfang.weg}</span> : null}
                        {r.umfang.neu ? <span className="neu">+{r.umfang.neu}</span> : null}
                      </span>
                      <span className="dk-rev-ts">{new Date(r.ts).toLocaleTimeString(gebiet, { hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                    {offen && (
                      <div className="dk-rev-diff">
                        <div className="dk-rev-legend">
                          <span><i className="weg"/> {t("verlauf.gingRaus")}</span><span><i className="neu"/> {t("verlauf.kamNeu")}</span>
                        </div>
                        {/* Kapitelweise statt dokumentweit: der Vergleich ueber das ganze Dokument
                            faerbt bei verschobenen Bloecken tausende Zeichen ein, obwohl sich
                            nur ein Wort geaendert hat. Ausserdem ist genau diese Form das, was
                            eine wiederhergestellte Dokumentation noch mitbringt. */}
                        {r.stellen?.length
                          ? r.stellen.map((s, si2) => (
                              <div className="dk-rev-stelle" key={si2}>
                                <div className="st-ttl">{s.titel || t("verlauf.dokumentkopf")}</div>
                                <div className="dk-rev-body">{diffAnsicht(s.vorherText, s.nachherText)}</div>
                              </div>
                            ))
                          : <div className="dk-rev-body">{diffAnsicht(r.vorher, r.nachher)}</div>}
                        {istJuengste && r.vorher && darfSchreiben ? (
                          <button className="btn btn--secondary btn--sm" onClick={() => nimmRevisionZurueck(idx)}>{t("verlauf.zuruecknehmen")}</button>
                        ) : (
                          <div className="dk-rev-note">{!darfSchreiben
                            ? t("verlauf.nurBearbeiter")
                            : istJuengste
                              ? t("verlauf.ausFruehererSitzung")
                              : t("verlauf.nurJuengste")}</div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          {/* Schreibkonflikt: steht AUSSERHALB des Scrollbereichs, damit der Hinweis sichtbar
              bleibt, waehrend der Nutzer im Text nach unten arbeitet, um seine Eingaben zu retten.
              Kein Schliessen-Knopf: eine wegklickbare Warnung ueber ungesicherte Arbeit waere
              selbst die Gefahr. Der Selbst-schreiben-Modus wird nicht angefasst - der getippte
              Text bleibt im DOM stehen, sichtbar und kopierbar. */}
          {konflikt && (
            <div className={"dk-konflikt doku-no-print"
              + (konflikt.riskant && !konflikt.gesichert ? " riskant" : "")
              + (konflikt.gesichert ? " ok" : "")}
              role="alert" aria-live="assertive">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                <line x1="12" y1="9" x2="12" y2="13" /><circle cx="12" cy="17" r="1" fill="currentColor" />
              </svg>
              <div className="tx">
                <Avatar farbe={konflikt.farbe} name={konflikt.werName} />
                {!konflikt.riskant && (
                  <span><b>{konflikt.werName}</b>{t("konflikt.nurAnsicht")}</span>
                )}
                {konflikt.riskant && !konflikt.gesichert && (
                  <span><b>{t("konflikt.speichernNichtMoeglich")}</b> {t("konflikt.speichernNichtMoeglichText", { name: konflikt.werName })}</span>
                )}
                {/* Bewusst "die Fassung von X" statt Genitiv-s: bei Namen auf s, z oder x
                    waere die angehaengte Endung schlicht falsch geschrieben. */}
                {konflikt.riskant && konflikt.gesichert && (
                  <span>{t("konflikt.gesichert", { name: konflikt.werName })}</span>
                )}
              </div>
              {/* Die Bestaetigung ERSETZT die Handlungszeile, sie tritt nicht daneben - dasselbe
                  Muster wie beim Rechteentzug, also nichts Neues zu lernen. */}
              {konflikt.frage ? (
                <span className="acts">
                  <span className="warn">{t("konflikt.warnung")}</span>
                  <button type="button" className="dk-btn prim sm" onClick={konfliktNeuLaden}>{t("konflikt.verwerfenNeuLaden")}</button>
                  <button type="button" className="dk-btn ghost sm" onClick={() => setKonflikt(k => ({ ...k, frage: false }))}>{t("gemeinsam:aktion.abbrechen")}</button>
                </span>
              ) : (
                <span className="acts">
                  {konflikt.riskant && (
                    <button type="button" className={"dk-btn sm " + (konflikt.gesichert ? "ghost" : "prim")}
                      onClick={() => { downloadMd(); setKonflikt(k => ({ ...k, gesichert: true })); }}>
                      {konflikt.gesichert ? t("konflikt.erneutSichern") : t("konflikt.standSichern")}
                    </button>
                  )}
                  <button type="button"
                    className={"dk-btn sm " + (!konflikt.riskant || konflikt.gesichert ? "prim" : "ghost")}
                    onClick={() => (konflikt.riskant && !konflikt.gesichert)
                      ? setKonflikt(k => ({ ...k, frage: true }))
                      : konfliktNeuLaden()}>
                    {t("konflikt.neuLaden")}
                  </button>
                </span>
              )}
            </div>
          )}
          <div key={docDomKey} ref={contentRef} className="doku-print-body" onMouseUp={handleContentMouseUp} onClick={handleContentClick}
            contentEditable={selfEdit} suppressContentEditableWarning onInput={markDirty} spellCheck={false}
            style={{ padding: "26px 34px", maxHeight: "76vh", overflowY: "auto", fontSize: 14, lineHeight: 1.75, color: v("text-1"), position: "relative", outline: "none", boxShadow: selfEdit ? "inset 2px 0 0 var(--iio-info)" : "none" }}>
            {docContent && (
              <div contentEditable={false} style={{ fontStyle: "italic", fontSize: 12, lineHeight: 1.55, color: v("text-3"), padding: "9px 13px", marginBottom: 18, borderLeft: "3px solid var(--iio-signal, #E85D2E)", background: v("bg-2"), borderRadius: "0 var(--iio-r-sm,4px) var(--iio-r-sm,4px) 0", userSelect: "none" }}>
                {kiHinweis}
              </div>
            )}
            {renderMarkdown(docContent, svgs, bpmnSvgs, markierungen)}
            {!loading && genEnd && docContent && (
              <div contentEditable={false} className="dk-endmark doku-no-print">
                <span className={"bar " + (genEnd === "complete" ? "ok" : "warn")} />
                {genEnd === "complete"
                  ? t("ergebnis.endeVollstaendig")
                  : genEnd === "truncated"
                    ? t("ergebnis.endeAbgeschnitten")
                    : t("ergebnis.endeAbgebrochen")}
              </div>
            )}
            {loading && <span className="iio-spinner" style={{ width: 14, height: 14, marginTop: 8, display: "inline-block" }} />}

            {/* Edit popup rendered outside via portal below */}
          </div>
        </div>
        {!loading && !selfEdit && darfSchreiben && (
          <div className="dk-hintln doku-no-print">
            <svg viewBox="0 0 24 24"><path d="M12 20h9M16 4l4 4-12 12H4v-4z" /></svg>
            {t("werkzeug.schreibhinweis")}
          </div>
        )}
        </section>
      )}

      {/* Aktives Feedback - die KI lernt daraus (klassifiziert nach allgemeinem Stil vs. projektspezifisch) */}
      {hasResult && !loading && (
        <div className="doku-no-print" style={{ marginTop: 16, padding: "14px 16px", background: v("bg-2"), border: `1px solid ${v("border") || "#ddd"}`, borderRadius: 10 }}>
          {fbSent ? (
            <div style={{ fontSize: 13, color: v("text-2"), display: "flex", alignItems: "center", gap: 8 }}>
              <span style={{ width: 2, height: 14, background: "var(--iio-success,#2D8A5A)", display: "inline-block", flexShrink: 0 }} />
              {t("rueckmeldung.dank")}
            </div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: v("text-1") }}>{t("rueckmeldung.frage")}</span>
                <button className="iio-btn iio-btn--ghost" onClick={() => setFbRating("up")}
                  style={{ fontSize: 13, padding: "5px 14px", ...(fbRating === "up" ? { borderColor: "var(--iio-success,#2D8A5A)", color: "var(--iio-success,#2D8A5A)" } : {}) }}>{t("rueckmeldung.gut")}</button>
                <button className="iio-btn iio-btn--ghost" onClick={() => setFbRating("down")}
                  style={{ fontSize: 13, padding: "5px 14px", ...(fbRating === "down" ? { borderColor: "var(--iio-warning,#C77A0B)", color: "var(--iio-warning,#C77A0B)" } : {}) }}>{t("rueckmeldung.nichtGanz")}</button>
              </div>
              {fbRating && (
                <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "center" }}>
                  <input value={fbText} onChange={e => setFbText(e.target.value)}
                    placeholder={fbRating === "up" ? t("rueckmeldung.platzhalterGut") : t("rueckmeldung.platzhalterSchlecht")}
                    onKeyDown={e => e.key === "Enter" && submitFeedback()}
                    style={{ flex: 1, padding: "8px 12px", border: `1px solid ${v("border") || "#ddd"}`, borderRadius: 6, fontSize: 13, background: v("bg-1"), color: v("text-1") }} />
                  <button className="iio-btn iio-btn--primary" onClick={() => submitFeedback()} style={{ padding: "8px 16px", fontSize: 13, whiteSpace: "nowrap" }}>{t("rueckmeldung.senden")}</button>
                </div>
              )}
              <div style={{ fontSize: 11, color: v("text-3"), marginTop: 8 }}>{t("rueckmeldung.hinweis")}</div>
            </>
          )}
        </div>
      )}

      {/* Append Input - schreibt in den Text, also nur fuer Bearbeiter */}
      {hasResult && !loading && !selfEdit && darfSchreiben && (
        <div className="dk-append doku-no-print">
          <input value={appendInput} onChange={e => setAppendInput(e.target.value)}
            placeholder={t("ergaenzen.platzhalter")}
            onKeyDown={e => e.key === "Enter" && handleAppend()} />
          <button type="button" className="dk-btn prim" onClick={handleAppend} disabled={appendLoading || !appendInput.trim()}>
            {appendLoading ? <span className="iio-spinner" style={{ width: 14, height: 14 }} /> : t("ergaenzen.knopf")}
          </button>
        </div>
      )}

      </div>{/* Ende dk-grid */}

      <div ref={printRootRef} style={{ display: "none" }} />

      {/* Grafik-anpassen-Popup (Hover-Button an Figuren) */}
      {figPopup && (<>
        <div onClick={() => { if (!figLoading) setFigPopup(null); }} style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.15)" }} />
        <div style={{ position: "fixed", top: figPopup.screenY, left: figPopup.screenX, zIndex: 9999, width: 380, background: "var(--iio-bg-1, #1a1e2e)", border: "1px solid var(--iio-border, #333)", borderRadius: 12, padding: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 13, fontWeight: 600, color: v("text-1"), marginBottom: 4 }}>{t("grafik.anpassenTitel")}</div>
          <div style={{ fontSize: 11.5, color: v("text-3"), marginBottom: 10, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{figPopup.cap}</div>
          {figPopup.msg ? (
            <>
              <div style={{ fontSize: 12.5, color: v("text-2"), lineHeight: 1.5, marginBottom: 10 }}>{figPopup.msg}</div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}><button className="iio-btn iio-btn--primary" onClick={() => setFigPopup(null)} style={{ fontSize: 12, padding: "6px 14px" }}>{t("gemeinsam:aktion.schliessen")}</button></div>
            </>
          ) : (
            <>
              <input autoFocus value={figPopup.instruction} onChange={e => setFigPopup(p => ({ ...p, instruction: e.target.value }))}
                placeholder={figPopup.kind === "bpmn" ? t("grafik.anpassenPlatzhalterBpmn") : t("grafik.anpassenPlatzhalter")}
                onKeyDown={e => { if (e.key === "Enter" && !figLoading) submitFigEdit(); if (e.key === "Escape" && !figLoading) setFigPopup(null); }}
                style={{ width: "100%", boxSizing: "border-box", padding: "8px 12px", border: `1px solid ${v("border") || "#444"}`, borderRadius: 6, fontSize: 13, background: v("bg-2"), color: v("text-1") }} />
              <div style={{ fontSize: 10.5, color: v("text-3"), margin: "8px 0 10px" }}>{t("grafik.gelerntHinweis")}</div>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                <button className="iio-btn iio-btn--ghost" disabled={figLoading} onClick={() => setFigPopup(null)} style={{ fontSize: 12, padding: "6px 12px" }}>{t("gemeinsam:aktion.abbrechen")}</button>
                <button className="iio-btn iio-btn--primary" disabled={figLoading || !figPopup.instruction.trim()} onClick={submitFigEdit} style={{ fontSize: 12, padding: "6px 14px", minWidth: 88, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {figLoading ? <span className="iio-spinner" style={{ width: 13, height: 13 }} /> : t("grafik.anpassen")}
                </button>
              </div>
            </>
          )}
        </div>
      </>)}

      {/* Grafik-hinzufuegen-Popup (Toolbar-Button) */}
      {addFigOpen && (<>
        <div onClick={() => { if (!addFigLoading) setAddFigOpen(false); }} style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.25)" }} />
        <div style={{ position: "fixed", top: "28vh", left: "50%", transform: "translateX(-50%)", zIndex: 9999, width: 460, maxWidth: "92vw", background: "var(--iio-bg-1, #1a1e2e)", border: "1px solid var(--iio-border, #333)", borderRadius: 12, padding: 18, boxShadow: "0 12px 40px rgba(0,0,0,0.5)" }} onClick={e => e.stopPropagation()}>
          <div style={{ fontSize: 14, fontWeight: 600, color: v("text-1"), marginBottom: 4 }}>{t("grafik.hinzufuegenTitel")}</div>
          <div style={{ fontSize: 12, color: v("text-3"), marginBottom: 12, lineHeight: 1.5 }}>{t("grafik.hinzufuegenText")}</div>
          <input autoFocus value={addFigText} onChange={e => setAddFigText(e.target.value)}
            placeholder={t("grafik.hinzufuegenPlatzhalter")}
            onKeyDown={e => { if (e.key === "Enter" && !addFigLoading) submitAddFig(); if (e.key === "Escape" && !addFigLoading) setAddFigOpen(false); }}
            style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${v("border") || "#444"}`, borderRadius: 6, fontSize: 13, background: v("bg-2"), color: v("text-1") }} />
          {addFigMsg && <div style={{ fontSize: 12, color: "var(--iio-warning,#C77A0B)", marginTop: 8 }}>{addFigMsg}</div>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <button className="iio-btn iio-btn--ghost" disabled={addFigLoading} onClick={() => setAddFigOpen(false)} style={{ fontSize: 12, padding: "6px 12px" }}>{t("gemeinsam:aktion.abbrechen")}</button>
            <button className="iio-btn iio-btn--primary" disabled={addFigLoading || !addFigText.trim()} onClick={submitAddFig} style={{ fontSize: 12, padding: "6px 14px", minWidth: 110, display: "flex", alignItems: "center", justifyContent: "center" }}>
              {addFigLoading ? <span className="iio-spinner" style={{ width: 13, height: 13 }} /> : t("grafik.erzeugen")}
            </button>
          </div>
        </div>
      </>)}

      {/* Inline Edit Popup - fixed position, outside scroll container */}
      {/* Popup am Info-Symbol: zeigt fuer GENAU dieses Kapitel, was rausging und was neu kam.
          Bewusst kapitelweit und nicht dokumentweit - der Nutzer klickt an einer Stelle und will
          diese Stelle sehen. Mehrere Aenderungen am selben Kapitel sind durchblaetterbar. */}
      {chgPop && markierungen?.get(chgPop.idx)?.length > 0 && (() => {
        const liste = markierungen.get(chgPop.idx);
        const pos = Math.min(Math.max(chgPop.pos, 0), liste.length - 1);
        const { rev, revIdx, stelle } = liste[pos];
        const aktLbl = t(`verlauf.aktion.${rev.aktion}`, { defaultValue: rev.aktion });
        const istJuengsteGesamt = revIdx === revisionen.length - 1;
        return (<>
          <div onClick={() => setChgPop(null)} style={{ position: "fixed", inset: 0, zIndex: 9998 }} />
          <div className="dk-chgpop" style={{ top: chgPop.screenY, left: chgPop.screenX }}
            onClick={e => e.stopPropagation()}>
            <div className="cp-head">
              <span className={`dk-rev-akt a-${rev.aktion}`}>{aktLbl}</span>
              <span className="cp-ttl">{stelle.titel || t("verlauf.dokumentkopf")}</span>
              <span className="cp-meta">{new Date(rev.ts).toLocaleTimeString(gebiet, { hour: "2-digit", minute: "2-digit" })}</span>
              <button type="button" className="cp-x" onClick={() => setChgPop(null)} aria-label={t("gemeinsam:aktion.schliessen")}>
                <svg viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            {liste.length > 1 && (
              <div className="cp-nav">
                <button type="button" disabled={pos === 0} onClick={() => setChgPop(p => ({ ...p, pos: pos - 1 }))} title={t("verlauf.popNeuere")}>
                  <svg viewBox="0 0 24 24"><polyline points="15 18 9 12 15 6" /></svg>
                </button>
                <span>{t("verlauf.popZaehler", { nummer: pos + 1, gesamt: liste.length })}</span>
                <button type="button" disabled={pos === liste.length - 1} onClick={() => setChgPop(p => ({ ...p, pos: pos + 1 }))} title={t("verlauf.popAeltere")}>
                  <svg viewBox="0 0 24 24"><polyline points="9 18 15 12 9 6" /></svg>
                </button>
              </div>
            )}
            {/* Urheber gehoert in denselben Satz wie der Auftrag - und der linke Streifen des
                Blocks bindet das Popup an den Balken, den der Nutzer gerade angeklickt hat.
                Ein Altbestand ohne Namen bekommt keinen angedichtet: dann faellt die Zeile weg. */}
            <div className="cp-anw" data-farbe={geteilt ? (farbeVon(rev.wer) ?? undefined) : undefined}>
              {geteilt && rev.werName && (
                <span className="cp-wer">
                  <Avatar farbe={farbeVon(rev.wer)} name={rev.werName} />
                  {t("verlauf.beauftragtVon")}<b>{rev.werName}</b>
                </span>
              )}
              <span className="cp-anw-txt">{rev.anweisung || t("verlauf.ohneAnweisung")}</span>
            </div>
            <div className="dk-rev-legend">
              <span><i className="weg" /> {t("verlauf.gingRaus")}</span><span><i className="neu" /> {t("verlauf.kamNeu")}</span>
            </div>
            <div className="dk-rev-body">{diffAnsicht(stelle.vorherText, stelle.nachherText)}</div>
            {istJuengsteGesamt && rev.vorher && darfSchreiben
              ? <button className="btn btn--secondary btn--sm" onClick={() => { nimmRevisionZurueck(revIdx); setChgPop(null); }}>{t("verlauf.zuruecknehmen")}</button>
              : <div className="dk-rev-note">{!darfSchreiben
                  ? t("verlauf.nurBearbeiter")
                  : istJuengsteGesamt
                    ? t("verlauf.ausFruehererSitzung")
                    : t("verlauf.nurJuengsteDokument")}</div>}
          </div>
        </>);
      })()}
      {/* Einladung aus dem Link: eigene Ansicht, bevor irgendetwas passiert. */}
      {einladungAnnahme && (() => {
        const z = einladungAnnahme.zustand;
        const offen = z === "offen";
        // z ist der Zustand vom Server und bleibt roh - die Kopfzeile kommt aus dem Katalog.
        const kopf = { offen: t("einladung.kopfOffen"), bereits: t("einladung.kopfBereits"), eigene: t("einladung.kopfEigene"),
          abgelaufen: t("einladung.kopfAbgelaufen"), eingeloest: t("einladung.kopfEingeloest") }[z] || t("einladung.kopfUngueltig");
        return (
          <div className="iio-modal-overlay is-open">
            <div className="iio-modal dk-einladung" role="dialog" aria-modal="true" aria-labelledby="dk-einl-t" onMouseDown={e => e.stopPropagation()}>
              <div className="iio-modal-head">
                <div className="iio-modal-head-left">
                  <span className="iio-modal-eyebrow">{t("einladung.eyebrow")}</span>
                  <h2 id="dk-einl-t">{kopf}</h2>
                </div>
              </div>
              <div className="iio-modal-body">
                {offen && (<>
                  <p className="dk-einl-tx"><b>{einladungAnnahme.besitzerName}</b>{t("einladung.einladungText", { titel: einladungAnnahme.titel })}</p>
                  <p className="dk-einl-rolle">{t("einladung.rolleVor")}<b>{einladungAnnahme.rolle === "bearbeiten" ? t("teilen.rolleBearbeiten") : t("teilen.rolleLesen")}</b>
                    {einladungAnnahme.rolle === "bearbeiten"
                      ? t("einladung.rolleBearbeitenText")
                      : t("einladung.rolleLesenText")}</p>
                </>)}
                {z === "bereits" && <p className="dk-einl-tx">{t("einladung.bereits", { titel: einladungAnnahme.titel })}</p>}
                {z === "eigene" && <p className="dk-einl-tx">{t("einladung.eigene", { titel: einladungAnnahme.titel })}</p>}
                {!offen && z !== "bereits" && z !== "eigene" && (
                  <p className="dk-einl-tx fehler">{einladungAnnahme.error || t("einladung.ungueltig")}</p>
                )}
              </div>
              <div className="iio-modal-foot">
                <span className="iio-modal-foot-note">{offen ? t("einladung.fussnote") : ""}</span>
                <span style={{ display: "flex", gap: 8 }}>
                  <button type="button" className="dk-btn ghost sm" onClick={einladungSchliessen}>{offen ? t("einladung.ablehnen") : t("gemeinsam:aktion.schliessen")}</button>
                  {offen && (
                    <button type="button" className="dk-btn prim sm" disabled={annahmeBusy} onClick={einladungAnnehmen}>
                      {annahmeBusy ? t("einladung.wirdAngenommen") : t("einladung.annehmen")}
                    </button>
                  )}
                </span>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Teilen-Dialog: natives .iio-modal, kein MUI-Portal (das verliert den .iio-Scope). */}
      {teilenOffen && (
        <div className="iio-modal-overlay is-open" onMouseDown={e => { if (e.target === e.currentTarget) setTeilenOffen(false); }}>
          <div className="iio-modal dk-share" role="dialog" aria-modal="true" aria-labelledby="dk-share-t"
            ref={teilenRef} onMouseDown={e => e.stopPropagation()}>
            <div className="iio-modal-head">
              <div className="iio-modal-head-left">
                <span className="iio-modal-eyebrow">{flowName}</span>
                <h2 id="dk-share-t">{t("teilen.titel")}</h2>
              </div>
              <button type="button" className="iio-modal-close" onClick={() => setTeilenOffen(false)} aria-label={t("gemeinsam:aktion.schliessen")}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div className="iio-modal-body">
              <p className="dk-share-intro">{t("teilen.intro")}</p>

              {/* Vier Sperren gegen "bearbeiten statt lesen": Lesen ist vorbelegt, die Karten
                  nennen die Folge, der Knopf nennt die Rolle, und Hochstufen bestaetigt. */}
              <fieldset className="dk-share-rollen">
                <legend className="iio-field-label">{t("teilen.rolle")}</legend>
                {/* k geht an die Schnittstelle und bleibt woertlich; t und d sind nur Anzeige. */}
                {[
                  { k: "lesen", t: t("teilen.rolleLesen"), d: t("teilen.rolleLesenText") },
                  { k: "bearbeiten", t: t("teilen.rolleBearbeiten"), d: t("teilen.rolleBearbeitenText") },
                ].map(r => (
                  <label key={r.k} className={"cs-pw-radio" + (teilenRolle === r.k ? " on" : "") + (r.k === "bearbeiten" ? " stark" : "")}>
                    <input type="radio" name="dk-rolle" value={r.k}
                      checked={teilenRolle === r.k} onChange={() => setTeilenRolle(r.k)} />
                    <span className="rc" aria-hidden="true" />
                    <span className="rt"><b>{r.t}</b><span>{r.d}</span></span>
                  </label>
                ))}
              </fieldset>

              <div className="dk-share-einladen">
                <button type="button" className="dk-btn prim sm" disabled={teilenBusy} onClick={linkErzeugen}>
                  {teilenRolle === "bearbeiten" ? t("teilen.linkBearbeiten") : t("teilen.linkLesen")}
                </button>
              </div>

              {/* Der frische Link steht gross da und ist markierbar - er ist das einzige, was der
                  Besitzer jetzt weitergeben muss. */}
              {neuerLink && (
                <div className="dk-share-link">
                  <span className="lbl">{t("teilen.linkLabel", { zeit: new Date(neuerLink.laeuftAbAm).toLocaleString(gebiet, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) })}</span>
                  <div className="rw">
                    <input className="iio-input" readOnly value={neuerLink.url} onFocus={e => e.target.select()} />
                    <button type="button" className="dk-btn prim sm" onClick={() => linkKopieren(neuerLink.url)}>{t("teilen.linkKopieren")}</button>
                  </div>
                  <span className="hint">{t("teilen.linkHinweis")}</span>
                </div>
              )}

              {teilenMeldung && (
                <div className={"dk-share-meldung" + (teilenMeldung.art === "fehler" ? " fehler" : "")} role="status">{teilenMeldung.text}</div>
              )}

              {einladungen.filter(e => e.zustand === "offen").length > 0 && (
                <div className="dk-share-liste">
                  <span className="iio-field-label">{t("teilen.offeneEinladungen")}</span>
                  {einladungen.filter(e => e.zustand === "offen").map(e => (
                    <div className="dk-share-row offen" key={e.token}>
                      <i className="dk-avatar wartet" aria-hidden="true">?</i>
                      <span className="nm">{t("teilen.nochNichtAngenommen")}</span>
                      <span className="rl">{t("teilen.einladungBis", { rolle: e.rolle === "bearbeiten" ? t("teilen.rolleBearbeiten") : t("teilen.rolleLesen"), zeit: new Date(e.laeuftAbAm).toLocaleString(gebiet, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) })}</span>
                      <button type="button" className="dk-btn ghost sm" disabled={teilenBusy}
                        onClick={() => linkZurueckziehen(e.token)}>{t("teilen.zurueckziehen")}</button>
                    </div>
                  ))}
                </div>
              )}

              <div className="dk-share-liste">
                <span className="iio-field-label">{t("teilen.zugriff")}</span>
                {mitwirkende.map(m => (
                  <div className={"dk-share-row" + (m.entzogen ? " weg" : "")} key={m.userId} data-farbe={m.farbe == null ? "x" : m.farbe}>
                    <Avatar farbe={m.farbe} name={m.name} />
                    <span className="nm">{m.name}</span>

                    {m.istBesitzer ? (
                      <span className="rl">{t("teilen.besitzer")}</span>
                    ) : m.entzogen ? (
                      /* Kein Rollenwaehler und kein Entziehen: die Person hat keinen Zugriff mehr.
                         Sie steht hier, weil ihre Farbe und ihr Name im Verlauf gebunden bleiben. */
                      <span className="rl">{t("teilen.keinZugriff")}</span>
                    ) : (<>
                      <select className="iio-input dk-share-sel" value={m.rolle} disabled={teilenBusy}
                        onChange={e => rolleAendern(m, e.target.value)}>
                        <option value="lesen">{t("teilen.rolleLesen")}</option>
                        <option value="bearbeiten">{t("teilen.rolleBearbeiten")}</option>
                      </select>
                      <button type="button" className="dk-share-x" disabled={teilenBusy}
                        onClick={() => setBestaetigung({ userId: m.userId, art: "entzug" })}>{t("teilen.zugriffEntziehen")}</button>
                    </>)}

                    {/* Bestaetigung bleibt IN der Zeile - ein Modal ueber dem Modal verliert den Bezug. */}
                    {bestaetigung?.userId === m.userId && bestaetigung.art === "hoch" && (
                      <div className="dk-share-confirm">
                        <span><b>{m.name}</b>{t("teilen.bestaetigungHoch")}</span>
                        <button type="button" className="dk-btn prim sm" disabled={teilenBusy} onClick={() => setzeRolle(m, "bearbeiten")}>{t("teilen.bestaetigen")}</button>
                        <button type="button" className="dk-btn ghost sm" onClick={() => setBestaetigung(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
                      </div>
                    )}
                    {bestaetigung?.userId === m.userId && bestaetigung.art === "entzug" && (
                      <div className="dk-share-confirm warn">
                        <span>{t("teilen.bestaetigungEntzug")}</span>
                        <button type="button" className="dk-btn prim sm" disabled={teilenBusy} onClick={() => entziehen(m)}>{t("teilen.entziehen")}</button>
                        <button type="button" className="dk-btn ghost sm" onClick={() => setBestaetigung(null)}>{t("gemeinsam:aktion.abbrechen")}</button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="iio-modal-foot">
              <div className="iio-modal-foot-left">
                <span className="dk-share-foot">{t("teilen.fussnote")}</span>
              </div>
              <div className="iio-modal-foot-right">
                <button type="button" className="dk-btn dark sm" onClick={() => setTeilenOffen(false)}>{t("teilen.fertig")}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {editPopup && (<>
        <div onClick={() => { if (!editLoading) setEditPopup(null); }} style={{ position: "fixed", inset: 0, zIndex: 9998, background: "rgba(0,0,0,0.15)" }} />
        <div style={{ position: "fixed", top: editPopup.screenY, left: editPopup.screenX, zIndex: 9999, width: 380, background: "var(--iio-bg-1, #1a1e2e)", border: "1px solid var(--iio-border, #333)", borderRadius: 12, padding: 16, boxShadow: "0 12px 40px rgba(0,0,0,0.5)", animation: "dokuPopupIn 0.15s ease-out" }}
          onClick={e => e.stopPropagation()}>
          <style>{`@keyframes dokuPopupIn{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:translateY(0)}}`}</style>
          <div style={{ fontSize: 11, color: "var(--iio-text-3, #888)", marginBottom: 6, fontWeight: 600 }}>{t("bearbeiten.titel")}</div>
          <div onClick={e => {
              const el = e.currentTarget;
              if (el.style.maxHeight === "none") { el.style.maxHeight = "52px"; el.style.cursor = "pointer"; }
              else { el.style.maxHeight = "none"; el.style.cursor = "default"; }
            }}
            style={{ fontSize: 12, color: "var(--iio-text-2, #aaa)", padding: "8px 10px", background: "var(--iio-bg-2, #252a3a)", borderRadius: 6, marginBottom: 10, maxHeight: 52, overflow: "hidden", fontStyle: "italic", cursor: editPopup.selectedText?.length > 100 ? "pointer" : "default", transition: "max-height 0.2s ease", lineHeight: 1.5, position: "relative" }}>
            "{editPopup.selectedText}"
            {editPopup.selectedText?.length > 100 && <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 20, background: "linear-gradient(transparent, var(--iio-bg-2, #252a3a))", pointerEvents: "none" }} />}
          </div>
          <input value={editPopup.instruction} onChange={e => setEditPopup(p => ({ ...p, instruction: e.target.value }))}
            placeholder={t("bearbeiten.platzhalter")}
            onKeyDown={e => { if (e.key === "Enter" && !editLoading) submitEdit(); if (e.key === "Escape" && !editLoading) setEditPopup(null); }}
            autoFocus disabled={editLoading}
            style={{ width: "100%", padding: "8px 12px", border: "1px solid var(--iio-border, #333)", borderRadius: 6, fontSize: 13, boxSizing: "border-box", background: "var(--iio-bg-1, #1a1e2e)", color: "var(--iio-text-1, #eee)", opacity: editLoading ? 0.5 : 1 }} />
          <div style={{ display: "flex", gap: 6, marginTop: 10, justifyContent: "flex-end" }}>
            <button className="iio-btn iio-btn--ghost" onClick={() => setEditPopup(null)} disabled={editLoading} style={{ fontSize: 12, padding: "6px 14px" }}>{t("gemeinsam:aktion.abbrechen")}</button>
            <button className="iio-btn iio-btn--primary" onClick={submitEdit} disabled={editLoading || !editPopup.instruction?.trim()} style={{ fontSize: 12, padding: "6px 16px", minWidth: 90, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {editLoading ? <><span className="iio-spinner" style={{ width: 13, height: 13 }} /> {t("bearbeiten.wirdAngepasst")}</> : t("bearbeiten.anwenden")}
            </button>
          </div>
        </div>
      </>)}
    </div>
  );
};

export default DokuPage;
